<template>
    <v-dialog
        :model-value="modelValue"
        max-width="1200"
        width="90vw"
        :fullscreen="$vuetify.display.smAndDown"
        scrollable
        @update:model-value="onDialogToggle">
        <v-card>
            <v-toolbar
                color="surface"
                class="builder-dialog-toolbar"
                :extended="$vuetify.display.mdAndUp">
                <v-toolbar-title class="text-truncate">
                    {{ dialogTitle }}
                </v-toolbar-title>

                <div
                    v-if="$vuetify.display.mdAndUp"
                    class="d-flex align-center flex-wrap ga-4 text-body-2 ms-4">
                    <div>
                        <span class="text-medium-emphasis">Curs valutar:</span>
                        <strong class="ms-1">{{
                            formatResultValue("displayExchangeRate") || "—"
                        }}</strong>
                    </div>
                    <div>
                        <span class="text-medium-emphasis">Cod:</span>
                        <strong class="ms-1">{{
                            computedProduct.productCode || "—"
                        }}</strong>
                    </div>
                    <div>
                        <span class="text-medium-emphasis">Status:</span>
                        <strong
                            class="ms-1"
                            :style="productStatusTextStyle(productBuilderForm.status)"
                            >{{
                                formatProductStatusLabel(productBuilderForm.status)
                            }}</strong
                        >
                    </div>
                    <div v-if="simulationMode">
                        <v-chip
                            size="x-small"
                            variant="tonal"
                            color="warning"
                            prepend-icon="mdi-flask-outline">
                            Simulare
                        </v-chip>
                    </div>
                    <div v-if="showBuilderNewChip">
                        <v-chip
                            size="x-small"
                            variant="tonal"
                            color="success"
                            prepend-icon="mdi-plus-circle-outline">
                            Nou
                        </v-chip>
                    </div>
                    <div v-if="showInitialProductCode">
                        <span class="text-medium-emphasis"
                            >Cod produs inițial:</span
                        >
                        <strong class="ms-1">{{
                            sessionSourceProductCode
                        }}</strong>
                    </div>

                    <div>
                        <span class="text-medium-emphasis">Preț listă:</span>
                        <strong class="ms-1">{{
                            formatSummaryPrice(
                                defaultComputedProduct.listPriceRonIncVatFinal,
                            )
                        }}</strong>
                    </div>
                    <div
                        v-if="
                            isCompoundCreateMode &&
                            defaultComputedProduct.suggestedListPriceRonIncVatFinal
                        ">
                        <span class="text-medium-emphasis">Sugerat:</span>
                        <strong class="ms-1">{{
                            formatSummaryPrice(
                                defaultComputedProduct.suggestedListPriceRonIncVatFinal,
                            )
                        }}</strong>
                    </div>
                </div>

                <v-spacer />

                <v-btn icon variant="text" @click="close">
                    <v-icon>mdi-close</v-icon>
                </v-btn>

                <template v-if="$vuetify.display.mdAndUp" #extension>
                    <div class="w-100 px-4 pb-3">
                        <div
                            class="builder-panel-nav d-flex flex-wrap align-center ga-1">
                            <template
                                v-for="(step, index) in panelSteps"
                                :key="step.value">
                                <v-chip
                                    size="small"
                                    variant="outlined"
                                    class="builder-panel-chip"
                                    @click="goToPanel(step.value)">
                                    {{ step.title }}
                                </v-chip>
                                <v-icon
                                    v-if="index < panelSteps.length - 1"
                                    size="small"
                                    class="text-medium-emphasis">
                                    mdi-chevron-right
                                </v-icon>
                            </template>
                        </div>
                    </div>
                </template>
            </v-toolbar>

            <v-card-text class="pt-4">
                <div
                    v-if="formLoading"
                    class="d-flex justify-center align-center py-16">
                    <v-progress-circular
                        color="primary"
                        indeterminate
                        :size="64"
                        :width="5" />
                </div>
                <template v-else>
                <fieldset
                    class="builder-form-fieldset"
                    :class="{ 'builder-form-fieldset--readonly': readOnly }">
                <v-alert
                    v-if="$vuetify.display.smAndDown"
                    type="info"
                    variant="tonal"
                    class="mb-4">
                    <div class="d-flex flex-wrap ga-6">
                        <div>
                            <div class="text-caption">Cod produs</div>
                            <div class="text-h6">
                                {{ computedProduct.productCode || "—" }}
                            </div>
                        </div>
                        <div v-if="showBuilderNewChip">
                            <div class="text-caption">Tip draft</div>
                            <div class="text-h6">
                                <v-chip
                                    size="small"
                                    variant="tonal"
                                    color="success"
                                    prepend-icon="mdi-plus-circle-outline">
                                    Nou
                                </v-chip>
                            </div>
                        </div>
                        <div v-if="showInitialProductCode">
                            <div class="text-caption">Cod produs inițial</div>
                            <div class="text-h6">
                                {{ sessionSourceProductCode }}
                            </div>
                        </div>
                        <div>
                            <div class="text-caption">Curs valutar</div>
                            <div class="text-h6">
                                {{
                                    formatResultValue("displayExchangeRate") ||
                                    "—"
                                }}
                            </div>
                        </div>
                        <div>
                            <div class="text-caption">Status</div>
                            <div
                                class="text-h6"
                                :style="productStatusTextStyle(productBuilderForm.status)">
                                {{
                                    formatProductStatusLabel(
                                        productBuilderForm.status,
                                    )
                                }}
                            </div>
                        </div>
                        <div>
                            <div class="text-caption">Preț listă cu TVA</div>
                            <div class="text-h6">
                                {{
                                    formatSummaryPrice(
                                        defaultComputedProduct.listPriceRonIncVatFinal,
                                    )
                                }}
                            </div>
                        </div>
                        <div
                            v-if="
                                isCompoundCreateMode &&
                                defaultComputedProduct.suggestedListPriceRonIncVatFinal
                            ">
                            <div class="text-caption">Preț sugerat cu TVA</div>
                            <div class="text-h6">
                                {{
                                    formatSummaryPrice(
                                        defaultComputedProduct.suggestedListPriceRonIncVatFinal,
                                    )
                                }}
                            </div>
                        </div>
                    </div>
                </v-alert>

                <v-form
                    ref="builderForm"
                    autocomplete="off"
                    validate-on="submit lazy">
                    <div class="builder-form">
                        <section
                            :id="panelDomId('structure')"
                            class="builder-section builder-section--structure">
                            <div
                                class="builder-section-heading d-flex align-center flex-wrap ga-2">
                                <div class="builder-section-title">
                                    Structură produs
                                </div>
                                <v-chip
                                    v-if="showBuilderNewChip"
                                    size="small"
                                    variant="tonal"
                                    color="success"
                                    prepend-icon="mdi-plus-circle-outline">
                                    Nou
                                </v-chip>
                                <v-chip
                                    v-if="showBuilderCopiedChip"
                                    size="small"
                                    variant="tonal"
                                    color="info"
                                    prepend-icon="mdi-content-copy">
                                    Copiat din {{ sessionSourceProductCode }}
                                </v-chip>
                                <v-btn
                                    class="ms-auto"
                                    variant="text"
                                    size="small"
                                    color="primary"
                                    prepend-icon="mdi-plus"
                                    @click="addSystem">
                                    Adaugă sistem
                                </v-btn>
                            </div>
                            <v-row>
                                <v-col
                                    v-for="(
                                        system, index
                                    ) in productBuilderForm.systems"
                                    :key="`system-group-${index}`"
                                    cols="12"
                                    :md="isExistingProductEdit ? 5 : 4">
                                    <div class="d-flex align-center ga-1">
                                        <v-menu
                                            :model-value="
                                                systemPairMenusOpen[index]
                                            "
                                            :close-on-content-click="false"
                                            location="bottom start"
                                            class="flex-grow-1"
                                            @update:model-value="
                                                onSystemPairMenuToggle(
                                                    index,
                                                    $event,
                                                )
                                            ">
                                            <template
                                                #activator="{
                                                    props: menuProps,
                                                }">
                                                <v-text-field
                                                    v-bind="menuProps"
                                                    :model-value="
                                                        systemPairDisplay(index)
                                                    "
                                                    :label="
                                                        systemPairLabel(index)
                                                    "
                                                    :class="
                                                        compareFieldClass(
                                                            'systems',
                                                        )
                                                    "
                                                    :variant="fieldVariant"
                                                    density="compact"
                                                    hide-details="auto"
                                                    readonly
                                                    :clearable="
                                                        productBuilderForm
                                                            .systems.length ===
                                                        1
                                                    "
                                                    append-inner-icon="mdi-chevron-down"
                                                    :rules="[
                                                        subcategoryRule(index),
                                                    ]"
                                                    @click:clear.stop="
                                                        clearSystemPair(index)
                                                    " />
                                            </template>

                                            <v-card
                                                min-width="320"
                                                elevation="8">
                                                <v-card-text
                                                    class="d-flex flex-column ga-3 pt-4">
                                                    <v-select
                                                        v-model="
                                                            productBuilderForm
                                                                .systems[index]
                                                        "
                                                        :items="systemOptions"
                                                        item-title="title"
                                                        item-value="value"
                                                        label="Sistem"
                                                        :variant="fieldVariant"
                                                        density="compact"
                                                        hide-details
                                                        @update:model-value="
                                                            onSystemChange(
                                                                index,
                                                                $event,
                                                            )
                                                        " />
                                                    <v-select
                                                        v-model="
                                                            productBuilderForm
                                                                .systemSubcategories[
                                                                index
                                                            ]
                                                        "
                                                        :items="
                                                            subcategoryOptionsForSystem(
                                                                index,
                                                            )
                                                        "
                                                        item-title="title"
                                                        item-value="value"
                                                        label="Subsistem"
                                                        :variant="fieldVariant"
                                                        density="compact"
                                                        hide-details
                                                        :disabled="
                                                            !productBuilderForm
                                                                .systems[index]
                                                        "
                                                        @update:model-value="
                                                            closeSystemPairMenu(
                                                                index,
                                                            )
                                                        " />
                                                </v-card-text>
                                            </v-card>
                                        </v-menu>
                                        <v-btn
                                            v-if="
                                                productBuilderForm.systems
                                                    .length > 1
                                            "
                                            icon
                                            size="x-small"
                                            variant="text"
                                            @click="removeSystem(index)">
                                            <v-icon size="small"
                                                >mdi-close</v-icon
                                            >
                                        </v-btn>
                                    </div>
                                </v-col>
                                <v-col cols="12" md="4">
                                    <div class="d-flex align-center ga-1">
                                        <v-autocomplete
                                            v-model="
                                                productBuilderForm.chapterCode
                                            "
                                            :items="chapterOptions"
                                            item-title="title"
                                            item-value="code"
                                            label="Categorie"
                                            :class="
                                                compareFieldClass('chapterCode')
                                            "
                                            :variant="fieldVariant"
                                            density="compact"
                                            hide-details="auto"
                                            :rules="[requiredRule]"
                                            clearable
                                            class="flex-grow-1"
                                            @update:model-value="
                                                onChapterCodeChange
                                            " />
                                        <v-btn
                                            icon
                                            size="small"
                                            variant="text"
                                            title="Adaugă capitol"
                                            @click="
                                                openStructureCreateDialog(
                                                    'chapter',
                                                )
                                            ">
                                            <v-icon size="small"
                                                >mdi-plus</v-icon
                                            >
                                        </v-btn>
                                        <v-btn
                                            icon
                                            size="small"
                                            variant="text"
                                            title="Editează capitol"
                                            :disabled="
                                                !productBuilderForm.chapterCode
                                            "
                                            @click="
                                                openStructureEditDialog(
                                                    'chapter',
                                                )
                                            ">
                                            <v-icon size="small"
                                                >mdi-pencil</v-icon
                                            >
                                        </v-btn>
                                    </div>
                                </v-col>
                                <v-col cols="12" md="4">
                                    <div class="d-flex align-center ga-1">
                                        <v-autocomplete
                                            v-model="
                                                productBuilderForm.subchapterCode
                                            "
                                            :items="subchapterOptions"
                                            item-title="title"
                                            item-value="code"
                                            label="Subcategorie"
                                            :class="
                                                compareFieldClass(
                                                    'subchapterCode',
                                                )
                                            "
                                            :variant="fieldVariant"
                                            density="compact"
                                            hide-details="auto"
                                            :rules="[requiredRule]"
                                            clearable
                                            class="flex-grow-1"
                                            @update:model-value="
                                                onSubchapterCodeChange
                                            " />
                                        <v-btn
                                            icon
                                            size="small"
                                            variant="text"
                                            :disabled="
                                                !primarySystem ||
                                                !productBuilderForm.chapterCode
                                            "
                                            title="Adaugă subcapitol"
                                            @click="
                                                openStructureCreateDialog(
                                                    'subchapter',
                                                )
                                            ">
                                            <v-icon size="small"
                                                >mdi-plus</v-icon
                                            >
                                        </v-btn>
                                        <v-btn
                                            icon
                                            size="small"
                                            variant="text"
                                            title="Editează subcapitol"
                                            :disabled="
                                                !productBuilderForm.chapterCode ||
                                                !productBuilderForm.subchapterCode
                                            "
                                            @click="
                                                openStructureEditDialog(
                                                    'subchapter',
                                                )
                                            ">
                                            <v-icon size="small"
                                                >mdi-pencil</v-icon
                                            >
                                        </v-btn>
                                    </div>
                                </v-col>
                                <v-col cols="12" md="4">
                                    <v-text-field
                                        v-model="
                                            productBuilderForm.progressiveNumber
                                        "
                                        label="Număr progresiv"
                                        :class="
                                            compareFieldClass(
                                                'progressiveNumber',
                                            )
                                        "
                                        :variant="fieldVariant"
                                        density="compact"
                                        hide-details="auto"
                                        autocomplete="off"
                                        :rules="[requiredRule]"
                                        clearable />
                                </v-col>
                                <v-col
                                    v-if="!isExistingProductEdit"
                                    cols="12"
                                    sm="6"
                                    md="5">
                                    <ProductCodeSuggestCombobox
                                        ref="productCodeSuggest"
                                        v-model="productCodeAutofill"
                                        :products="existingProducts"
                                        :variant="fieldVariant"
                                        @select-existing="
                                            onProductCodeSuggestSelect
                                        "
                                        @blur="onProductCodeAutofillBlur" />
                                </v-col>
                            </v-row>
                        </section>

                        <section
                            :id="panelDomId('manufacturer')"
                            class="builder-section builder-section--manufacturer">
                            <div class="builder-section-heading">
                                <div class="builder-section-title">
                                    Producător
                                </div>
                            </div>
                            <v-row>
                                <v-col cols="12" md="4">
                                    <div class="d-flex align-center ga-1">
                                        <v-autocomplete
                                            v-model="
                                                productBuilderForm.manufacturerName
                                            "
                                            :items="manufacturerOptions"
                                            item-title="name"
                                            item-value="name"
                                            label="Producător"
                                            :class="
                                                compareFieldClass(
                                                    'manufacturerName',
                                                )
                                            "
                                            :variant="fieldVariant"
                                            density="compact"
                                            hide-details="auto"
                                            class="flex-grow-1"
                                            :disabled="isCompoundCreateMode"
                                            :clearable="!isCompoundCreateMode"
                                            @update:model-value="
                                                onManufacturerNameChange
                                            " />
                                        <v-btn
                                            icon
                                            size="small"
                                            variant="text"
                                            title="Creează producător"
                                            :disabled="isCompoundCreateMode"
                                            @click="openCreateManufacturerDialog">
                                            <v-icon size="small"
                                                >mdi-plus</v-icon
                                            >
                                        </v-btn>
                                        <v-btn
                                            icon
                                            size="small"
                                            variant="text"
                                            title="Editează producător"
                                            :disabled="
                                                isCompoundCreateMode ||
                                                !selectedManufacturerId
                                            "
                                            @click="openEditManufacturerDialog">
                                            <v-icon size="small"
                                                >mdi-pencil</v-icon
                                            >
                                        </v-btn>
                                    </div>
                                </v-col>
                                <v-col cols="12" md="4">
                                    <v-autocomplete
                                        v-model="
                                            productBuilderForm.manufacturerCode
                                        "
                                        :items="manufacturerCodeOptions"
                                        item-title="title"
                                        item-value="code"
                                        label="Cod producător"
                                        :class="
                                            compareFieldClass('manufacturerCode')
                                        "
                                        :variant="fieldVariant"
                                        density="compact"
                                        hide-details="auto"
                                        :rules="[requiredRule]"
                                        :disabled="isCompoundCreateMode"
                                        :clearable="!isCompoundCreateMode"
                                        @update:model-value="
                                            onManufacturerCodeChange
                                        " />
                                </v-col>
                                <v-col cols="12" md="4">
                                    <v-text-field
                                        v-model="
                                            productBuilderForm.manufacturerProductCode
                                        "
                                        label="Cod produs furnizor"
                                        :class="
                                            compareFieldClass(
                                                'manufacturerProductCode',
                                            )
                                        "
                                        :variant="fieldVariant"
                                        density="compact"
                                        hide-details="auto"
                                        autocomplete="off"
                                        clearable />
                                </v-col>
                            </v-row>
                        </section>

                        <section
                            :id="panelDomId('product')"
                            class="builder-section builder-section--product">
                            <div
                                class="builder-section-heading d-flex align-center">
                                <div class="builder-section-title">
                                    Detalii produs
                                </div>
                                <v-switch
                                    v-model="
                                        productBuilderForm.isCompositeProduct
                                    "
                                    class="ms-auto flex-grow-0"
                                    label="Produs compus"
                                    color="primary"
                                    density="compact"
                                    hide-details
                                    :disabled="
                                        isPersistedEditMode ||
                                        isLegacyUpgradeMode
                                    "
                                    @update:model-value="
                                        onCompositeProductToggle
                                    " />
                            </div>
                            <v-row>
                                <v-col
                                    v-if="productBuilderForm.isCompositeProduct"
                                    cols="12">
                                    <v-sheet
                                        class="compound-components-panel pa-3"
                                        border
                                        rounded>
                                        <div
                                            class="d-flex flex-wrap align-center ga-2">
                                            <v-btn
                                                color="primary"
                                                variant="outlined"
                                                size="small"
                                                :disabled="
                                                    isPersistedEditMode ||
                                                    isLegacyUpgradeMode
                                                "
                                                @click="
                                                    openCompoundPickerDialog
                                                ">
                                                <v-icon start
                                                    >mdi-format-list-checks</v-icon
                                                >
                                                {{
                                                    selectedCompoundComponents.length
                                                        ? "Modifică componente"
                                                        : "Selectează componente"
                                                }}
                                            </v-btn>
                                            <div
                                                v-if="
                                                    selectedCompoundComponents.length
                                                "
                                                class="compound-components-summary d-flex flex-wrap ga-1">
                                                <v-chip
                                                    v-for="product in selectedCompoundComponents"
                                                    :key="`selected-${product.product_code}`"
                                                    size="small"
                                                    :color="
                                                        isComponentKeptInList(
                                                            product.product_code,
                                                        )
                                                            ? 'primary'
                                                            : undefined
                                                    "
                                                    :variant="
                                                        isComponentKeptInList(
                                                            product.product_code,
                                                        )
                                                            ? 'tonal'
                                                            : 'outlined'
                                                    ">
                                                    {{ product.product_code }}
                                                    <span
                                                        v-if="
                                                            !isComponentKeptInList(
                                                                product.product_code,
                                                            )
                                                        "
                                                        class="text-caption ms-1">
                                                        (doar în compus)
                                                    </span>
                                                </v-chip>
                                            </div>
                                        </div>

                                        <div
                                            v-if="compoundComponentsError"
                                            class="text-error text-caption mt-2">
                                            {{ compoundComponentsError }}
                                        </div>
                                    </v-sheet>
                                </v-col>
                                <v-col cols="12" md="6">
                                    <v-text-field
                                        v-model="productBuilderForm.productName"
                                        label="Denumire comercială"
                                        :class="compareFieldClass('productName')"
                                        :variant="fieldVariant"
                                        density="compact"
                                        hide-details="auto"
                                        autocomplete="off"
                                        :rules="[requiredRule]"
                                        clearable />
                                </v-col>
                                <v-col cols="12" md="4">
                                    <v-text-field
                                        v-model="
                                            productBuilderForm.manufacturerProductName
                                        "
                                        label="Denumire furnizor"
                                        :class="
                                            compareFieldClass(
                                                'manufacturerProductName',
                                            )
                                        "
                                        :variant="fieldVariant"
                                        density="compact"
                                        hide-details="auto"
                                        autocomplete="off"
                                        clearable />
                                </v-col>
                                <v-col cols="12" md="3">
                                    <v-select
                                        v-model="
                                            productBuilderForm.measurementUnit
                                        "
                                        :items="measurementUnits"
                                        item-title="text"
                                        item-value="value"
                                        label="U.M."
                                        :class="
                                            compareFieldClass('measurementUnit')
                                        "
                                        :variant="fieldVariant"
                                        density="compact"
                                        hide-details="auto"
                                        :rules="[requiredRule]" />
                                </v-col>
                                <v-col cols="12" md="3">
                                    <v-text-field
                                        v-model="productBuilderForm.weight"
                                        label="Greutate"
                                        :class="compareFieldClass('weight')"
                                        :variant="fieldVariant"
                                        density="compact"
                                        hide-details="auto"
                                        autocomplete="off"
                                        clearable />
                                </v-col>
                                <v-col cols="12" md="3">
                                    <v-text-field
                                        v-model="
                                            productBuilderForm.nomenclatureCode
                                        "
                                        label="Cod nomenclator"
                                        :class="
                                            compareFieldClass('nomenclatureCode')
                                        "
                                        :variant="fieldVariant"
                                        density="compact"
                                        hide-details="auto"
                                        autocomplete="off"
                                        clearable />
                                </v-col>
                                <v-col cols="12" md="3">
                                    <v-text-field
                                        v-model="
                                            productBuilderForm.commercialPolicyDiscount
                                        "
                                        label="Discount politică comercială (%)"
                                        :class="
                                            compareFieldClass(
                                                'commercialPolicyDiscount',
                                            )
                                        "
                                        :variant="fieldVariant"
                                        density="compact"
                                        hide-details="auto"
                                        autocomplete="off"
                                        clearable
                                        suffix="%" />
                                </v-col>
                                <v-col cols="12">
                                    <v-textarea
                                        v-model="productBuilderForm.productNotes"
                                        label="Note produs"
                                        :class="compareFieldClass('productNotes')"
                                        :variant="fieldVariant"
                                        density="compact"
                                        hide-details="auto"
                                        rows="2"
                                        auto-grow
                                        clearable />
                                </v-col>
                                <v-col cols="12" md="3">
                                    <v-select
                                        v-if="statusLockedToDeVerificat"
                                        :model-value="productBuilderForm.status"
                                        :items="lockedDeVerificatStatusOptions"
                                        item-title="title"
                                        item-value="value"
                                        label="Status"
                                        :class="compareFieldClass('status')"
                                        :variant="fieldVariant"
                                        density="compact"
                                        hide-details="auto"
                                        readonly />
                                    <v-combobox
                                        v-else
                                        :model-value="productStatusComboboxDisplay"
                                        :items="productStatusComboboxItems"
                                        label="Status"
                                        placeholder="Selectează sau scrie…"
                                        :class="compareFieldClass('status')"
                                        :variant="fieldVariant"
                                        density="compact"
                                        hide-details="auto"
                                        clearable
                                        @update:model-value="onProductStatusChange" />
                                </v-col>
                                <v-col cols="12" md="6">
                                    <v-checkbox
                                        v-model="
                                            productBuilderForm.visibleInOfferApp
                                        "
                                        label="Vizibil program ofertare"
                                        :class="
                                            compareFieldClass(
                                                'visibleInOfferApp',
                                            )
                                        "
                                        density="compact"
                                        hide-details="auto" />
                                </v-col>
                            </v-row>
                        </section>

                        <section
                            :id="panelDomId('supplierPricing')"
                            class="builder-section builder-section--supplier">
                            <div
                                class="builder-section-heading d-flex align-center flex-wrap ga-2">
                                <div class="builder-section-title">
                                    Detalii furnizor
                                </div>
                                <template v-if="!readOnly">
                                    <v-spacer />
                                    <v-switch
                                        v-model="activeUsesManufacturerAsFurnizor"
                                        label="Producător ca furnizor"
                                        density="compact"
                                        hide-details
                                        color="primary" />
                                    <v-btn
                                        variant="text"
                                        size="small"
                                        color="primary"
                                        prepend-icon="mdi-plus"
                                        @click="addFurnizorTab">
                                        Adaugă furnizor
                                    </v-btn>
                                    <v-btn
                                        v-if="
                                            (
                                                productBuilderForm.furnizorPricings ||
                                                []
                                            ).length > 1
                                        "
                                        icon
                                        size="small"
                                        variant="text"
                                        color="error"
                                        title="Șterge furnizor"
                                        @click="removeActiveFurnizorTab">
                                        <v-icon size="small">mdi-delete</v-icon>
                                    </v-btn>
                                </template>
                            </div>

                            <div
                                class="d-flex flex-wrap align-center ga-2 mb-3 compare-furnizor-tabs-nav">
                                <v-tabs
                                    :model-value="
                                        productBuilderForm.activeFurnizorPricingKey
                                    "
                                    density="compact"
                                    class="flex-grow-1"
                                    @update:model-value="onFurnizorTabChange">
                                    <v-tab
                                        v-for="pricing in productBuilderForm.furnizorPricings"
                                        :key="pricing.clientKey"
                                        :value="pricing.clientKey"
                                        :class="{
                                            'compare-furnizor-tab--changed':
                                                isCompareFurnizorTabChanged(
                                                    pricing,
                                                ),
                                        }">
                                        {{ furnizorTabLabel(pricing) }}
                                        <v-chip
                                            v-if="pricing.isDefault"
                                            class="ms-2"
                                            size="x-small"
                                            color="primary"
                                            variant="tonal">
                                            implicit
                                        </v-chip>
                                    </v-tab>
                                </v-tabs>
                                <v-btn
                                    v-if="!readOnly && !activeFurnizorPricing?.isDefault"
                                    class="flex-shrink-0"
                                    variant="tonal"
                                    size="small"
                                    color="primary"
                                    @click="markActiveFurnizorDefault">
                                    Setează ca implicit
                                </v-btn>
                            </div>

                            <v-row
                                v-if="!readOnly && !activeUsesManufacturerAsFurnizor"
                                class="mb-1"
                                dense>
                                <v-col cols="12" md="8" lg="6">
                                    <div class="d-flex align-center ga-1">
                                        <v-autocomplete
                                            :model-value="activeFurnizorSelectCode"
                                            :items="furnizorSelectOptions"
                                            item-title="title"
                                            item-value="code"
                                            label="Furnizor"
                                            :class="
                                                compareFieldClass('furnizorCode')
                                            "
                                            :variant="fieldVariant"
                                            density="compact"
                                            hide-details="auto"
                                            clearable
                                            class="flex-grow-1"
                                            @update:model-value="
                                                onActiveFurnizorSelectCode
                                            " />
                                        <v-btn
                                            icon
                                            size="small"
                                            variant="text"
                                            title="Creează furnizor"
                                            @click="openCreateSupplierDialog">
                                            <v-icon size="small"
                                                >mdi-plus</v-icon
                                            >
                                        </v-btn>
                                        <v-btn
                                            icon
                                            size="small"
                                            variant="text"
                                            title="Editează furnizor"
                                            :disabled="!activeFurnizorId"
                                            @click="openEditSupplierDialog">
                                            <v-icon size="small"
                                                >mdi-pencil</v-icon
                                            >
                                        </v-btn>
                                    </div>
                                </v-col>
                            </v-row>

                            <v-row class="mb-2">
                                <v-col cols="12" md="4">
                                    <v-text-field
                                        :model-value="
                                            pricingInputDisplayValue(
                                                'supplierListPriceNew',
                                            )
                                        "
                                        label="Preț listă furnizor"
                                        :class="{
                                            'pricing-field--manual':
                                                isPricingFieldHighlighted(
                                                    'supplierListPriceNew',
                                                ),
                                        }"
                                        :variant="fieldVariant"
                                        density="compact"
                                        hide-details="auto"
                                        autocomplete="off"
                                        :hint="
                                            pricingInputFieldSavedHint(
                                                'supplierListPriceNew',
                                            )
                                        "
                                        persistent-hint
                                        :rules="[
                                            requiredRule,
                                            positiveNumberRule,
                                        ]"
                                        @focus="
                                            onPricingInputFieldFocus(
                                                'supplierListPriceNew',
                                            )
                                        "
                                        @update:model-value="
                                            onPricingInputFieldDraft(
                                                'supplierListPriceNew',
                                                $event,
                                            )
                                        "
                                        @blur="
                                            onPricingInputFieldBlur(
                                                'supplierListPriceNew',
                                            )
                                        ">
                                        <template
                                            v-if="
                                                pricingInputFieldChangeHint(
                                                    'supplierListPriceNew',
                                                )
                                            "
                                            #append-inner>
                                            <span
                                                class="supplier-list-price-change font-weight-medium"
                                                :class="
                                                    pricingInputFieldChangeHintClass(
                                                        'supplierListPriceNew',
                                                    )
                                                ">
                                                {{
                                                    pricingInputFieldChangeHint(
                                                        'supplierListPriceNew',
                                                    )
                                                }}
                                            </span>
                                        </template>
                                    </v-text-field>
                                </v-col>
                                <v-col cols="12" md="4">
                                    <v-select
                                        :model-value="
                                            productBuilderForm.currency
                                        "
                                        :items="currencyOptions"
                                        item-title="title"
                                        item-value="value"
                                        label="Monedă"
                                        :class="{
                                            'pricing-field--manual':
                                                isPricingFieldHighlighted(
                                                    'currency',
                                                ),
                                        }"
                                        :variant="fieldVariant"
                                        density="compact"
                                        hide-details="auto"
                                        @update:model-value="
                                            onPricingSelectFieldChange
                                        " />
                                </v-col>
                                <v-col cols="12" md="4">
                                    <v-text-field
                                        :model-value="
                                            productBuilderForm.paymentTermDays
                                        "
                                        label="Termen plată (zile)"
                                        :class="
                                            compareFieldClass('paymentTermDays')
                                        "
                                        :variant="fieldVariant"
                                        density="compact"
                                        hide-details="auto"
                                        inputmode="numeric"
                                        autocomplete="off"
                                        clearable
                                        @update:model-value="
                                            onPaymentTermDaysInput
                                        " />
                                </v-col>
                            </v-row>

                            <v-row class="mb-2">
                                <v-col cols="12">
                                    <v-textarea
                                        v-model="productBuilderForm.notes"
                                        label="Note - Furnizor"
                                        :class="compareFieldClass('notes')"
                                        :variant="fieldVariant"
                                        density="compact"
                                        hide-details="auto"
                                        rows="2"
                                        auto-grow
                                        clearable />
                                </v-col>
                            </v-row>

                            <div
                                class="d-flex align-center mb-3 flex-wrap ga-2">
                                <div class="text-subtitle-2 font-weight-medium">
                                    Discount
                                </div>
                                <v-spacer />
                                <v-btn
                                    variant="text"
                                    size="small"
                                    color="primary"
                                    prepend-icon="mdi-plus"
                                    @click="addDiscount">
                                    Adaugă discount
                                </v-btn>
                            </div>
                            <v-row v-if="hasActiveDiscountFields">
                                <v-col
                                    v-for="(
                                        discount, index
                                    ) in productBuilderForm.discounts"
                                    :key="`discount-${index}`"
                                    cols="12"
                                    md="3">
                                    <div class="d-flex justify-end mb-1">
                                        <v-btn
                                            icon
                                            size="x-small"
                                            variant="text"
                                            color="error"
                                            title="Șterge discount"
                                            @click="removeDiscount(index)">
                                            <v-icon size="small">
                                                mdi-delete
                                            </v-icon>
                                        </v-btn>
                                    </div>
                                    <percentage-text-field
                                        :model-value="
                                            pricingInputDisplayValue(
                                                `discounts.${index}`,
                                            )
                                        "
                                        :label="discountFieldLabel(index)"
                                        :class="{
                                            'pricing-field--manual':
                                                isPricingFieldHighlighted(
                                                    `discounts.${index}`,
                                                ),
                                        }"
                                        :variant="fieldVariant"
                                        density="compact"
                                        hide-details
                                        @focus="onDiscountFieldFocus(index)"
                                        @update:model-value="
                                            onDiscountFieldDraft(index, $event)
                                        "
                                        @blur="onDiscountFieldBlur(index)">
                                        <template #append-inner>
                                            <span
                                                v-if="
                                                    discountFieldChangeHint(
                                                        index,
                                                    )
                                                "
                                                class="supplier-list-price-change font-weight-medium me-1"
                                                :class="
                                                    discountFieldChangeHintClass(
                                                        index,
                                                    )
                                                ">
                                                {{
                                                    discountFieldChangeHint(
                                                        index,
                                                    )
                                                }}
                                            </span>
                                            <v-icon
                                                v-if="
                                                    hasDiscountValueToClear(
                                                        index,
                                                    )
                                                "
                                                size="small"
                                                title="Golește valoarea"
                                                @click.stop="
                                                    clearDiscountValue(index)
                                                ">
                                                mdi-close
                                            </v-icon>
                                        </template>
                                    </percentage-text-field>
                                    <v-textarea
                                        v-model="
                                            productBuilderForm.discountNotes[index]
                                        "
                                        :label="discountNoteFieldLabel(index)"
                                        :variant="fieldVariant"
                                        density="compact"
                                        hide-details="auto"
                                        rows="2"
                                        auto-grow
                                        clearable
                                        class="mt-2" />
                                </v-col>
                            </v-row>
                        </section>

                        <section
                            :id="panelDomId('transportMarkup')"
                            class="builder-section builder-section--pricing">
                            <div
                                class="builder-section-heading d-flex align-center flex-wrap ga-2">
                                <div class="builder-section-title">
                                    Calculare preț
                                </div>
                                <v-btn
                                    v-if="hasPricingChangesFromBaseline"
                                    icon="mdi-restore"
                                    size="small"
                                    variant="text"
                                    color="warning"
                                    title="Resetează la valorile salvate"
                                    @click="resetAllPricingFieldsToBaseline" />
                                <v-spacer />
                                <div
                                    class="lock-markup-to-reference d-inline-flex align-center">
                                    <v-checkbox
                                        :model-value="
                                            Boolean(
                                                productBuilderForm.lockMarkupToReference,
                                            )
                                        "
                                        density="compact"
                                        hide-details
                                        color="success"
                                        :disabled="readOnly"
                                        @update:model-value="
                                            onLockMarkupToReferenceChange
                                        ">
                                        <template #label>
                                            <span
                                                class="lock-markup-to-reference__label d-inline-flex align-center ga-1">
                                                <v-icon
                                                    size="18"
                                                    :icon="
                                                        productBuilderForm.lockMarkupToReference
                                                            ? 'mdi-lock'
                                                            : 'mdi-lock-open-variant-outline'
                                                    " />
                                                <span>Adaos = referință</span>
                                            </span>
                                        </template>
                                    </v-checkbox>
                                    <span
                                        class="lock-markup-to-reference__info d-inline-flex align-center"
                                        tabindex="0"
                                        @click.stop.prevent>
                                        <v-icon
                                            size="18"
                                            color="medium-emphasis"
                                            icon="mdi-information-outline" />
                                        <v-tooltip
                                            activator="parent"
                                            location="bottom"
                                            max-width="280">
                                            La schimbări de achiziție, adaosul
                                            actual rămâne egal cu adaosul de
                                            referință.
                                        </v-tooltip>
                                    </span>
                                </div>
                            </div>
                            <v-row>
                                <v-col cols="12" md="3">
                                    <percentage-text-field
                                        :model-value="
                                            pricingInputDisplayValue(
                                                'transportCostRate',
                                            )
                                        "
                                        :label="transportCostRateLabel"
                                        :class="{
                                            'pricing-field--manual':
                                                isPricingFieldHighlighted(
                                                    'transportCostRate',
                                                ),
                                            'pricing-field--formula':
                                                isTransportCostFormulaMode,
                                        }"
                                        :variant="fieldVariant"
                                        density="compact"
                                        hide-details="auto"
                                        :hint="
                                            pricingInputFieldSavedHint(
                                                'transportCostRate',
                                                { asPercent: true },
                                            )
                                        "
                                        persistent-hint
                                        @focus="
                                            onPricingInputFieldFocus(
                                                'transportCostRate',
                                            )
                                        "
                                        @update:model-value="
                                            onPricingInputFieldDraft(
                                                'transportCostRate',
                                                $event,
                                            )
                                        "
                                        @blur="
                                            onPricingInputFieldBlur(
                                                'transportCostRate',
                                            )
                                        ">
                                        <template #append-inner>
                                            <span
                                                v-if="
                                                    pricingInputFieldChangeHint(
                                                        'transportCostRate',
                                                    )
                                                "
                                                class="supplier-list-price-change font-weight-medium me-1"
                                                :class="
                                                    pricingInputFieldChangeHintClass(
                                                        'transportCostRate',
                                                    )
                                                ">
                                                {{
                                                    pricingInputFieldChangeHint(
                                                        'transportCostRate',
                                                    )
                                                }}
                                            </span>
                                            <v-btn
                                                icon
                                                size="x-small"
                                                :variant="
                                                    isTransportCostFormulaMode
                                                        ? 'tonal'
                                                        : 'text'
                                                "
                                                :color="
                                                    isTransportCostFormulaMode
                                                        ? 'success'
                                                        : undefined
                                                "
                                                :class="{
                                                    'transport-cost-formula-btn--active':
                                                        isTransportCostFormulaMode,
                                                }"
                                                :title="transportCostFormulaButtonTitle"
                                                @click.stop="
                                                    openTransportCostFormulaModal
                                                ">
                                                <v-icon size="small">
                                                    mdi-calculator
                                                </v-icon>
                                            </v-btn>
                                        </template>
                                    </percentage-text-field>
                                </v-col>
                                <v-col cols="12" md="3">
                                    <v-text-field
                                        :model-value="
                                            pricingInputDisplayValue(
                                                'stampDutyAmount',
                                            )
                                        "
                                        label="Taxă de timbru"
                                        :class="{
                                            'pricing-field--manual':
                                                isPricingFieldHighlighted(
                                                    'stampDutyAmount',
                                                ),
                                            'pricing-field--formula':
                                                isStampDutyFormulaMode,
                                        }"
                                        :variant="fieldVariant"
                                        density="compact"
                                        hide-details="auto"
                                        inputmode="decimal"
                                        autocomplete="off"
                                        :hint="
                                            pricingInputFieldSavedHint(
                                                'stampDutyAmount',
                                            )
                                        "
                                        persistent-hint
                                        @focus="
                                            onPricingInputFieldFocus(
                                                'stampDutyAmount',
                                            )
                                        "
                                        @update:model-value="
                                            onPricingInputFieldDraft(
                                                'stampDutyAmount',
                                                $event,
                                            )
                                        "
                                        @blur="
                                            onPricingInputFieldBlur(
                                                'stampDutyAmount',
                                            )
                                        ">
                                        <template #append-inner>
                                            <span
                                                v-if="
                                                    pricingInputFieldChangeHint(
                                                        'stampDutyAmount',
                                                    )
                                                "
                                                class="supplier-list-price-change font-weight-medium me-1"
                                                :class="
                                                    pricingInputFieldChangeHintClass(
                                                        'stampDutyAmount',
                                                    )
                                                ">
                                                {{
                                                    pricingInputFieldChangeHint(
                                                        'stampDutyAmount',
                                                    )
                                                }}
                                            </span>
                                            <v-btn
                                                icon
                                                size="x-small"
                                                :variant="
                                                    isStampDutyFormulaMode
                                                        ? 'tonal'
                                                        : 'text'
                                                "
                                                :color="
                                                    isStampDutyFormulaMode
                                                        ? 'success'
                                                        : undefined
                                                "
                                                :class="{
                                                    'transport-cost-formula-btn--active':
                                                        isStampDutyFormulaMode,
                                                }"
                                                :title="stampDutyFormulaButtonTitle"
                                                @click.stop="
                                                    openStampDutyFormulaModal
                                                ">
                                                <v-icon size="small">
                                                    mdi-calculator
                                                </v-icon>
                                            </v-btn>
                                        </template>
                                    </v-text-field>
                                </v-col>
                                <v-col cols="12" md="3">
                                    <percentage-text-field
                                        :model-value="
                                            pricingInputDisplayValue(
                                                'markupReference',
                                            )
                                        "
                                        label="Adaos referință (%)"
                                        :class="{
                                            'pricing-field--manual':
                                                isPricingFieldHighlighted(
                                                    'markupReference',
                                                ),
                                        }"
                                        :variant="fieldVariant"
                                        density="compact"
                                        hide-details="auto"
                                        :hint="
                                            pricingInputFieldSavedHint(
                                                'markupReference',
                                                { asPercent: true },
                                            )
                                        "
                                        persistent-hint
                                        @focus="
                                            onPricingInputFieldFocus(
                                                'markupReference',
                                            )
                                        "
                                        @update:model-value="
                                            onPricingInputFieldDraft(
                                                'markupReference',
                                                $event,
                                            )
                                        "
                                        @blur="
                                            onPricingInputFieldBlur(
                                                'markupReference',
                                            )
                                        ">
                                        <template #append-inner>
                                            <span
                                                v-if="
                                                    pricingInputFieldChangeHint(
                                                        'markupReference',
                                                    )
                                                "
                                                class="supplier-list-price-change font-weight-medium"
                                                :class="
                                                    pricingInputFieldChangeHintClass(
                                                        'markupReference',
                                                    )
                                                ">
                                                {{
                                                    pricingInputFieldChangeHint(
                                                        'markupReference',
                                                    )
                                                }}
                                            </span>
                                        </template>
                                    </percentage-text-field>
                                </v-col>
                                <v-col cols="12" md="3">
                                    <percentage-text-field
                                        :model-value="
                                            pricingInputDisplayValue(
                                                'markupActual',
                                            )
                                        "
                                        label="Adaos actual (%)"
                                        :class="{
                                            'pricing-field--manual':
                                                isPricingFieldHighlighted(
                                                    'markupActual',
                                                ),
                                        }"
                                        :variant="fieldVariant"
                                        density="compact"
                                        hide-details="auto"
                                        :hint="
                                            pricingInputFieldSavedHint(
                                                'markupActual',
                                                { asPercent: true },
                                            )
                                        "
                                        persistent-hint
                                        @focus="
                                            onPricingInputFieldFocus(
                                                'markupActual',
                                            )
                                        "
                                        @update:model-value="
                                            onPricingInputFieldDraft(
                                                'markupActual',
                                                $event,
                                            )
                                        "
                                        @blur="
                                            onPricingInputFieldBlur(
                                                'markupActual',
                                            )
                                        ">
                                        <template #append-inner>
                                            <span
                                                v-if="
                                                    pricingInputFieldChangeHint(
                                                        'markupActual',
                                                    )
                                                "
                                                class="supplier-list-price-change font-weight-medium"
                                                :class="
                                                    pricingInputFieldChangeHintClass(
                                                        'markupActual',
                                                    )
                                                ">
                                                {{
                                                    pricingInputFieldChangeHint(
                                                        'markupActual',
                                                    )
                                                }}
                                            </span>
                                        </template>
                                    </percentage-text-field>
                                </v-col>
                                <v-col
                                    v-for="resultField in editablePricingFields"
                                    :key="resultField.key"
                                    cols="12"
                                    md="3">
                                    <v-text-field
                                        :model-value="
                                            resultFieldDisplayValue(
                                                resultField.key,
                                                resultField.format,
                                            )
                                        "
                                        :label="resultField.label"
                                        :class="{
                                            'pricing-field--manual':
                                                isPricingFieldHighlighted(
                                                    resultField.key,
                                                ),
                                        }"
                                        :variant="fieldVariant"
                                        density="compact"
                                        hide-details="auto"
                                        :hint="
                                            pricingResultFieldSavedHint(
                                                resultField.key,
                                            )
                                        "
                                        persistent-hint
                                        autocomplete="off"
                                        @focus="
                                            onPricingResultFieldFocus(
                                                resultField,
                                            )
                                        "
                                        @update:model-value="
                                            onPricingResultFieldDraft(
                                                resultField,
                                                $event,
                                            )
                                        "
                                        @blur="
                                            onPricingResultFieldBlur(
                                                resultField,
                                            )
                                        "
                                        @keydown.enter.prevent="
                                            onPricingResultFieldBlur(
                                                resultField,
                                            )
                                        ">
                                        <template
                                            v-if="
                                                pricingResultFieldChangeHint(
                                                    resultField.key,
                                                    resultField.format,
                                                )
                                            "
                                            #append-inner>
                                            <span
                                                class="supplier-list-price-change font-weight-medium"
                                                :class="
                                                    pricingResultFieldChangeHintClass(
                                                        resultField.key,
                                                        resultField.format,
                                                    )
                                                ">
                                                {{
                                                    pricingResultFieldChangeHint(
                                                        resultField.key,
                                                        resultField.format,
                                                    )
                                                }}
                                            </span>
                                        </template>
                                    </v-text-field>
                                </v-col>
                            </v-row>

                            <div
                                class="d-flex align-center mb-3 mt-4 flex-wrap ga-2">
                                <div class="text-subtitle-2 font-weight-medium">
                                    Majorare costuri
                                </div>
                                <v-spacer />
                                <v-btn
                                    variant="text"
                                    size="small"
                                    color="primary"
                                    prepend-icon="mdi-plus"
                                    @click="addCostUplift">
                                    Adaugă majorare
                                </v-btn>
                            </div>
                            <v-row v-if="hasActiveCostUpliftFields">
                                <v-col
                                    v-for="(
                                        _uplift, index
                                    ) in productBuilderForm.costUplifts"
                                    :key="`cost-uplift-${index}`"
                                    cols="12"
                                    md="3">
                                    <div class="d-flex justify-end mb-1">
                                        <v-btn
                                            icon
                                            size="x-small"
                                            variant="text"
                                            color="error"
                                            title="Șterge majorare"
                                            @click="removeCostUplift(index)">
                                            <v-icon size="small">
                                                mdi-delete
                                            </v-icon>
                                        </v-btn>
                                    </div>
                                    <v-select
                                        :model-value="
                                            costUpliftTypeAt(index)
                                        "
                                        :items="costUpliftTypeOptions"
                                        item-title="title"
                                        item-value="value"
                                        label="Tip majorare"
                                        :variant="fieldVariant"
                                        density="compact"
                                        hide-details="auto"
                                        class="mb-2"
                                        @update:model-value="
                                            onCostUpliftTypeChange(
                                                index,
                                                $event,
                                            )
                                        " />
                                    <percentage-text-field
                                        v-if="costUpliftTypeAt(index) === 'percent'"
                                        :model-value="
                                            productBuilderForm.costUplifts[
                                                index
                                            ]
                                        "
                                        :label="costUpliftFieldLabel(index)"
                                        :variant="fieldVariant"
                                        density="compact"
                                        hide-details="auto"
                                        @update:model-value="
                                            onCostUpliftFieldDraft(
                                                index,
                                                $event,
                                            )
                                        "
                                        @blur="onCostUpliftFieldBlur(index)">
                                        <template #append-inner>
                                            <v-icon
                                                v-if="
                                                    hasCostUpliftValueToClear(
                                                        index,
                                                    )
                                                "
                                                size="small"
                                                title="Golește valoarea"
                                                @click.stop="
                                                    clearCostUpliftValue(index)
                                                ">
                                                mdi-close
                                            </v-icon>
                                        </template>
                                    </percentage-text-field>
                                    <v-text-field
                                        v-else
                                        :model-value="
                                            productBuilderForm.costUplifts[
                                                index
                                            ]
                                        "
                                        :label="costUpliftFieldLabel(index)"
                                        :variant="fieldVariant"
                                        density="compact"
                                        hide-details="auto"
                                        suffix="RON"
                                        inputmode="decimal"
                                        autocomplete="off"
                                        @update:model-value="
                                            onCostUpliftFieldDraft(
                                                index,
                                                $event,
                                            )
                                        "
                                        @blur="onCostUpliftFieldBlur(index)">
                                        <template #append-inner>
                                            <v-icon
                                                v-if="
                                                    hasCostUpliftValueToClear(
                                                        index,
                                                    )
                                                "
                                                size="small"
                                                title="Golește valoarea"
                                                @click.stop="
                                                    clearCostUpliftValue(index)
                                                ">
                                                mdi-close
                                            </v-icon>
                                        </template>
                                    </v-text-field>
                                    <v-textarea
                                        v-model="
                                            productBuilderForm.costUpliftNotes[
                                                index
                                            ]
                                        "
                                        :label="
                                            costUpliftNoteFieldLabel(index)
                                        "
                                        :variant="fieldVariant"
                                        density="compact"
                                        hide-details="auto"
                                        rows="2"
                                        auto-grow
                                        clearable
                                        class="mt-2" />
                                </v-col>
                            </v-row>
                        </section>

                        <section
                            :id="panelDomId('results')"
                            class="builder-section builder-section--results">
                            <div class="builder-section-heading">
                                <div class="builder-section-title">
                                    Rezultate finale calculate
                                </div>
                            </div>
                            <v-row>
                                <v-col
                                    v-for="resultField in displayResultFields"
                                    :key="resultField.key"
                                    cols="12"
                                    md="4">
                                    <div
                                        class="pricing-result-info pa-3 rounded border"
                                        :class="{
                                            'pricing-result-info--manual':
                                                isPricingFieldHighlighted(
                                                    resultField.key,
                                                ),
                                        }">
                                        <div
                                            class="text-caption text-medium-emphasis">
                                            {{ resultField.label }}
                                        </div>
                                        <div
                                            class="d-flex align-center flex-wrap ga-2 mt-1">
                                            <span
                                                class="text-body-1 font-weight-medium">
                                                {{
                                                    formatResultValue(
                                                        resultField.key,
                                                        resultField.format,
                                                    ) || "—"
                                                }}
                                            </span>
                                            <span
                                                v-if="
                                                    pricingResultFieldChangeHint(
                                                        resultField.key,
                                                        resultField.format,
                                                    )
                                                "
                                                class="supplier-list-price-change font-weight-medium"
                                                :class="
                                                    pricingResultFieldChangeHintClass(
                                                        resultField.key,
                                                        resultField.format,
                                                    )
                                                ">
                                                {{
                                                    pricingResultFieldChangeHint(
                                                        resultField.key,
                                                        resultField.format,
                                                    )
                                                }}
                                            </span>
                                        </div>
                                        <div
                                            v-if="
                                                pricingResultFieldSavedHint(
                                                    resultField.key,
                                                )
                                            "
                                            class="text-caption text-medium-emphasis mt-1">
                                            {{
                                                pricingResultFieldSavedHint(
                                                    resultField.key,
                                                )
                                            }}
                                        </div>
                                    </div>
                                </v-col>
                            </v-row>
                        </section>
                    </div>
                </v-form>
                </fieldset>
                </template>
            </v-card-text>

            <v-card-actions
                v-if="!formLoading"
                class="d-flex flex-wrap justify-end ga-2 pa-4">
                <v-spacer class="d-none d-sm-flex" />
                <v-btn
                    color="danger"
                    variant="elevated"
                    :block="$vuetify.display.xs"
                    :disabled="savingDraft || savingDirect"
                    @click="close">
                    {{ readOnly ? 'Închide' : 'Anulează' }}
                </v-btn>
                <template v-if="simulationMode">
                    <v-btn
                        color="primary"
                        variant="elevated"
                        style="color: white !important"
                        :block="$vuetify.display.xs"
                        @click="submitSimulationApply">
                        Aplică simularea
                    </v-btn>
                </template>
                <template v-else-if="!readOnly">
                    <v-btn
                        color="secondary"
                        variant="elevated"
                        style="color: white !important"
                        :block="$vuetify.display.xs"
                        :loading="savingDraft"
                        :disabled="savingDraft || savingDirect"
                        @click="submitDraft">
                        Salvează în lucru
                    </v-btn>
                    <v-btn
                        v-if="allowDirectSave"
                        color="primary"
                        variant="elevated"
                        style="color: white !important"
                        :block="$vuetify.display.xs"
                        :loading="savingDirect"
                        :disabled="!canSaveDirectToList"
                        :title="saveDirectDisabledReason"
                        @click="submitDirectSave">
                        Salvează în listă
                    </v-btn>
                </template>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <v-dialog
        v-model="propagateSellingPriceDialogOpen"
        max-width="520"
        persistent
        :z-index="3400">
        <v-card>
            <v-card-title class="text-h6">
                Actualizare preț vânzare
            </v-card-title>
            <v-card-text>
                <p class="mb-2">
                    Produsul are mai mulți furnizori. Doriți să actualizați
                    automat prețul de vânzare
                    <strong v-if="pendingPropagateSellingPrice">
                        ({{
                            formatSummaryPrice(pendingPropagateSellingPrice)
                        }}
                        RON cu TVA)
                    </strong>
                    și la ceilalți furnizori?
                </p>
                <p class="mb-0 text-medium-emphasis">
                    Adaosul actual va fi recalculat pe fiecare furnizor în
                    funcție de prețul său de achiziție.
                </p>
            </v-card-text>
            <v-card-actions class="pa-4 flex-wrap ga-2">
                <v-spacer class="d-none d-sm-flex" />
                <v-btn
                    variant="text"
                    @click="cancelPropagateSellingPriceDialog">
                    Anulează
                </v-btn>
                <v-btn
                    color="secondary"
                    variant="elevated"
                    style="color: white !important"
                    @click="resolvePropagateSellingPriceDialog(false)">
                    Nu, doar acest furnizor
                </v-btn>
                <v-btn
                    color="primary"
                    variant="elevated"
                    style="color: white !important"
                    @click="resolvePropagateSellingPriceDialog(true)">
                    Da, toți furnizorii
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <PriceListPropagateFurnizorFieldsDialog
        v-model="propagateFurnizorFieldsDialogOpen"
        :furnizor-label="activePropagateFurnizorLabel"
        :source-product-code="computedProduct.productCode"
        :source-product-name="productBuilderForm.productName"
        :peers="propagateFurnizorPeers"
        :peers-total-count="propagateFurnizorPeersTotalCount"
        :page="propagateFurnizorPeersPage"
        :items-per-page="propagateFurnizorPeersItemsPerPage"
        :default-field-keys="propagateFurnizorDefaultFieldKeys"
        :source-form="productBuilderForm"
        :identity="activePropagateFurnizorIdentity"
        :pricing-settings="effectivePricingSettings"
        :saving="savingDraft"
        :loading="propagateFurnizorPeersLoading"
        :resolve-all-peer-codes="resolveAllPropagateFurnizorPeerCodes"
        @skip="onPropagateFurnizorFieldsSkip"
        @apply="onPropagateFurnizorFieldsApply"
        @cancel="onPropagateFurnizorFieldsCancel"
        @update:page="onPropagateFurnizorPeersPageChange"
        @update:items-per-page="onPropagateFurnizorPeersItemsPerPageChange"
        @filters="onPropagateFurnizorPeersFilters"
        @search="onPropagateFurnizorPeersSearch" />

    <v-dialog
        v-model="currencyConvertDialogOpen"
        max-width="520"
        persistent
        :z-index="3400">
        <v-card>
            <v-card-title class="text-h6">
                Conversie preț listă
            </v-card-title>
            <v-card-text>
                <p class="mb-2">
                    Schimbați moneda din
                    <strong>{{ currencyConvertFromLabel }}</strong>
                    în
                    <strong>{{ currencyConvertToLabel }}</strong>.
                </p>
                <p class="mb-0">
                    Doriți să convertiți automat
                    <strong>prețul listă furnizor</strong>
                    cu cursul setat pe listă?
                </p>
            </v-card-text>
            <v-card-actions class="pa-4 flex-wrap ga-2">
                <v-spacer class="d-none d-sm-flex" />
                <v-btn
                    variant="text"
                    @click="cancelCurrencyConvertDialog">
                    Anulează
                </v-btn>
                <v-btn
                    color="secondary"
                    variant="elevated"
                    style="color: white !important"
                    @click="resolveCurrencyConvertDialog(false)">
                    Nu, doar moneda
                </v-btn>
                <v-btn
                    color="primary"
                    variant="elevated"
                    style="color: white !important"
                    @click="resolveCurrencyConvertDialog(true)">
                    Da, convertește
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <v-dialog
        v-model="existingProductUpdateConfirmOpen"
        max-width="520"
        persistent
        :z-index="3400">
        <v-card>
            <v-card-title class="text-h6">
                Cod produs existent
            </v-card-title>
            <v-card-text>
                <p class="mb-0">
                    Codul produsului există deja în listă. Doriți să
                    continuați cu actualizarea produsului existent în lista
                    în lucru?
                </p>
            </v-card-text>
            <v-card-actions class="pa-4 flex-wrap ga-2">
                <v-spacer class="d-none d-sm-flex" />
                <v-btn
                    variant="text"
                    @click="cancelExistingProductUpdateDialog">
                    Anulează
                </v-btn>
                <v-btn
                    color="primary"
                    variant="elevated"
                    style="color: white !important"
                    @click="confirmExistingProductUpdateDialog">
                    Continuă cu actualizarea
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <transport-cost-formula-modal
        v-model="transportCostFormulaModalOpen"
        :breakdown="productBuilderForm.transportCostBreakdown"
        :supplier-list-price-new="productBuilderForm.supplierListPriceNew"
        :supplier-currency="productBuilderForm.currency"
        :pricing-settings="effectivePricingSettings"
        :read-only="readOnly"
        @apply="onTransportCostFormulaApply" />

    <stamp-duty-formula-modal
        v-model="stampDutyFormulaModalOpen"
        :breakdown="productBuilderForm.stampDutyBreakdown"
        :product-weight="productBuilderForm.weight"
        :pricing-settings="effectivePricingSettings"
        :read-only="readOnly"
        @apply="onStampDutyFormulaApply" />

    <v-dialog
        v-model="compoundPickerDialogOpen"
        max-width="900"
        scrollable
        persistent>
        <v-card>
            <v-toolbar density="compact">
                <v-toolbar-title>Selectează produse componente</v-toolbar-title>
                <v-spacer />
                <v-btn icon variant="text" @click="closeCompoundPickerDialog">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-toolbar>

            <v-card-text class="pt-4">
                <v-text-field
                    v-model="compoundProductSearch"
                    label="Caută produse (cod sau nume)"
                    prepend-inner-icon="mdi-magnify"
                    :variant="fieldVariant"
                    density="compact"
                    hide-details="auto"
                    clearable
                    autofocus
                    class="mb-3" />

                <v-data-table
                    v-model="compoundDraftCodes"
                    v-model:page="compoundPickerPage"
                    v-model:items-per-page="compoundPickerItemsPerPage"
                    :headers="compoundPickerHeaders"
                    :items="compoundPickerTableItems"
                    item-value="value"
                    show-select
                    density="compact"
                    :row-props="compoundPickerRowProps"
                    :items-per-page-options="[
                        { value: 10, title: '10' },
                        { value: 25, title: '25' },
                        { value: 50, title: '50' },
                    ]">
                    <template v-slot:[`item.product_code`]="{ item }">
                        {{ item.product_code }}
                    </template>
                    <template v-slot:[`item.product_name`]="{ item }">
                        {{ item.product_name || "—" }}
                    </template>
                    <template v-slot:[`item.price`]="{ item }">
                        {{ formatSummaryPrice(item.price) }}
                    </template>
                    <template v-slot:[`item.keep_in_list`]="{ item }">
                        <v-checkbox
                            v-if="compoundDraftSelectedSet.has(item.value)"
                            :model-value="isCompoundDraftKeptInList(item.value)"
                            density="compact"
                            hide-details
                            color="secondary"
                            @update:model-value="
                                setCompoundDraftKeepInList(item.value, $event)
                            " />
                        <span v-else class="text-medium-emphasis">—</span>
                    </template>
                </v-data-table>

                <div
                    v-if="compoundDraftPriceSum > 0"
                    class="text-caption text-medium-emphasis mt-2 text-end">
                    Sumă: {{ formatSummaryPrice(compoundDraftPriceSum) }}
                </div>
            </v-card-text>

            <v-card-actions class="pa-4">
                <v-spacer />
                <v-btn variant="text" @click="closeCompoundPickerDialog">
                    Anulează
                </v-btn>
                <v-btn
                    color="primary"
                    variant="elevated"
                    style="color: white !important"
                    :disabled="compoundDraftCodes.length < 2"
                    @click="confirmCompoundPickerDialog">
                    Confirmă selecția
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <v-dialog
        v-model="structureCreateDialogOpen"
        max-width="420"
        :persistent="structureCreateSaving"
        @update:model-value="onStructureCreateDialogToggle">
        <v-card>
            <v-toolbar density="compact">
                <v-toolbar-title>{{
                    structureCreateDialogTitle
                }}</v-toolbar-title>
                <v-spacer />
                <v-btn
                    icon
                    variant="text"
                    :disabled="structureCreateSaving"
                    @click="closeStructureCreateDialog">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-toolbar>

            <v-card-text class="pt-4 d-flex flex-column ga-3">
                <v-select
                    v-if="structureCreateMode === 'chapter'"
                    v-model="structureCreateForm.systems"
                    :items="systemOptions"
                    item-title="title"
                    item-value="value"
                    label="Sisteme"
                    :variant="fieldVariant"
                    density="compact"
                    hide-details="auto"
                    multiple
                    chips
                    closable-chips
                    :disabled="structureCreateSaving" />
                <div
                    v-if="structureCreateMode === 'subchapter'"
                    class="text-body-2 text-medium-emphasis">
                    Capitol: {{ productBuilderForm.chapterCode }}
                    <span v-if="productBuilderForm.chapterName">
                        – {{ productBuilderForm.chapterName }}
                    </span>
                </div>
                <v-text-field
                    v-model="structureCreateForm.code"
                    label="Cod"
                    :variant="fieldVariant"
                    density="compact"
                    hide-details="auto"
                    :error-messages="structureCreateCodeErrors"
                    :disabled="structureCreateSaving"
                    autofocus />
                <v-text-field
                    v-model="structureCreateForm.name"
                    label="Denumire"
                    :variant="fieldVariant"
                    density="compact"
                    hide-details="auto"
                    :error-messages="structureCreateNameErrors"
                    :disabled="structureCreateSaving"
                    @keyup.enter="submitStructureCreateDialog" />
            </v-card-text>

            <v-card-actions class="px-4 pb-4">
                <v-spacer />
                <v-btn
                    variant="text"
                    :disabled="structureCreateSaving"
                    @click="closeStructureCreateDialog">
                    Anulează
                </v-btn>
                <v-btn
                    color="secondary"
                    variant="flat"
                    style="color: white !important"
                    :loading="structureCreateSaving"
                    :disabled="!canSubmitStructureCreate"
                    @click="submitStructureCreateDialog">
                    Salvează
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <v-dialog
        v-model="createManufacturerDialogOpen"
        max-width="420"
        :persistent="creatingManufacturer">
        <v-card>
            <v-toolbar density="compact">
                <v-toolbar-title>
                    {{
                        createManufacturerIsEdit
                            ? "Editează producător"
                            : "Producător nou"
                    }}
                </v-toolbar-title>
                <v-spacer />
                <v-btn
                    icon
                    variant="text"
                    :disabled="creatingManufacturer"
                    @click="closeCreateManufacturerDialog">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-toolbar>

            <v-card-text class="pt-4">
                <v-form
                    ref="createManufacturerForm"
                    @submit.prevent="submitCreateManufacturer">
                    <div class="d-flex flex-column ga-3">
                        <v-text-field
                            v-model="newManufacturerCode"
                            label="Cod"
                            :variant="fieldVariant"
                            density="compact"
                            hide-details="auto"
                            :error-messages="manufacturerCreateCodeErrors"
                            :disabled="creatingManufacturer"
                            autofocus />
                        <v-text-field
                            v-model="newManufacturerName"
                            label="Denumire"
                            :variant="fieldVariant"
                            density="compact"
                            hide-details="auto"
                            :error-messages="manufacturerCreateNameErrors"
                            :disabled="creatingManufacturer"
                            @keyup.enter="submitCreateManufacturer" />
                    </div>
                </v-form>
            </v-card-text>

            <v-card-actions class="px-4 pb-4">
                <v-spacer />
                <v-btn
                    variant="text"
                    :disabled="creatingManufacturer"
                    @click="closeCreateManufacturerDialog">
                    Anulează
                </v-btn>
                <v-btn
                    color="secondary"
                    variant="flat"
                    style="color: white !important"
                    :loading="creatingManufacturer"
                    :disabled="!canSubmitManufacturerCreate"
                    @click="submitCreateManufacturer">
                    Salvează
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <v-dialog
        v-model="createSupplierDialogOpen"
        max-width="420"
        :persistent="creatingSupplier">
        <v-card>
            <v-toolbar density="compact">
                <v-toolbar-title>
                    {{
                        createSupplierIsEdit
                            ? "Editează furnizor"
                            : "Furnizor nou"
                    }}
                </v-toolbar-title>
                <v-spacer />
                <v-btn
                    icon
                    variant="text"
                    :disabled="creatingSupplier"
                    @click="closeCreateSupplierDialog">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-toolbar>

            <v-card-text class="pt-4">
                <v-form
                    ref="createSupplierForm"
                    @submit.prevent="submitCreateSupplier">
                    <div class="d-flex flex-column ga-3">
                        <v-text-field
                            v-model="newSupplierCode"
                            label="Cod"
                            :variant="fieldVariant"
                            density="compact"
                            hide-details="auto"
                            :error-messages="supplierCreateCodeErrors"
                            :disabled="creatingSupplier"
                            autofocus />
                        <v-text-field
                            v-model="newSupplierName"
                            label="Denumire"
                            :variant="fieldVariant"
                            density="compact"
                            hide-details="auto"
                            :error-messages="supplierCreateNameErrors"
                            :disabled="creatingSupplier"
                            @keyup.enter="submitCreateSupplier" />
                    </div>
                </v-form>
            </v-card-text>

            <v-card-actions class="px-4 pb-4">
                <v-spacer />
                <v-btn
                    variant="text"
                    :disabled="creatingSupplier"
                    @click="closeCreateSupplierDialog">
                    Anulează
                </v-btn>
                <v-btn
                    color="secondary"
                    variant="flat"
                    style="color: white !important"
                    :loading="creatingSupplier"
                    :disabled="!canSubmitSupplierCreate"
                    @click="submitCreateSupplier">
                    Salvează
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { cloneDeep } from "lodash";
import {
    normalizeSystemValue,
    syncCategoriesFromSystems,
    buildCategoryAssignmentsFromSystems,
    getDefaultSystemSubcategory,
    getProductMainCategories,
} from "@/data/productCategories";
import {
    CURRENCY_OPTIONS,
    getManufacturerCodes,
    SYSTEM_OPTIONS,
} from "@/data/manufacturerCodes";
import {
    PRODUCT_STATUS_LABELS,
    PRODUCT_STATUS_OPTIONS,
    formatProductStatus,
    getProductStatusTextStyle,
    normalizeProductStatus,
} from "@/data/productStatus";
import { buildFurnizorSelectOptions, getFurnizori } from "@/data/furnizorNames";
import PercentageTextField from "@/components/common/PercentageTextField.vue";
import ProductCodeSuggestCombobox from "@/components/pricelist/ProductCodeSuggestCombobox.vue";
import TransportCostFormulaModal from "@/components/pricelist/TransportCostFormulaModal.vue";
import StampDutyFormulaModal from "@/components/pricelist/StampDutyFormulaModal.vue";
import PriceListPropagateFurnizorFieldsDialog from "@/components/pricelist/PriceListPropagateFurnizorFieldsDialog.vue";
import apiClient from "@/utils/apiClient";
import {
    collectPropagatePeers,
    detectDefaultPropagateFieldKeys,
    emptyPropagateFilterCriteria,
    extractPropagateValuesFromForm,
    filterPropagatePeersWithCriteria,
    getFurnizorIdentity,
    propagateCriteriaNeedsFullFetch,
} from "@/utils/priceListPropagateFurnizor";
import { itemMatchesBulkCriteria } from "@/utils/priceListBulkApply";
import {
    builderFormToSavePayload,
    legacyProductToBuilderForm,
    pricingDetailToBuilderForm,
} from "@/utils/pricingDetailMapper";
import {
    findChapterByCode,
    findChapterByName,
    findSubchapterByCode,
    findSubchapterByName,
    getChapterNameForSystems,
    getChapterOptionsForSystems,
    getSubchapterNameForSystems,
    getSubchapterOptionsForSystems,
    padSubchapterCode,
} from "@/data/productStructureLookups";
import {
    findManufacturerByCode,
    findReferencePriceForProduct,
    parseProductCodeStructure,
    suggestNextCatalogCode,
    suggestNextProgressiveNumber,
} from "@/utils/productStructureHelpers";
import {
    addFurnizorPricingTab,
    applyFurnizorPricingToRoot,
    builderFormToCalculatorInput,
    calculateProductPricing,
    createDefaultProductBuilderForm,
    DEFAULT_PRICING_SETTINGS,
    formatNumericInputValue,
    formatPercentValue,
    formatPricingResultValue,
    getActiveFurnizorPricing,
    getDefaultFurnizorPricing,
    hasVisibleCostUpliftFields,
    hasVisibleDiscountFields,
    mergeFormWithFurnizorPricing,
    mergeListPricingSettings,
    normalizePricingFormNumericDisplay,
    parseNumericInput,
    persistActiveFurnizorPricingFromRoot,
    ratioToPercentageInput,
    removeFurnizorPricingTab,
    setDefaultFurnizorPricing,
    switchActiveFurnizorPricing,
    systemsArrayFromForm,
    syncDiscountsListToLegacy,
    syncSystemsListToLegacy,
} from "@/utils/priceListProductCalculator";
import {
    applyPricingFieldEdit,
    applySellingPriceToOtherFurnizorPricings,
    convertPricingBaselineForCurrencyChange,
    convertSupplierListPriceAmount,
    PRICING_INPUT_FIELD_KEYS,
    PRICING_RESULT_FIELD_KEYS,
    shouldOfferPropagateSellingPriceToOtherFurnizors,
} from "@/utils/priceListPricingFieldSync";
import {
    cloneTransportCostBreakdown,
    createDefaultTransportCostBreakdown,
    normalizeTransportCostBreakdown,
    TRANSPORT_COST_MODE_FORMULA,
    TRANSPORT_COST_MODE_PERCENT,
} from "@/utils/transportCostBreakdown";
import {
    cloneStampDutyBreakdown,
    createDefaultStampDutyBreakdown,
    normalizeStampDutyBreakdown,
    STAMP_DUTY_MODE_FORMULA,
    STAMP_DUTY_MODE_MANUAL,
    syncStampDutyFromBreakdown,
} from "@/utils/stampDutyBreakdown";
import {
    findMatchingLiveFurnizorTab,
    resolveDraftDisplayKind,
} from "@/utils/priceListTablePricing";
import {
    buildAllPricingFieldBaselines,
    buildAllPricingFieldReferenceBaselines,
    buildPricingFieldBaselineSnapshot,
    buildPricingFieldReferenceSnapshot,
    baselineValueForField,
    calculatePricingValueChangeRatio,
    calculatePricingPercentFieldChangeRatio,
    formatPricingResultSavedHint,
    formatPricingFieldSavedHint,
    isPricingFieldChangedFromBaseline,
    pricingFieldChangeHintClass,
    pricingFieldValuesEqual,
    restorePricingFieldsFromBaseline,
} from "@/utils/priceListPricingFieldBaselines";
import {
    buildCompoundProductSeed,
    SISTEMA_MANUFACTURER_CODE,
    SISTEMA_MANUFACTURER_NAME,
} from "@/utils/priceListSimpleUpdate";
import {
    formatNumber,
    showAlertModal,
} from "@/utils/utils";
import {
    createProductChapter,
    createProductSubchapter,
    ensureProductLookupsLoaded,
    fetchSubcategoriesForSystem,
    createFurnizor,
    createManufacturer,
    updateFurnizor,
    updateManufacturer,
    updateProductChapter,
    updateProductSubchapter,
} from "@/utils/productLookupApi";
import { resolveProductCodeSuggestValue } from "@/utils/productCodeSuggest";
import { comparePayloadToEditItem } from "@/utils/priceListCompareRows";
import {
    buildCompareHighlightIndex,
    isCompareFieldHighlightedForTab,
    isCompareFurnizorTabHighlighted,
} from "@/utils/priceListCompareHighlights";

import {
    changedKeysAllowDirectListSave,
    isProductDetailsDirectSaveField,
} from "@/utils/priceListProductDetailsKeys";

const MEASUREMENT_UNIT_ALIASES = {
    m2: "m²",
    "m²": "m²",
};

function stableSerialize(value) {
    if (value == null) {
        return "";
    }
    if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
        return String(value).trim();
    }
    if (Array.isArray(value)) {
        return JSON.stringify(
            value.map((entry) => {
                if (entry && typeof entry === "object") {
                    if ("id" in entry) {
                        return entry.id;
                    }
                    if ("clientKey" in entry) {
                        return {
                            ...entry,
                            clientKey: entry.clientKey,
                        };
                    }
                }
                return typeof entry === "string" ? entry.trim() : entry;
            }),
        );
    }
    if (typeof value === "object") {
        const keys = Object.keys(value).sort();
        const normalized = {};
        keys.forEach((key) => {
            normalized[key] = value[key];
        });
        return JSON.stringify(normalized);
    }
    return String(value);
}

export default {
    name: "PriceListProductBuilderModal",
    components: {
        PercentageTextField,
        ProductCodeSuggestCombobox,
        TransportCostFormulaModal,
        StampDutyFormulaModal,
        PriceListPropagateFurnizorFieldsDialog,
    },
    props: {
        modelValue: {
            type: Boolean,
            default: false,
        },
        editItem: {
            type: Object,
            default: null,
        },
        priceLists: {
            type: Array,
            default: () => [],
        },
        selectedPriceList: {
            type: [Number, String, null],
            default: null,
        },
        pricingSettings: {
            type: Object,
            default: () => ({ ...DEFAULT_PRICING_SETTINGS }),
        },
        existingProductCodes: {
            type: Array,
            default: () => [],
        },
        existingProducts: {
            type: Array,
            default: () => [],
        },
        /**
         * Load one page of price-list products for a furnizor label (incl.
         * producător ca furnizor). Used by propagate-to-peers.
         * @type {{ type: Function, default: null }}
         *   ({ furnizorLabel, search, criteria, page, itemsPerPage }) =>
         *     Promise<{ items: Array, count: number, page: number }>
         */
        fetchFurnizorPeerProductsPage: {
            type: Function,
            default: null,
        },
        /**
         * Load all products for a furnizor (select-all / client-filtered propagate).
         * ({ furnizorLabel, search, criteria }) => Promise<Array<Object>>
         */
        fetchFurnizorPeerProductsAll: {
            type: Function,
            default: null,
        },
        productDrafts: {
            type: Array,
            default: () => [],
        },
        measurementUnits: {
            type: Array,
            default: () => [],
        },
        fieldVariant: {
            type: String,
            default: "outlined",
        },
        savingDraft: {
            type: Boolean,
            default: false,
        },
        savingDirect: {
            type: Boolean,
            default: false,
        },
        draftEntry: {
            type: Object,
            default: null,
        },
        blockedDraftProductCodes: {
            type: Array,
            default: () => [],
        },
        readOnly: {
            type: Boolean,
            default: false,
        },
        compareChanges: {
            type: Array,
            default: () => [],
        },
        compareFurnizorTabKey: {
            type: String,
            default: null,
        },
        /** Snapshot payload from lista de referință (left) — only for compare read-only hints. */
        compareReferencePayload: {
            type: Object,
            default: null,
        },
        /** Opens builder for supplier-compare simulation (no official list writes). */
        simulationMode: {
            type: Boolean,
            default: false,
        },
        /** Pre-filled builder form already containing simulation overrides. */
        simulationForm: {
            type: Object,
            default: null,
        },
        /** Official (non-simulated) form used only to freeze orange-highlight baselines. */
        simulationOfficialForm: {
            type: Object,
            default: null,
        },
        /** Furnizor tab to activate after init (clientKey / code / column key). */
        initialFurnizorTabKey: {
            type: String,
            default: null,
        },
        /** Locks product status to De verificat (price list verifier role). */
        statusLockedToDeVerificat: {
            type: Boolean,
            default: false,
        },
        /** Allows saving directly to the live price list (managers only). */
        allowDirectSave: {
            type: Boolean,
            default: true,
        },
    },
    emits: ["update:modelValue", "draft-save", "save-direct", "simulation-apply"],
    data() {
        return {
            productBuilderForm: createDefaultProductBuilderForm(),
            manufacturerOptions: getManufacturerCodes(),
            furnizorOptions: getFurnizori(),
            currencyOptions: CURRENCY_OPTIONS,
            productStatusOptions: PRODUCT_STATUS_OPTIONS,
            systemOptions: SYSTEM_OPTIONS,
            costUpliftTypeOptions: [
                { title: "Sumă fixă (RON/buc)", value: "fixed" },
                { title: "Procent (%)", value: "percent" },
            ],
            transportCostFormulaModalOpen: false,
            stampDutyFormulaModalOpen: false,
            /**
             * Per-furnizor builder % captured before the calculator was applied.
             * Avoids leaking the previous tab's snapshot onto a new furnizor.
             */
            transportCostRateBeforeFormulaByTab: {},
            stampDutyAmountBeforeFormulaByTab: {},
            resultFields: [
                {
                    key: "finalEntryPriceExVat",
                    label: "Preț intrare final fără TVA",
                    format: "money2",
                    readonly: true,
                },
                {
                    key: "entryPriceRonExVat",
                    label: "Preț intrare RON fără TVA",
                    format: "money2",
                    readonly: true,
                },
                {
                    key: "entryPriceRonIncVat",
                    label: "Preț intrare RON cu TVA",
                    format: "money2",
                    readonly: true,
                },
                {
                    key: "listPriceRonExVat",
                    label: "Preț listă RON fără TVA",
                    format: "money2",
                },
                {
                    key: "listPriceRonIncVatFinal",
                    label: "Preț listă RON cu TVA actual",
                    format: "money2",
                },
                {
                    key: "listPriceEuroExVat",
                    label: "Preț listă EUR fără TVA",
                    format: "money2",
                    readonly: true,
                },
            ],
            manufacturerSyncLock: null,
            structureAutoFillLock: false,
            markupActualManual: false,
            pricingSyncLock: false,
            pricingResultDrafts: {},
            pricingResultEditingKey: null,
            pricingInputDrafts: {},
            pricingInputEditingKey: null,
            pricingFieldBaselinesByTab: {},
            pricingFieldReferenceBaselinesByTab: {},
            // Valoare veche source (advances across draft save/reopen cycles).
            pricingFieldLastSavedBaselinesByTab: {},
            // Snapshot of form values when this editor session opened.
            pricingFieldSessionStartBaselinesByTab: {},
            pricingFieldFocusSnapshot: null,
            listPriceIncVatFocusSnapshot: null,
            manuallyEditedPricingFields: {},
            furnizorTabSwitchLock: false,
            productCodeAutofill: "",
            supplierListPriceSessionBaselineByTab: {},
            supplierListPricePersistedOldByTab: {},
            // clientKey → true until first draft save after "Adaugă furnizor"
            newFurnizorKeysPendingFirstSave: {},
            /** Keys of furnizor tabs not yet on the published list — lista în lucru shows „—”. */
            unpublishedNewFurnizorKeys: {},
            listPriceRonIncVatSessionBaseline: 0,
            listPriceRonIncVatPersistedReference: "",
            listPriceComparisonBaselineFrozen: false,
            currencyConvertDialogOpen: false,
            pendingCurrencyChange: null,
            subcategoryOptionsBySystem: {},
            systemPairMenusOpen: [false],
            compoundProductSearch: "",
            compoundPickerDialogOpen: false,
            createManufacturerDialogOpen: false,
            createManufacturerIsEdit: false,
            creatingManufacturer: false,
            editingManufacturerId: null,
            newManufacturerCode: "",
            newManufacturerName: "",
            createSupplierDialogOpen: false,
            createSupplierIsEdit: false,
            creatingSupplier: false,
            editingSupplierId: null,
            newSupplierCode: "",
            newSupplierName: "",
            compoundDraftCodes: [],
            compoundDraftKeepInList: [],
            compoundPickerSyncLock: false,
            compoundComponentsTouched: false,
            compoundPickerPage: 1,
            compoundPickerItemsPerPage: 10,
            compoundPickerHeaders: [
                { title: "Cod", key: "product_code", sortable: true },
                { title: "Nume", key: "product_name", sortable: true },
                { title: "Preț", key: "price", sortable: true, align: "end" },
                {
                    title: "În listă",
                    key: "keep_in_list",
                    sortable: false,
                    align: "center",
                    width: "96px",
                },
            ],
            structureCreateDialogOpen: false,
            structureCreateMode: "chapter",
            structureCreateIsEdit: false,
            structureCreateOriginalCode: "",
            structureCreateSaving: false,
            structureCreateForm: {
                systems: [],
                code: "",
                name: "",
            },
            // Bumped after lookup reload so chapter/subchapter computed options re-evaluate.
            structureOptionsVersion: 0,
            formLoading: false,
            localEditItem: null,
            formBaseline: null,
            propagateSellingPriceDialogOpen: false,
            pendingPropagateSellingPrice: null,
            propagateFurnizorFieldsDialogOpen: false,
            /** Current page of catalog items used to build propagateFurnizorPeers. */
            propagateFurnizorPeerSourceItems: [],
            /** product_code → item, accumulated across visited pages (for apply). */
            propagateFurnizorPeerItemsByCode: {},
            propagateFurnizorPeersTotalCount: 0,
            propagateFurnizorPeersPage: 1,
            propagateFurnizorPeersItemsPerPage: 25,
            propagateFurnizorPeersSearch: "",
            propagateFurnizorPeersFilters: emptyPropagateFilterCriteria(),
            /** Full peer list when structural filters force client-side pagination. */
            propagateFurnizorPeersClientAll: null,
            propagateFurnizorPeersLoading: false,
            pendingFurnizorPropagate: null,
            existingProductUpdateConfirmOpen: false,
            confirmedSaveAsExistingProductUpdate: false,
            ensuringFurnizorPromise: null,
            sessionSourceProductCode: "",
            sessionCopyBaselineCode: "",
            sessionCopyOriginCode: "",
            // Compare read-only only: baselines from lista de referință.
            // Never used by the normal edit / draft workflow.
            compareReferenceBaselinesByTab: {},
            compareReferenceTabs: [],
        };
    },
    computed: {
        lockedDeVerificatStatusOptions() {
            return this.productStatusOptions.filter(
                (option) => option.value === 'de_verificat',
            );
        },
        /** Labels only — combobox free-text UX; form still stores snake_case values. */
        productStatusComboboxItems() {
            return this.productStatusOptions.map((option) => option.title);
        },
        productStatusComboboxDisplay() {
            const status = this.productBuilderForm.status;
            if (status == null || status === "") {
                return null;
            }
            return PRODUCT_STATUS_LABELS[status] || status;
        },
        effectiveEditItem() {
            return this.localEditItem || this.editItem;
        },
        isPersistedEditMode() {
            return Boolean(
                this.effectiveEditItem?.pricing_detail ||
                    this.effectiveEditItem?.has_pricing_detail,
            );
        },
        isLegacyUpgradeMode() {
            return Boolean(this.effectiveEditItem && !this.isPersistedEditMode);
        },
        isExistingProductEdit() {
            return Boolean(this.effectiveEditItem);
        },
        isDraftEditMode() {
            return Boolean(this.draftEntry?.id);
        },
        isCompoundCreateMode() {
            return Boolean(this.productBuilderForm?.isCompositeProduct);
        },
        hasActiveDiscountFields() {
            return hasVisibleDiscountFields(
                this.productBuilderForm?.discounts,
            );
        },
        hasActiveCostUpliftFields() {
            return hasVisibleCostUpliftFields(
                this.productBuilderForm?.costUplifts,
            );
        },
        isEditMode() {
            return this.isPersistedEditMode;
        },
        isStructureChangedProductCopy() {
            const baseline = String(this.sessionCopyBaselineCode || "").trim();
            const current = String(this.computedProduct?.productCode || "").trim();
            if (!baseline || !current || baseline === current) {
                return false;
            }
            if (this.isDraftEditMode) {
                return !this.productBuilderForm.overrideProductCode;
            }
            return (
                this.isExistingProductEdit
                && !this.productBuilderForm.overrideProductCode
            );
        },
        showInitialProductCode() {
            const initial = String(this.sessionSourceProductCode || "").trim();
            const current = String(this.computedProduct?.productCode || "").trim();
            return Boolean(initial && current && initial !== current);
        },
        draftBuilderKind() {
            if (!this.isDraftEditMode || !this.draftEntry) {
                return null;
            }
            return resolveDraftDisplayKind(this.draftEntry);
        },
        showBuilderNewChip() {
            // Structure copies use "Copiat din" in the structure section.
            if (this.showInitialProductCode || this.isStructureChangedProductCopy) {
                return false;
            }
            return this.draftBuilderKind === "new";
        },
        showBuilderCopiedChip() {
            const source = String(this.sessionSourceProductCode || "").trim();
            if (!source) {
                return false;
            }
            return (
                this.showInitialProductCode
                || this.isStructureChangedProductCopy
            );
        },
        isInPlaceProductCodeUpdate() {
            return this.isInPlaceProductCodeUpdateForCode(
                this.computedProduct?.productCode,
            );
        },
        isDuplicateLiveProductCodeForCreate() {
            const code = String(this.computedProduct?.productCode || "").trim();
            if (!code || !this.existingProductCodes.includes(code)) {
                return false;
            }
            return !this.isInPlaceProductCodeUpdateForCode(code);
        },
        /**
         * Form keys that differ from the open baseline.
         * Auto-synced markupActual is ignored unless the user edited it manually.
         */
        changedFormKeysFromBaseline() {
            if (!this.formBaseline?.values) {
                return [];
            }
            const baselineValues = this.formBaseline.values;
            const form = this.productBuilderForm || {};
            const keys = new Set([
                ...Object.keys(baselineValues),
                ...Object.keys(form),
            ]);
            const changed = [];
            keys.forEach((key) => {
                // Calculator may rewrite adaos; only count intentional edits.
                if (key === "markupActual") {
                    return;
                }
                const current = stableSerialize(form[key]);
                const previous = Object.prototype.hasOwnProperty.call(
                    baselineValues,
                    key,
                )
                    ? baselineValues[key]
                    : stableSerialize(undefined);
                if (current !== previous) {
                    changed.push(key);
                }
            });
            if (
                Boolean(this.markupActualManual) !==
                Boolean(this.formBaseline.markupActualManual)
            ) {
                changed.push("markupActual");
            } else if (
                this.markupActualManual &&
                stableSerialize(form.markupActual) !==
                    baselineValues.markupActual
            ) {
                changed.push("markupActual");
            }
            return changed;
        },
        canSaveDirectToList() {
            return (
                !this.savingDraft &&
                !this.savingDirect &&
                !this.formLoading &&
                Boolean(this.formBaseline?.values) &&
                changedKeysAllowDirectListSave(this.changedFormKeysFromBaseline)
            );
        },
        saveDirectDisabledReason() {
            if (this.canSaveDirectToList) {
                return "";
            }
            const changed = this.changedFormKeysFromBaseline;
            if (!changed.length) {
                return "Modificați Detalii produs sau Sistem / Subsistem pentru a salva direct în listă.";
            }
            if (!changed.every((key) => isProductDetailsDirectSaveField(key))) {
                return "Salvarea directă în listă este disponibilă doar pentru Detalii produs și Sistem / Subsistem.";
            }
            return "";
        },
        filteredCompoundProductOptions() {
            const query = String(this.compoundProductSearch || "")
                .trim()
                .toLowerCase();
            if (!query) {
                return this.compoundProductOptions;
            }
            return this.compoundProductOptions.filter(option =>
                option.searchText.includes(query),
            );
        },
        compoundPickerTableItems() {
            const selected = this.compoundDraftSelectedSet;
            return [...this.filteredCompoundProductOptions].sort(
                (left, right) => {
                    const leftSelected = selected.has(left.value);
                    const rightSelected = selected.has(right.value);
                    if (leftSelected !== rightSelected) {
                        return leftSelected ? -1 : 1;
                    }
                    return String(left.product_code).localeCompare(
                        String(right.product_code),
                        "ro",
                    );
                },
            );
        },
        compoundDraftKeepInListSet() {
            return new Set(
                (this.compoundDraftKeepInList || [])
                    .map(code => String(code).trim())
                    .filter(Boolean),
            );
        },
        selectedCompoundComponents() {
            const selected = new Set(
                (this.productBuilderForm.componentProductCodes || [])
                    .map(code => String(code).trim())
                    .filter(Boolean),
            );
            return (this.existingProducts || []).filter(product =>
                selected.has(String(product.product_code || "").trim()),
            );
        },
        compoundProductOptions() {
            return (this.existingProducts || [])
                .filter(product => product?.product_code)
                .map(product => ({
                    value: String(product.product_code).trim(),
                    product_code: String(product.product_code).trim(),
                    product_name: product.product_name || product.name || "",
                    price: parseNumericInput(product.price) || 0,
                    title: `${product.product_code} — ${
                        product.product_name || product.name || ""
                    }`,
                    searchText: [
                        product.product_code,
                        product.product_name,
                        product.name,
                    ]
                        .filter(Boolean)
                        .join(" ")
                        .toLowerCase(),
                    product,
                }));
        },
        compoundDraftSelectedSet() {
            return new Set(
                (this.compoundDraftCodes || [])
                    .map(code => String(code).trim())
                    .filter(Boolean),
            );
        },
        compoundDraftSelectedProducts() {
            const selected = this.compoundDraftSelectedSet;
            return (this.existingProducts || []).filter(product =>
                selected.has(String(product.product_code || "").trim()),
            );
        },
        compoundPriceSum() {
            return this.selectedCompoundComponents.reduce(
                (sum, product) => sum + (parseNumericInput(product.price) || 0),
                0,
            );
        },
        compoundDraftPriceSum() {
            return this.compoundDraftSelectedProducts.reduce(
                (sum, product) => sum + (parseNumericInput(product.price) || 0),
                0,
            );
        },
        compoundComponentsError() {
            if (!this.productBuilderForm.isCompositeProduct) {
                return "";
            }
            if (
                this.compoundPickerDialogOpen ||
                !this.compoundComponentsTouched
            ) {
                return "";
            }
            if (
                (this.productBuilderForm.componentProductCodes || []).length >=
                2
            ) {
                return "";
            }
            return "Selectați cel puțin 2 produse componente.";
        },
        dialogTitle() {
            if (this.simulationMode) {
                return "Simulare comparație furnizori";
            }
            if (this.readOnly) {
                return "Vizualizare produs (comparare)";
            }
            if (this.isCompoundCreateMode) {
                return "Creează produs compus";
            }
            if (this.isDraftEditMode) {
                return "Editează produs din lista în lucru";
            }
            if (this.isLegacyUpgradeMode) {
                return "Calculează preț produs existent";
            }
            if (this.isPersistedEditMode) {
                return "Editează produs";
            }
            return "Creează produs";
        },
        compareHighlightIndex() {
            if (!this.readOnly) {
                return null;
            }
            return buildCompareHighlightIndex(this.compareChanges);
        },
        usesCompareReferenceHints() {
            return (
                this.readOnly
                && Array.isArray(this.compareReferenceTabs)
                && this.compareReferenceTabs.length > 0
            );
        },
        chapterOptions() {
            void this.structureOptionsVersion;
            return this.withSelectedStructureOption(
                getChapterOptionsForSystems(this.selectedSystems),
                this.productBuilderForm.chapterCode,
                this.productBuilderForm.chapterName,
            );
        },
        subchapterOptions() {
            void this.structureOptionsVersion;
            return this.withSelectedStructureOption(
                getSubchapterOptionsForSystems(
                    this.selectedSystems,
                    this.productBuilderForm.chapterCode,
                ),
                this.productBuilderForm.subchapterCode,
                this.productBuilderForm.subchapterName,
            );
        },
        selectedSystems() {
            return systemsArrayFromForm(this.productBuilderForm);
        },
        primarySystem() {
            return this.selectedSystems[0] || "";
        },
        structureCreateDialogTitle() {
            if (this.structureCreateMode === "subchapter") {
                return this.structureCreateIsEdit
                    ? "Editează subcapitol"
                    : "Adaugă subcapitol";
            }
            return this.structureCreateIsEdit
                ? "Editează capitol"
                : "Adaugă capitol";
        },
        structureCreateCodeErrors() {
            void this.structureOptionsVersion;
            const code =
                this.structureCreateMode === "subchapter"
                    ? padSubchapterCode(this.structureCreateForm.code)
                    : String(this.structureCreateForm.code || "").trim();
            if (!code) {
                return [];
            }
            const excludeCode = this.structureCreateIsEdit
                ? String(this.structureCreateOriginalCode || "").trim()
                : "";
            if (this.structureCreateMode === "subchapter") {
                const chapterCode = String(
                    this.productBuilderForm.chapterCode || "",
                ).trim();
                if (!chapterCode) {
                    return [];
                }
                const normalizedExclude = padSubchapterCode(excludeCode);
                if (normalizedExclude && code === normalizedExclude) {
                    return [];
                }
                const existing = findSubchapterByCode(chapterCode, code);
                if (existing) {
                    return [
                        `Codul ${code} există deja (subcapitol „${existing.name}”).`,
                    ];
                }
                return [];
            }
            if (excludeCode && code === excludeCode) {
                return [];
            }
            const existing = findChapterByCode(code);
            if (existing) {
                return [
                    `Codul ${code} există deja (capitol „${existing.name}”).`,
                ];
            }
            return [];
        },
        structureCreateNameErrors() {
            void this.structureOptionsVersion;
            const name = String(this.structureCreateForm.name || "").trim();
            if (!name) {
                return [];
            }
            const excludeCode = this.structureCreateIsEdit
                ? String(this.structureCreateOriginalCode || "").trim()
                : "";
            if (this.structureCreateMode === "subchapter") {
                const chapterCode = String(
                    this.productBuilderForm.chapterCode || "",
                ).trim();
                if (!chapterCode) {
                    return [];
                }
                const existing = findSubchapterByName(chapterCode, name, {
                    excludeCode,
                });
                if (existing) {
                    return [
                        `Denumirea „${name}" există deja (cod ${existing.code}).`,
                    ];
                }
                return [];
            }
            const existing = findChapterByName(name, { excludeCode });
            if (existing) {
                return [
                    `Denumirea „${name}" există deja (cod ${existing.code}).`,
                ];
            }
            return [];
        },
        canSubmitStructureCreate() {
            const hasCode = Boolean(
                String(this.structureCreateForm.code || "").trim(),
            );
            const hasName = Boolean(
                String(this.structureCreateForm.name || "").trim(),
            );
            const hasSystems =
                this.structureCreateMode !== "chapter" ||
                (Array.isArray(this.structureCreateForm.systems) &&
                    this.structureCreateForm.systems.some(value =>
                        String(value || "").trim(),
                    ));
            const hasNoDuplicates =
                !this.structureCreateCodeErrors.length &&
                !this.structureCreateNameErrors.length;
            return (
                hasCode &&
                hasName &&
                hasSystems &&
                hasNoDuplicates &&
                !this.structureCreateSaving
            );
        },
        manufacturerCreateCodeErrors() {
            const code = String(this.newManufacturerCode || "").trim();
            if (!code) {
                return [];
            }
            const existing = this.manufacturerOptions.find(
                row =>
                    String(row.code || "")
                        .trim()
                        .toLowerCase() === code.toLowerCase() &&
                    (!this.createManufacturerIsEdit ||
                        row.id !== this.editingManufacturerId),
            );
            if (existing) {
                return [
                    `Codul ${code} există deja (producător „${existing.name}”).`,
                ];
            }
            return [];
        },
        manufacturerCreateNameErrors() {
            const name = String(this.newManufacturerName || "").trim();
            if (!name) {
                return [];
            }
            const existing = this.manufacturerOptions.find(
                row =>
                    String(row.name || "")
                        .trim()
                        .toLowerCase() === name.toLowerCase() &&
                    (!this.createManufacturerIsEdit ||
                        row.id !== this.editingManufacturerId),
            );
            if (existing) {
                return [
                    `Denumirea „${name}" există deja (cod ${existing.code}).`,
                ];
            }
            return [];
        },
        canSubmitManufacturerCreate() {
            const hasCode = Boolean(
                String(this.newManufacturerCode || "").trim(),
            );
            const hasName = Boolean(
                String(this.newManufacturerName || "").trim(),
            );
            const hasNoDuplicates =
                !this.manufacturerCreateCodeErrors.length &&
                !this.manufacturerCreateNameErrors.length;
            return (
                hasCode &&
                hasName &&
                hasNoDuplicates &&
                !this.creatingManufacturer
            );
        },
        supplierCreateCodeErrors() {
            const code = String(this.newSupplierCode || "").trim();
            if (!code) {
                return [];
            }
            const existing = this.allFurnizorCatalogCodes.find(
                row =>
                    String(row.code || "")
                        .trim()
                        .toLowerCase() === code.toLowerCase() &&
                    (!this.createSupplierIsEdit ||
                        row.id !== this.editingSupplierId),
            );
            if (existing) {
                return [
                    `Codul ${code} există deja (furnizor „${existing.name}”).`,
                ];
            }
            return [];
        },
        supplierCreateNameErrors() {
            const name = String(this.newSupplierName || "").trim();
            if (!name) {
                return [];
            }
            const existing = this.allFurnizorCatalogCodes.find(
                row =>
                    String(row.name || "")
                        .trim()
                        .toLowerCase() === name.toLowerCase() &&
                    (!this.createSupplierIsEdit ||
                        row.id !== this.editingSupplierId),
            );
            if (existing) {
                return [
                    `Denumirea „${name}" există deja (cod ${existing.code}).`,
                ];
            }
            return [];
        },
        canSubmitSupplierCreate() {
            const hasCode = Boolean(String(this.newSupplierCode || "").trim());
            const hasName = Boolean(String(this.newSupplierName || "").trim());
            const hasNoDuplicates =
                !this.supplierCreateCodeErrors.length &&
                !this.supplierCreateNameErrors.length;
            return (
                hasCode && hasName && hasNoDuplicates && !this.creatingSupplier
            );
        },
        manufacturerCodeOptions() {
            return this.manufacturerOptions.map(manufacturer => ({
                ...manufacturer,
                title: `${manufacturer.code} – ${manufacturer.name}`,
            }));
        },
        furnizorSelectOptions() {
            return buildFurnizorSelectOptions(
                this.furnizorOptions,
                this.manufacturerOptions,
            );
        },
        allFurnizorCatalogCodes() {
            return buildFurnizorSelectOptions(
                this.furnizorOptions,
                this.manufacturerOptions,
            );
        },
        selectedManufacturerId() {
            const code = String(
                this.productBuilderForm.manufacturerCode || "",
            ).trim();
            if (!code) {
                return null;
            }
            const selected = findManufacturerByCode(
                this.manufacturerOptions,
                code,
            );
            return selected?.id ?? null;
        },
        activeFurnizorPricing() {
            return getActiveFurnizorPricing(this.productBuilderForm);
        },
        activePropagateFurnizorIdentity() {
            return getFurnizorIdentity(this.activeFurnizorPricing, {
                manufacturerCode: this.productBuilderForm.manufacturerCode,
                manufacturerName: this.productBuilderForm.manufacturerName,
            });
        },
        activePropagateFurnizorLabel() {
            const fromIdentity = this.activePropagateFurnizorIdentity?.label || "";
            if (fromIdentity) {
                return fromIdentity;
            }
            return this.furnizorTabLabel(this.activeFurnizorPricing) || "";
        },
        propagateFurnizorPeers() {
            const identity = this.activePropagateFurnizorIdentity;
            const exclude = String(this.computedProduct?.productCode || "").trim();
            if (!identity || !exclude) {
                return [];
            }
            const sourceItems = Array.isArray(this.propagateFurnizorPeerSourceItems)
                ? this.propagateFurnizorPeerSourceItems
                : [];
            return collectPropagatePeers({
                items: sourceItems,
                drafts: this.productDrafts,
                identity,
                excludeProductCode: exclude,
            });
        },
        propagateFurnizorDefaultFieldKeys() {
            return detectDefaultPropagateFieldKeys({
                form: this.productBuilderForm,
                baseline: this.activePricingFieldSessionStartBaseline,
                // Open-session snapshot keeps original currency after FX convert.
                openBaseline: this.activePricingFieldBaseline,
                nomenclatureBaseline: this.sessionNomenclatureBaseline,
            });
        },
        sessionNomenclatureBaseline() {
            if (this.draftEntry?.builderState?.productBuilderForm) {
                return String(
                    this.draftEntry.builderState.productBuilderForm.nomenclatureCode
                        || "",
                ).trim();
            }
            const detail = this.effectiveEditItem?.pricing_detail || {};
            return String(
                detail.nomenclatureCode
                    || this.effectiveEditItem?.nomenclature_code
                    || "",
            ).trim();
        },
        activeUsesManufacturerAsFurnizor: {
            get() {
                return Boolean(
                    this.activeFurnizorPricing?.usesManufacturerAsFurnizor,
                );
            },
            set(value) {
                const active = this.activeFurnizorPricing;
                if (!active) {
                    return;
                }
                active.usesManufacturerAsFurnizor = Boolean(value);
                if (value) {
                    active.furnizorId = null;
                    active.furnizorCode = "";
                    active.furnizorName = "";
                }
            },
        },
        activeFurnizorId: {
            get() {
                return this.activeFurnizorPricing?.furnizorId ?? null;
            },
            set(value) {
                const active = this.activeFurnizorPricing;
                if (!active) {
                    return;
                }
                active.furnizorId = value;
                const selected = this.furnizorSelectOptions.find(
                    row =>
                        row.id != null &&
                        value != null &&
                        String(row.id) === String(value),
                );
                if (selected) {
                    active.furnizorCode = selected.code;
                    active.furnizorName = selected.name;
                    active.usesManufacturerAsFurnizor = false;
                } else if (!value) {
                    active.furnizorCode = "";
                    active.furnizorName = "";
                }
            },
        },
        activeFurnizorSelectCode: {
            get() {
                const active = this.activeFurnizorPricing;
                if (!active) {
                    return null;
                }
                const code = String(active.furnizorCode || "").trim();
                if (code) {
                    return code;
                }
                if (active.furnizorId != null) {
                    const match = this.furnizorSelectOptions.find(
                        row =>
                            row.id != null &&
                            String(row.id) === String(active.furnizorId),
                    );
                    return match?.code || null;
                }
                return null;
            },
            set(code) {
                const active = this.activeFurnizorPricing;
                if (!active) {
                    return;
                }
                const normalized = String(code || "").trim();
                if (!normalized) {
                    active.furnizorId = null;
                    active.furnizorCode = "";
                    active.furnizorName = "";
                    return;
                }
                const selected = this.furnizorSelectOptions.find(
                    row =>
                        String(row.code || "")
                            .trim()
                            .toLowerCase() === normalized.toLowerCase(),
                );
                if (!selected) {
                    // Keep the typed code so apply can get_or_create the catalog row.
                    active.furnizorId = null;
                    active.furnizorCode = normalized;
                    if (!String(active.furnizorName || "").trim()) {
                        active.furnizorName = normalized;
                    }
                    active.usesManufacturerAsFurnizor = false;
                    return;
                }
                active.furnizorId = selected.id ?? null;
                active.furnizorCode = selected.code;
                active.furnizorName = selected.name;
                active.usesManufacturerAsFurnizor = false;
            },
        },
        computedProduct() {
            return calculateProductPricing(
                builderFormToCalculatorInput(this.productBuilderForm),
                this.effectivePricingSettings,
            );
        },
        defaultComputedProduct() {
            const active = getActiveFurnizorPricing(this.productBuilderForm);
            const defaultPricing = getDefaultFurnizorPricing(
                this.productBuilderForm,
            );
            if (
                active &&
                defaultPricing &&
                active.clientKey === defaultPricing.clientKey
            ) {
                return this.computedProduct;
            }
            const merged = mergeFormWithFurnizorPricing(
                this.productBuilderForm,
                defaultPricing,
            );
            return calculateProductPricing(
                builderFormToCalculatorInput(merged),
                this.effectivePricingSettings,
            );
        },
        effectivePricingSettings() {
            return mergeListPricingSettings(this.pricingSettings);
        },
        currencyConvertFromLabel() {
            return this.currencyOptionLabel(
                this.pendingCurrencyChange?.from,
            );
        },
        currencyConvertToLabel() {
            return this.currencyOptionLabel(this.pendingCurrencyChange?.to);
        },
        editablePricingFields() {
            return this.resultFields.filter(field => !field.readonly);
        },
        displayResultFields() {
            return (this.resultFields || []).map((field) => {
                if (field.key !== "finalEntryPriceExVat") {
                    return field;
                }
                const currencyLabel = this.currencyOptionLabel(
                    this.productBuilderForm.currency,
                );
                return {
                    ...field,
                    label: `Preț intrare final fără TVA ${currencyLabel}`,
                    format: "money2",
                };
            });
        },
        supplierListPriceChangeRatio() {
            const oldPrice = String(
                this.productBuilderForm.supplierListPriceOld ?? "",
            ).trim();
            if (!oldPrice) {
                return null;
            }
            const ratio = Number(this.computedProduct.priceChangeRatio);
            return Number.isFinite(ratio) ? ratio : null;
        },
        supplierListPriceChangeHint() {
            if (this.supplierListPriceChangeRatio === null) {
                return "";
            }
            return formatPercentValue(this.supplierListPriceChangeRatio, 1);
        },
        supplierListPriceChangeHintClass() {
            const ratio = this.supplierListPriceChangeRatio;
            if (ratio === null || ratio === 0) {
                return "text-medium-emphasis";
            }
            return ratio > 0 ? "text-error" : "text-success";
        },
        panelSteps() {
            const steps = [
                { value: "structure", title: "Structură" },
                { value: "product", title: "Produs" },
                { value: "supplierPricing", title: "Detalii furnizor" },
                { value: "transportMarkup", title: "Calculare preț" },
                { value: "results", title: "Rezultate" },
            ];
            return steps;
        },
        activeFurnizorPricingKey() {
            return this.productBuilderForm.activeFurnizorPricingKey || "";
        },
        manualEditsForActiveFurnizor() {
            const tabKey = this.activeFurnizorPricingKey;
            if (!tabKey) {
                return {};
            }
            return this.manuallyEditedPricingFields[tabKey] || {};
        },
        activePricingFieldBaseline() {
            return (
                this.pricingFieldBaselinesByTab[this.activeFurnizorPricingKey] ||
                null
            );
        },
        activePricingFieldReferenceBaseline() {
            return (
                this.pricingFieldReferenceBaselinesByTab[
                    this.activeFurnizorPricingKey
                ] || null
            );
        },
        activePricingFieldLastSavedBaseline() {
            return (
                this.pricingFieldLastSavedBaselinesByTab[
                    this.activeFurnizorPricingKey
                ] ||
                this.activePricingFieldBaseline ||
                null
            );
        },
        activePricingFieldSessionStartBaseline() {
            return (
                this.pricingFieldSessionStartBaselinesByTab[
                    this.activeFurnizorPricingKey
                ] ||
                this.activePricingFieldLastSavedBaseline ||
                null
            );
        },
        hasPricingChangesFromBaseline() {
            const baseline = this.activePricingFieldBaseline;
            if (!baseline) {
                return false;
            }
            const formatResult = (key, format = "") =>
                this.formatResultValue(key, format);
            const inputKeys = [
                "supplierListPriceNew",
                "currency",
                "transportCostRate",
                "transportCostBreakdown",
                "stampDutyAmount",
                "stampDutyBreakdown",
                "markupReference",
                "markupActual",
                "lockMarkupToReference",
                "overrideListPriceRonIncVatFinal",
                "overrideListPriceRonIncVatFinalValue",
                "useManualFinalPrice",
                "manualListPriceRonIncVatFinal",
                "priceAdjustmentRatio",
                "listPriceRonIncVatReference",
            ];
            if (
                inputKeys.some(fieldKey =>
                    isPricingFieldChangedFromBaseline(
                        fieldKey,
                        baseline,
                        this.productBuilderForm,
                        formatResult,
                        this.resultFields,
                        this.effectivePricingSettings,
                    ),
                )
            ) {
                return true;
            }
            const discounts = this.productBuilderForm.discounts || [];
            for (let index = 0; index < discounts.length; index += 1) {
                if (
                    isPricingFieldChangedFromBaseline(
                        `discounts.${index}`,
                        baseline,
                        this.productBuilderForm,
                        formatResult,
                        this.resultFields,
                        this.effectivePricingSettings,
                    )
                ) {
                    return true;
                }
            }
            return PRICING_RESULT_FIELD_KEYS.some(fieldKey =>
                isPricingFieldChangedFromBaseline(
                    fieldKey,
                    baseline,
                    this.productBuilderForm,
                    formatResult,
                    this.resultFields,
                    this.effectivePricingSettings,
                ),
            );
        },
        transportCostBreakdown() {
            return normalizeTransportCostBreakdown(
                this.productBuilderForm?.transportCostBreakdown,
            );
        },
        isTransportCostFormulaMode() {
            return (
                this.transportCostBreakdown.mode === TRANSPORT_COST_MODE_FORMULA
                && !this.transportCostBreakdown.rateIsManual
            );
        },
        transportCostFormulaButtonTitle() {
            const currency = this.transportCostWorkingCurrencyLabel;
            const currencySuffix = currency ? ` · costuri în ${currency}` : '';
            if (this.isTransportCostFormulaMode) {
                return `Procent calculat din formulă — click pentru editare${currencySuffix}`;
            }
            if (this.transportCostBreakdown.mode === TRANSPORT_COST_MODE_FORMULA) {
                return `Procent manual — formula salvată, click pentru editare${currencySuffix}`;
            }
            return `Formula de calcul${currencySuffix}`;
        },
        transportCostWorkingCurrencyLabel() {
            const currency = String(
                this.productBuilderForm?.currency || '',
            ).trim().toLowerCase();
            if (!currency) {
                return '';
            }
            if (currency === 'euro' || currency === 'eur') {
                return 'EUR';
            }
            if (currency === 'usd') {
                return 'USD';
            }
            if (currency === 'pln') {
                return 'PLN';
            }
            if (currency === 'ron') {
                return 'RON';
            }
            return currency.toUpperCase();
        },
        transportCostRateLabel() {
            const currency = this.transportCostWorkingCurrencyLabel;
            if (currency) {
                return `Cost transport + alte taxe (%) · ${currency}`;
            }
            return 'Cost transport + alte taxe (%)';
        },
        stampDutyBreakdown() {
            return normalizeStampDutyBreakdown(
                this.productBuilderForm?.stampDutyBreakdown,
            );
        },
        isStampDutyFormulaMode() {
            return (
                this.stampDutyBreakdown.mode === STAMP_DUTY_MODE_FORMULA
                && !this.stampDutyBreakdown.amountIsManual
            );
        },
        stampDutyFormulaButtonTitle() {
            if (this.isStampDutyFormulaMode) {
                return 'Valoare calculată din formulă — click pentru editare';
            }
            if (this.stampDutyBreakdown.mode === STAMP_DUTY_MODE_FORMULA) {
                return 'Valoare manuală — formula salvată, click pentru editare';
            }
            return 'Formula de calcul (pe greutate sau pe bucată)';
        },
    },
    watch: {
        'productBuilderForm.weight'() {
            if (
                this.formLoading
                || this.pricingSyncLock
                || !this.isStampDutyFormulaMode
            ) {
                return;
            }
            const synced = syncStampDutyFromBreakdown(
                this.productBuilderForm,
                this.effectivePricingSettings,
            );
            if (synced) {
                persistActiveFurnizorPricingFromRoot(this.productBuilderForm);
            }
        },
        modelValue(isOpen) {
            if (isOpen) {
                this.formBaseline = null;
                void this.initializeForm().then(() => {
                    this.$nextTick(() => {
                        this.freezeListPriceComparisonBaseline();
                        this.resetBuilderFormValidation();
                        this.$nextTick(() => {
                            // Live product open: re-capture after markup/freeze settle
                            // so a freshly saved furnizor (incl. newly added) is not
                            // falsely orange with a reset button.
                            // Simulation keeps official baselines so edited fields stay orange.
                            if (!this.isDraftEditMode && !this.simulationMode) {
                                this.captureCleanOpenPricingBaselines();
                                // Brand-new create: re-apply Valoare veche = 0 after
                                // clean capture (otherwise empty baselines wipe zeros).
                                if (!this.isExistingProductEdit) {
                                    this.applyZeroValoareVecheBaselinesForNewProduct();
                                }
                            } else if (this.simulationMode && this._simulationOfficialBaselines) {
                                this.pricingFieldBaselinesByTab = cloneDeep(
                                    this._simulationOfficialBaselines,
                                );
                                this.pricingFieldLastSavedBaselinesByTab = cloneDeep(
                                    this._simulationOfficialLastSaved || {},
                                );
                                this.pricingFieldSessionStartBaselinesByTab = cloneDeep(
                                    this._simulationOfficialSessionStart || {},
                                );
                            }
                            // Capture after open-settling mutations so direct-save
                            // is not blocked by post-load form drift.
                            this.captureFormBaseline();
                        });
                    });
                });
            } else {
                this.formLoading = false;
                this.localEditItem = null;
                this.formBaseline = null;
                this.clearPricingResultDrafts();
                this.clearPricingInputDrafts();
                this.clearAllManualPricingFields();
                this.pricingFieldBaselinesByTab = {};
                this.pricingFieldReferenceBaselinesByTab = {};
                this.pricingFieldLastSavedBaselinesByTab = {};
                this.pricingFieldSessionStartBaselinesByTab = {};
                this._simulationOfficialBaselines = null;
                this._simulationOfficialLastSaved = null;
                this._simulationOfficialSessionStart = null;
                this.pricingFieldFocusSnapshot = null;
                this.listPriceIncVatFocusSnapshot = null;
                this.sessionSourceProductCode = "";
                this.sessionCopyBaselineCode = "";
                this.sessionCopyOriginCode = "";
                this.existingProductUpdateConfirmOpen = false;
                this.confirmedSaveAsExistingProductUpdate = false;
                this.$refs.productCodeSuggest?.reset?.();
                this.productCodeAutofill = "";
                this.$nextTick(() => {
                    this.resetBuilderFormValidation();
                });
            }
        },
        compoundProductSearch() {
            this.compoundPickerPage = 1;
        },
        compoundDraftCodes(newCodes, oldCodes) {
            if (this.compoundPickerSyncLock) {
                return;
            }
            const normalizedNew = (Array.isArray(newCodes) ? newCodes : [])
                .map(code => String(code).trim())
                .filter(Boolean);
            const normalizedOld = (Array.isArray(oldCodes) ? oldCodes : [])
                .map(code => String(code).trim())
                .filter(Boolean);
            const keepSet = new Set(this.compoundDraftKeepInListSet);

            normalizedNew.forEach(code => {
                if (!normalizedOld.includes(code)) {
                    keepSet.add(code);
                }
            });
            normalizedOld.forEach(code => {
                if (!normalizedNew.includes(code)) {
                    keepSet.delete(code);
                }
            });

            this.compoundDraftKeepInList = [...keepSet];
            this.compoundPickerPage = 1;
        },
        "productBuilderForm.chapterCode"() {
            this.maybeSuggestProgressiveNumber();
            this.clearLegacyProductCodeOverride();
            this.syncCopyParentOnStructureChange();
        },
        "productBuilderForm.subchapterCode"() {
            this.maybeSuggestProgressiveNumber();
            this.clearLegacyProductCodeOverride();
            this.syncCopyParentOnStructureChange();
        },
        "productBuilderForm.progressiveNumber"() {
            this.clearLegacyProductCodeOverride();
            this.syncCopyParentOnStructureChange();
        },
        "productBuilderForm.manufacturerCode"(newCode, oldCode) {
            this.clearLegacyProductCodeOverride();
            const previous = String(oldCode ?? "").trim();
            const next = String(newCode ?? "").trim();
            if (previous && previous !== next) {
                this.productBuilderForm.manufacturerProductCode = "";
                (this.productBuilderForm.furnizorPricings || []).forEach(
                    (pricing) => {
                        pricing.manufacturerProductCode = "";
                    },
                );
            }
            this.syncCopyParentOnStructureChange();
        },
        computedProduct: {
            handler(product) {
                this.syncComputedMarkupActual(product);
            },
            deep: true,
        },
    },
    methods: {
        resetBuilderFormValidation() {
            this.$refs.builderForm?.resetValidation?.();
        },
        captureFormBaseline() {
            if (!this.modelValue) {
                this.formBaseline = null;
                return;
            }
            const form = this.productBuilderForm || {};
            const values = {};
            Object.keys(form).forEach((key) => {
                values[key] = stableSerialize(form[key]);
            });
            this.formBaseline = {
                values,
                markupActualManual: Boolean(this.markupActualManual),
            };
        },
        requiredRule(value) {
            return !!String(value ?? "").trim() || "Câmp obligatoriu.";
        },
        positiveNumberRule(value) {
            return (
                parseNumericInput(value) > 0 ||
                "Introduceți o valoare pozitivă."
            );
        },
        onPaymentTermDaysInput(value) {
            const digits = String(value ?? "").replace(/\D/g, "");
            this.productBuilderForm.paymentTermDays = digits;
        },
        resolveSavedListPriceRonIncVat() {
            const editItem = this.effectiveEditItem;
            if (editItem?.price != null && editItem.price !== "") {
                return String(editItem.price);
            }
            const draftPrice = this.draftEntry?.payload?.price;
            if (draftPrice != null && draftPrice !== "") {
                return String(draftPrice);
            }
            const detailFinal =
                editItem?.pricing_detail?.listPriceRonIncVatFinal ??
                getDefaultFurnizorPricing(this.productBuilderForm)
                    ?.listPriceRonIncVatFinal;
            if (detailFinal != null && detailFinal !== "") {
                return String(detailFinal);
            }
            const reference = String(
                this.productBuilderForm.listPriceRonIncVatReference ?? "",
            ).trim();
            if (reference) {
                return reference;
            }
            const computedFinal =
                this.defaultComputedProduct?.listPriceRonIncVatFinal;
            return computedFinal != null && computedFinal !== ""
                ? String(computedFinal)
                : "";
        },
        preparePriceComparisonBaselines() {
            const form = this.productBuilderForm;
            const pricings = form.furnizorPricings || [];
            // Capture per-tab baselines from each furnizor pricing row.
            pricings.forEach((pricing) => {
                const tabOld = String(pricing.supplierListPriceOld || "").trim();
                const tabNew = String(
                    pricing.supplierListPriceNew || pricing.supplierListPriceOld || "",
                ).trim();
                this.supplierListPricePersistedOldByTab[pricing.clientKey] = tabOld;
                this.supplierListPriceSessionBaselineByTab[pricing.clientKey] = tabNew;
                // Normalize: during edit, "old" becomes the baseline for live % comparison.
                pricing.supplierListPriceNew = tabNew;
                pricing.supplierListPriceOld = tabNew;
            });
            // Sync root buffer from active tab.
            const activeKey = form.activeFurnizorPricingKey;
            const persistedNew = this.supplierListPriceSessionBaselineByTab[activeKey] || "";
            form.supplierListPriceOld = persistedNew;
            form.supplierListPriceNew = persistedNew;
            this.listPriceRonIncVatPersistedReference = String(
                form.listPriceRonIncVatReference ||
                    this.resolveSavedListPriceRonIncVat() ||
                    "",
            ).trim();
            persistActiveFurnizorPricingFromRoot(form);
            // Capture "valoare veche" from persisted old prices before live-edit mutation.
            if (!Object.keys(this.pricingFieldReferenceBaselinesByTab).length) {
                this.pricingFieldReferenceBaselinesByTab =
                    buildAllPricingFieldReferenceBaselines(
                        form,
                        this.effectivePricingSettings,
                        this.resultFields,
                    );
            }

            this.freezeListPriceComparisonBaseline();
            persistActiveFurnizorPricingFromRoot(form);
        },
        freezeListPriceComparisonBaseline() {
            if (this.listPriceComparisonBaselineFrozen) {
                return;
            }
            const fromSaved = parseNumericInput(
                this.resolveSavedListPriceRonIncVat(),
            );
            const fromDisplay = parseNumericInput(
                this.computedProduct?.listPriceRonIncVatFinal,
            );
            const baseline = fromSaved > 0 ? fromSaved : fromDisplay;
            if (!(baseline > 0)) {
                return;
            }
            this.listPriceRonIncVatSessionBaseline = baseline;
            this.listPriceComparisonBaselineFrozen = true;
            this.productBuilderForm.listPriceRonIncVatReference =
                formatNumericInputValue(baseline, 2);
            persistActiveFurnizorPricingFromRoot(this.productBuilderForm);
        },
        restorePriceComparisonSessionFromSavedForm() {
            const form = this.productBuilderForm;
            const pricings = form.furnizorPricings || [];
            // Restore per-tab baselines from each furnizor pricing row.
            pricings.forEach((pricing) => {
                const tabOld = String(pricing.supplierListPriceOld || "").trim();
                const tabNew = String(
                    pricing.supplierListPriceNew || pricing.supplierListPriceOld || "",
                ).trim();
                this.supplierListPricePersistedOldByTab[pricing.clientKey] = tabOld;
                this.supplierListPriceSessionBaselineByTab[pricing.clientKey] = tabNew;
            });
            this.listPriceRonIncVatPersistedReference = String(
                form.listPriceRonIncVatReference || "",
            ).trim();
            const listBaseline =
                parseNumericInput(form.listPriceRonIncVatReference) ||
                parseNumericInput(this.draftEntry?.payload?.price);
            if (listBaseline > 0) {
                this.listPriceRonIncVatSessionBaseline = listBaseline;
            }
            this.listPriceComparisonBaselineFrozen = true;
            persistActiveFurnizorPricingFromRoot(form);
        },
        applyPriceBaselinesForSave(form = this.productBuilderForm) {
            // Apply supplier price baselines per furnizor tab.
            persistActiveFurnizorPricingFromRoot(form);
            const pricings = form.furnizorPricings || [];
            pricings.forEach((pricing) => {
                const tabKey = pricing.clientKey;
                const supplierCurrent = String(
                    pricing.supplierListPriceNew ?? "",
                ).trim();
                const supplierBaseline = String(
                    this.supplierListPriceSessionBaselineByTab[tabKey] ?? "",
                ).trim();
                const currentNum = parseNumericInput(supplierCurrent);
                const baselineNum = parseNumericInput(supplierBaseline);
                if (
                    baselineNum > 0 &&
                    currentNum > 0 &&
                    currentNum !== baselineNum
                ) {
                    pricing.supplierListPriceOld = supplierBaseline;
                    pricing.supplierListPriceNew = supplierCurrent;
                } else {
                    pricing.supplierListPriceNew =
                        supplierCurrent || supplierBaseline;
                    pricing.supplierListPriceOld =
                        this.supplierListPricePersistedOldByTab[tabKey] ||
                        pricing.supplierListPriceNew;
                }
            });
            // Sync root buffer from active tab.
            const active = pricings.find(
                (p) => p.clientKey === form.activeFurnizorPricingKey,
            );
            if (active) {
                form.supplierListPriceOld = active.supplierListPriceOld;
                form.supplierListPriceNew = active.supplierListPriceNew;
            }

            // List reference must use the *active* furnizor session baseline.
            // Product-level baseline belongs to the default tab and would
            // overwrite another furnizor's restored reference on draft save.
            const activeKey = String(form.activeFurnizorPricingKey || "").trim();
            const activeSessionStart = activeKey
                ? this.pricingFieldSessionStartBaselinesByTab[activeKey]
                : null;
            const listCurrent = parseNumericInput(
                this.computedProduct?.listPriceRonIncVatFinal,
            );
            const listBaseline = parseNumericInput(
                activeSessionStart?.listPriceRonIncVatFinal
                    ?? activeSessionStart?.listPriceRonIncVatReference
                    ?? this.listPriceRonIncVatSessionBaseline,
            );
            const activeIsDefault = Boolean(active?.isDefault) || !active;
            if (listCurrent > 0 && listBaseline > 0 && listCurrent !== listBaseline) {
                form.listPriceRonIncVatReference = formatNumericInputValue(
                    listBaseline,
                    2,
                );
            } else if (
                activeIsDefault
                && this.listPriceRonIncVatPersistedReference
            ) {
                form.listPriceRonIncVatReference =
                    this.listPriceRonIncVatPersistedReference;
            } else if (listBaseline > 0) {
                form.listPriceRonIncVatReference = formatNumericInputValue(
                    listBaseline,
                    2,
                );
            }
        },
        /**
         * After draft payload build, reset live supplier old/new to the last
         * saved "new" values so reopen does not show a catalog-old % for a
         * field the user never edited in this session.
         */
        normalizeSupplierPricesForDraftBuilderUi() {
            const form = this.productBuilderForm;
            const pricings = form.furnizorPricings || [];
            pricings.forEach((pricing) => {
                if (!pricing?.clientKey) {
                    return;
                }
                const current = String(pricing.supplierListPriceNew ?? "").trim();
                pricing.supplierListPriceOld = current;
                this.supplierListPriceSessionBaselineByTab[pricing.clientKey] =
                    current;
                this.supplierListPricePersistedOldByTab[pricing.clientKey] =
                    current;
            });
            const active = pricings.find(
                (p) => p.clientKey === form.activeFurnizorPricingKey,
            );
            if (active) {
                form.supplierListPriceOld = active.supplierListPriceOld;
                form.supplierListPriceNew = active.supplierListPriceNew;
            } else {
                const current = String(form.supplierListPriceNew ?? "").trim();
                form.supplierListPriceOld = current;
            }
            persistActiveFurnizorPricingFromRoot(form);
        },
        formatSummaryPrice(value) {
            if (!value) {
                return "—";
            }
            return formatNumber(this, value);
        },
        formatProductStatusLabel(value) {
            return formatProductStatus(value);
        },
        productStatusTextStyle(value) {
            return getProductStatusTextStyle(value);
        },
        onProductStatusChange(value) {
            this.productBuilderForm.status = normalizeProductStatus(value);
        },
        panelDomId(panelValue) {
            return `builder-panel-${panelValue}`;
        },
        systemPairLabel(index) {
            return index === 0
                ? "Sistem / Subsistem"
                : `Sistem / Subsistem ${index + 1}`;
        },
        systemPairDisplay(index) {
            const system = this.productBuilderForm.systems[index];
            const subcategory =
                this.productBuilderForm.systemSubcategories[index];
            if (!system) {
                return "";
            }
            const systemTitle =
                this.systemOptions.find(option => option.value === system)
                    ?.title || system;
            if (!subcategory) {
                return `${systemTitle} › …`;
            }
            const subcategoryTitle =
                this.subcategoryOptionsForSystem(index).find(
                    option => option.value === subcategory,
                )?.title || subcategory;
            return `${systemTitle} › ${subcategoryTitle}`;
        },
        syncSystemPairMenusOpen() {
            const count = this.productBuilderForm.systems.length;
            while (this.systemPairMenusOpen.length < count) {
                this.systemPairMenusOpen.push(false);
            }
            while (this.systemPairMenusOpen.length > count) {
                this.systemPairMenusOpen.pop();
            }
        },
        onSystemPairMenuToggle(index, open) {
            this.systemPairMenusOpen[index] = open;
            if (open && this.productBuilderForm.systems[index]) {
                this.loadSubcategoryOptionsForSystem(
                    this.productBuilderForm.systems[index],
                );
            }
        },
        closeSystemPairMenu(index) {
            this.systemPairMenusOpen[index] = false;
        },
        clearSystemPair(index) {
            this.productBuilderForm.systems[index] = "";
            this.productBuilderForm.systemSubcategories[index] = "";
            this.systemPairMenusOpen[index] = false;
        },
        subcategoryOptionsForSystem(index) {
            const system = this.productBuilderForm.systems[index];
            if (!system) {
                return [];
            }
            const normalizedSystem = normalizeSystemValue(system);
            return this.subcategoryOptionsBySystem[normalizedSystem] || [];
        },
        subcategoryRule(index) {
            return value => {
                const system = this.productBuilderForm.systems[index];
                if (!system) {
                    return true;
                }
                return !!String(value ?? "").trim() || "Selectați subsistemul.";
            };
        },
        discountFieldLabel(index) {
            return index === 0 ? "Discount (%)" : `Discount ${index + 1} (%)`;
        },
        discountNoteFieldLabel(index) {
            return index === 0
                ? "Note - Discount"
                : `Note - Discount ${index + 1}`;
        },
        costUpliftFieldLabel(index) {
            const isPercent = this.costUpliftTypeAt(index) === "percent";
            if (index === 0) {
                return isPercent
                    ? "Majorare cost (%)"
                    : "Majorare cost (RON/buc)";
            }
            return isPercent
                ? `Majorare cost ${index + 1} (%)`
                : `Majorare cost ${index + 1} (RON/buc)`;
        },
        costUpliftNoteFieldLabel(index) {
            return index === 0
                ? "Note - Majorare"
                : `Note - Majorare ${index + 1}`;
        },
        costUpliftTypeAt(index) {
            const types = this.productBuilderForm.costUpliftTypes;
            if (Array.isArray(types) && types[index]) {
                return types[index] === "percent" ? "percent" : "fixed";
            }
            return "fixed";
        },
        onCostUpliftTypeChange(index, nextType) {
            if (!Array.isArray(this.productBuilderForm.costUpliftTypes)) {
                this.productBuilderForm.costUpliftTypes = [];
            }
            while (
                this.productBuilderForm.costUpliftTypes.length
                < (this.productBuilderForm.costUplifts?.length || 0)
            ) {
                this.productBuilderForm.costUpliftTypes.push("fixed");
            }
            this.productBuilderForm.costUpliftTypes[index] =
                nextType === "percent" ? "percent" : "fixed";
            this.productBuilderForm.costUplifts[index] = "";
            this.applyCostUpliftPricingSideEffects();
        },
        onCostUpliftFieldDraft(index, value) {
            if (!Array.isArray(this.productBuilderForm.costUplifts)) {
                this.productBuilderForm.costUplifts = [];
            }
            const next = [...this.productBuilderForm.costUplifts];
            next[index] = value;
            this.productBuilderForm.costUplifts = next;
        },
        discountFieldBaseline(index) {
            return baselineValueForField(
                `discounts.${index}`,
                this.activePricingFieldBaseline,
            );
        },
        discountFieldOldValue(index) {
            const savedValue = this.discountFieldBaseline(index);
            if (String(savedValue ?? "").trim() !== "") {
                return savedValue;
            }
            const referenceValue = baselineValueForField(
                `discounts.${index}`,
                this.activePricingFieldReferenceBaseline,
            );
            if (String(referenceValue ?? "").trim() !== "") {
                return referenceValue;
            }
            // New discount row / no prior value: treat previous as 0% so hints can show.
            const current = this.productBuilderForm.discounts?.[index] ?? "";
            if (String(current ?? "").trim() !== "") {
                return "0";
            }
            return "";
        },
        discountFieldChangeRatio(index) {
            if (this.usesCompareReferenceHints) {
                return this.compareReferenceChangeRatio(`discounts.${index}`, {
                    asPercent: true,
                });
            }
            const baseline = this.discountFieldHintOldValue(index);
            // Old 0/empty ⇒ no % badge (nothing meaningful to compare against).
            if (!(parseNumericInput(baseline) > 0)) {
                return null;
            }
            const current = this.productBuilderForm.discounts?.[index] ?? "";
            return calculatePricingPercentFieldChangeRatio(current, baseline);
        },
        discountFieldChangeHint(index) {
            const ratio = this.discountFieldChangeRatio(index);
            if (ratio === null || ratio === 0) {
                return "";
            }
            return formatPercentValue(ratio, 1);
        },
        discountFieldChangeHintClass(index) {
            return pricingFieldChangeHintClass(
                this.discountFieldChangeRatio(index),
            );
        },
        discountFieldSavedHint(index) {
            const compareHint = this.formatCompareReferenceSavedHint(
                `discounts.${index}`,
                { asPercent: true, valueDecimals: 2 },
            );
            if (compareHint !== null) {
                return compareHint;
            }
            const savedValue = this.discountFieldHintOldValue(index);
            if (String(savedValue ?? "").trim() === "") {
                return "";
            }
            let referenceValue = baselineValueForField(
                `discounts.${index}`,
                this.activePricingFieldReferenceBaseline,
            );
            if (String(referenceValue ?? "").trim() === "") {
                referenceValue = savedValue;
            }
            return formatPricingFieldSavedHint(savedValue, referenceValue, {
                asPercent: true,
            });
        },
        discountFieldHintOldValue(index) {
            const hintValue = baselineValueForField(
                `discounts.${index}`,
                this.activePricingFieldLastSavedBaseline,
            );
            if (String(hintValue ?? "").trim() !== "") {
                return hintValue;
            }
            return this.discountFieldOldValue(index);
        },
        pricingPercentFieldOldValue(fieldKey) {
            const savedValue = baselineValueForField(
                fieldKey,
                this.activePricingFieldBaseline,
            );
            if (String(savedValue ?? "").trim() !== "") {
                return savedValue;
            }
            const referenceValue = baselineValueForField(
                fieldKey,
                this.activePricingFieldReferenceBaseline,
            );
            if (String(referenceValue ?? "").trim() !== "") {
                return referenceValue;
            }
            // No prior % value: treat previous as 0% so hints can show.
            const current = this.productBuilderForm[fieldKey] ?? "";
            if (String(current ?? "").trim() !== "") {
                return "0";
            }
            return "";
        },
        pricingPercentFieldHintOldValue(fieldKey) {
            const hintValue = baselineValueForField(
                fieldKey,
                this.activePricingFieldLastSavedBaseline,
            );
            if (String(hintValue ?? "").trim() !== "") {
                return hintValue;
            }
            return this.pricingPercentFieldOldValue(fieldKey);
        },
        pricingInputFieldSavedHint(fieldKey, { asPercent = false } = {}) {
            const compareHint = this.formatCompareReferenceSavedHint(fieldKey, {
                asPercent,
                valueDecimals: asPercent ? 2 : 4,
            });
            if (compareHint !== null) {
                return compareHint;
            }
            // Valoare veche from last-în-lucru hint baselines (not live edits).
            if (asPercent) {
                const savedValue = this.pricingPercentFieldHintOldValue(fieldKey);
                if (String(savedValue ?? "").trim() === "") {
                    return "";
                }
                let referenceValue = baselineValueForField(
                    fieldKey,
                    this.activePricingFieldReferenceBaseline,
                );
                if (String(referenceValue ?? "").trim() === "") {
                    referenceValue = savedValue;
                }
                return formatPricingFieldSavedHint(savedValue, referenceValue, {
                    asPercent: true,
                    valueDecimals: 2,
                });
            }
            // Prefer last-în-lucru; for brand-new furnizor that is explicit "0".
            const savedValue = baselineValueForField(
                fieldKey,
                this.activePricingFieldLastSavedBaseline,
            );
            const sessionValue = baselineValueForField(
                fieldKey,
                this.activePricingFieldBaseline,
            );
            const hintSaved =
                String(savedValue ?? "").trim() !== ""
                    ? savedValue
                    : sessionValue;
            if (fieldKey === "supplierListPriceNew") {
                // Compare current price vs hint old — form.old is often
                // normalized equal to new, so it cannot drive the %.
                if (String(hintSaved ?? "").trim() === "") {
                    return "";
                }
                const current =
                    this.pricingInputDisplayValue("supplierListPriceNew") ||
                    this.productBuilderForm.supplierListPriceNew ||
                    "";
                const oldText = formatNumericInputValue(
                    parseNumericInput(hintSaved),
                    4,
                );
                const ratio = calculatePricingValueChangeRatio(
                    current,
                    hintSaved,
                );
                if (ratio === null || ratio === 0) {
                    return `Valoare veche: ${oldText}`;
                }
                return `Valoare veche: ${oldText} (${formatPercentValue(ratio, 1)})`;
            }
            let referenceValue = baselineValueForField(
                fieldKey,
                this.activePricingFieldReferenceBaseline,
            );
            return formatPricingFieldSavedHint(hintSaved, referenceValue, {
                asPercent: false,
                valueDecimals: 4,
            });
        },
        pricingInputFieldChangeRatio(fieldKey) {
            const current = this.pricingInputDisplayValue(fieldKey);
            // Percent inputs: % badge follows Valoare veche. Old 0/empty ⇒ no ratio.
            if (
                fieldKey === "transportCostRate" ||
                fieldKey === "markupReference" ||
                fieldKey === "markupActual"
            ) {
                if (this.usesCompareReferenceHints) {
                    return this.compareReferenceChangeRatio(fieldKey, {
                        asPercent: true,
                    });
                }
                const oldValue = this.pricingPercentFieldHintOldValue(fieldKey);
                if (!(parseNumericInput(oldValue) > 0)) {
                    return null;
                }
                return calculatePricingPercentFieldChangeRatio(
                    current,
                    oldValue,
                );
            }
            if (fieldKey === "supplierListPriceNew") {
                if (this.usesCompareReferenceHints) {
                    return this.compareReferenceChangeRatio(fieldKey);
                }
                const baseline = baselineValueForField(
                    fieldKey,
                    this.activePricingFieldLastSavedBaseline,
                );
                return calculatePricingValueChangeRatio(current, baseline);
            }
            if (this.usesCompareReferenceHints) {
                return this.compareReferenceChangeRatio(fieldKey);
            }
            const baseline = baselineValueForField(
                fieldKey,
                this.activePricingFieldLastSavedBaseline,
            );
            return calculatePricingValueChangeRatio(current, baseline);
        },
        pricingInputFieldChangeHint(fieldKey) {
            const ratio = this.pricingInputFieldChangeRatio(fieldKey);
            if (ratio === null || ratio === 0) {
                return "";
            }
            return formatPercentValue(ratio, 1);
        },
        pricingInputFieldChangeHintClass(fieldKey) {
            return pricingFieldChangeHintClass(
                this.pricingInputFieldChangeRatio(fieldKey),
            );
        },
        goToPanel(panelValue) {
            this.$nextTick(() => {
                const panelElement = document.getElementById(
                    this.panelDomId(panelValue),
                );
                panelElement?.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                });
            });
        },
        formatResultValue(key, format = "") {
            return formatPricingResultValue(
                key,
                this.computedProduct[key],
                format,
            );
        },
        clearPricingResultDrafts() {
            this.pricingResultDrafts = {};
            this.pricingResultEditingKey = null;
        },
        clearPricingInputDrafts() {
            this.pricingInputDrafts = {};
            this.pricingInputEditingKey = null;
        },
        pricingInputDisplayValue(fieldKey) {
            if (
                this.pricingInputEditingKey === fieldKey &&
                Object.prototype.hasOwnProperty.call(
                    this.pricingInputDrafts,
                    fieldKey,
                )
            ) {
                return this.pricingInputDrafts[fieldKey];
            }
            if (String(fieldKey).startsWith("discounts.")) {
                const index = Number(String(fieldKey).split(".")[1]);
                return this.productBuilderForm.discounts?.[index] ?? "";
            }
            return this.productBuilderForm[fieldKey] ?? "";
        },
        resultFieldDisplayValue(fieldKey, format = "") {
            if (
                this.pricingResultEditingKey === fieldKey &&
                Object.prototype.hasOwnProperty.call(
                    this.pricingResultDrafts,
                    fieldKey,
                )
            ) {
                return this.pricingResultDrafts[fieldKey];
            }
            return this.formatResultValue(fieldKey, format);
        },
        syncMarkupActualManualFromForm() {
            this.markupActualManual = Boolean(
                this.productBuilderForm.overrideListPriceRonIncVatFinal ||
                    String(this.productBuilderForm.markupActual || "").trim(),
            );
        },
        isPricingFieldManual(fieldKey) {
            return Boolean(this.manualEditsForActiveFurnizor[fieldKey]);
        },
        isCompareFieldHighlighted(fieldKey) {
            if (!this.readOnly || !this.compareHighlightIndex) {
                return false;
            }
            const activeTab =
                (this.productBuilderForm?.furnizorPricings || []).find(
                    (row) =>
                        String(row?.clientKey || "") ===
                        String(this.activeFurnizorPricingKey || ""),
                ) || null;
            return isCompareFieldHighlightedForTab(
                this.compareHighlightIndex,
                fieldKey,
                activeTab,
            );
        },
        isCompareFurnizorTabChanged(pricing) {
            if (!this.readOnly || !this.compareHighlightIndex) {
                return false;
            }
            return isCompareFurnizorTabHighlighted(
                this.compareHighlightIndex,
                pricing,
            );
        },
        compareFieldClass(fieldKey) {
            return this.isCompareFieldHighlighted(fieldKey)
                ? "compare-field--changed"
                : undefined;
        },
        isPricingFieldHighlighted(fieldKey) {
            if (this.readOnly) {
                return this.isCompareFieldHighlighted(fieldKey);
            }
            return isPricingFieldChangedFromBaseline(
                fieldKey,
                this.activePricingFieldBaseline,
                this.productBuilderForm,
                (key, format = "") => this.formatResultValue(key, format),
                this.resultFields,
                this.effectivePricingSettings,
            );
        },
        activateCompareFurnizorTab() {
            const wanted = String(this.compareFurnizorTabKey || "").trim();
            if (!wanted || !this.readOnly) {
                return;
            }
            this.activateFurnizorTabByKey(wanted);
        },
        activateInitialFurnizorTab() {
            const wanted = String(this.initialFurnizorTabKey || "").trim();
            if (!wanted) {
                return;
            }
            this.activateFurnizorTabByKey(wanted);
        },
        activateFurnizorTabByKey(wantedRaw) {
            const wanted = String(wantedRaw || "").trim();
            if (!wanted) {
                return;
            }
            const wantedLower = wanted.toLowerCase();
            const mfrName = String(
                this.productBuilderForm?.manufacturerName || "",
            ).trim().toLowerCase();
            const mfrCode = String(
                this.productBuilderForm?.manufacturerCode || "",
            ).trim().toLowerCase();
            const tabs = this.productBuilderForm?.furnizorPricings || [];
            const match = tabs.find((tab) => {
                const clientKey = String(tab?.clientKey || "").trim();
                const code = String(tab?.furnizorCode || "").trim();
                const id = String(tab?.id || "").trim();
                const furnizorId = String(tab?.furnizorId || "").trim();
                if (
                    clientKey === wanted
                    || code === wanted
                    || id === wanted
                    || furnizorId === wanted
                    || `client:${clientKey}` === wanted
                    || `code:${code.toLowerCase()}` === wantedLower
                    || `id:${furnizorId}` === wanted
                ) {
                    return true;
                }
                if (tab?.usesManufacturerAsFurnizor) {
                    if (mfrCode && wantedLower === `mfr:${mfrCode}`) {
                        return true;
                    }
                    if (mfrName && wantedLower === `mfr-name:${mfrName}`) {
                        return true;
                    }
                }
                return false;
            });
            if (!match?.clientKey) {
                return;
            }
            this.withFurnizorTabSwitch(() => {
                applyFurnizorPricingToRoot(this.productBuilderForm, match);
                this.syncMarkupActualManualFromForm();
            });
        },
        clearCompareReferenceState() {
            this.compareReferenceBaselinesByTab = {};
            this.compareReferenceTabs = [];
        },
        loadCompareReferenceBaselines() {
            this.clearCompareReferenceState();
            if (!this.readOnly || !this.compareReferencePayload) {
                return;
            }
            const referenceItem = comparePayloadToEditItem(
                this.compareReferencePayload,
            );
            if (!referenceItem?.pricing_detail) {
                return;
            }
            const { productBuilderForm, pricingSettings } =
                pricingDetailToBuilderForm(
                    referenceItem.pricing_detail,
                    referenceItem,
                );
            const tabs = Array.isArray(productBuilderForm?.furnizorPricings)
                ? productBuilderForm.furnizorPricings
                : [];
            this.compareReferenceTabs = tabs;
            this.compareReferenceBaselinesByTab = buildAllPricingFieldBaselines(
                productBuilderForm,
                pricingSettings || this.effectivePricingSettings,
                this.resultFields,
            );
        },
        getActiveCompareReferenceBaseline() {
            if (!this.usesCompareReferenceHints) {
                return null;
            }
            const activeTab = this.activeFurnizorPricing || null;
            const leftTab = activeTab
                ? findMatchingLiveFurnizorTab(activeTab, this.compareReferenceTabs)
                : null;
            const matched =
                leftTab
                || this.compareReferenceTabs.find((tab) => tab?.isDefault)
                || this.compareReferenceTabs[0]
                || null;
            if (!matched?.clientKey) {
                return null;
            }
            return (
                this.compareReferenceBaselinesByTab[matched.clientKey] || null
            );
        },
        /**
         * Compare-only „Valoare veche” from lista de referință.
         * Returns null when compare hints are inactive (caller falls through).
         */
        formatCompareReferenceSavedHint(
            fieldKey,
            { asPercent = false, valueDecimals = 4 } = {},
        ) {
            if (!this.usesCompareReferenceHints) {
                return null;
            }
            const baseline = this.getActiveCompareReferenceBaseline();
            if (!baseline) {
                return "";
            }
            const oldValue = baselineValueForField(fieldKey, baseline);
            if (String(oldValue ?? "").trim() === "") {
                return "";
            }
            const current = asPercent
                ? (this.productBuilderForm[fieldKey]
                    ?? (String(fieldKey).startsWith("discounts.")
                        ? this.productBuilderForm.discounts?.[
                            Number(fieldKey.split(".")[1])
                        ]
                        : "")
                    ?? "")
                : this.pricingInputDisplayValue(fieldKey);

            if (asPercent) {
                const oldNum = parseNumericInput(oldValue);
                if (!Number.isFinite(oldNum) && String(oldValue).trim() === "") {
                    return "";
                }
                const oldText = `${formatNumericInputValue(oldNum, 2)}%`;
                const ratio = calculatePricingPercentFieldChangeRatio(
                    current,
                    oldValue,
                );
                if (ratio === null || ratio === 0) {
                    return `Valoare veche: ${oldText}`;
                }
                return `Valoare veche: ${oldText} (${formatPercentValue(ratio, 1)})`;
            }

            const oldNum = parseNumericInput(oldValue);
            if (!(oldNum > 0)) {
                return "";
            }
            const oldText = formatNumericInputValue(oldNum, valueDecimals);
            const ratio = calculatePricingValueChangeRatio(current, oldValue);
            if (ratio === null || ratio === 0) {
                return `Valoare veche: ${oldText}`;
            }
            return `Valoare veche: ${oldText} (${formatPercentValue(ratio, 1)})`;
        },
        formatCompareReferenceResultSavedHint(fieldKey) {
            if (!this.usesCompareReferenceHints) {
                return null;
            }
            const baseline = this.getActiveCompareReferenceBaseline();
            if (!baseline) {
                return "";
            }
            const oldValue = baselineValueForField(fieldKey, baseline);
            if (String(oldValue ?? "").trim() === "") {
                return "";
            }
            const oldNum = parseNumericInput(oldValue);
            if (!(oldNum > 0)) {
                return "";
            }
            const resultField = this.resultFields.find(
                (field) => field.key === fieldKey,
            );
            const current = this.formatResultValue(
                fieldKey,
                resultField?.format || "",
            );
            const oldText = formatNumericInputValue(oldNum, 2);
            const ratio = calculatePricingValueChangeRatio(current, oldValue);
            if (ratio === null || ratio === 0) {
                return `Valoare veche: ${oldText}`;
            }
            return `Valoare veche: ${oldText} (${formatPercentValue(ratio, 1)})`;
        },
        compareReferenceChangeRatio(fieldKey, { asPercent = false } = {}) {
            if (!this.usesCompareReferenceHints) {
                return undefined;
            }
            const baseline = this.getActiveCompareReferenceBaseline();
            if (!baseline) {
                return null;
            }
            const oldValue = baselineValueForField(fieldKey, baseline);
            if (String(oldValue ?? "").trim() === "") {
                return null;
            }
            const current = asPercent
                ? (this.productBuilderForm[fieldKey] ?? "")
                : this.pricingInputDisplayValue(fieldKey);
            if (asPercent) {
                return calculatePricingPercentFieldChangeRatio(current, oldValue);
            }
            return calculatePricingValueChangeRatio(current, oldValue);
        },
        markPricingFieldManual(fieldKey) {
            const tabKey = this.activeFurnizorPricingKey;
            if (!tabKey || !fieldKey) {
                return;
            }
            this.manuallyEditedPricingFields = {
                ...this.manuallyEditedPricingFields,
                [tabKey]: {
                    ...(this.manuallyEditedPricingFields[tabKey] || {}),
                    [fieldKey]: true,
                },
            };
        },
        clearManualPricingFields(fieldKeys = []) {
            const tabKey = this.activeFurnizorPricingKey;
            if (!tabKey || !fieldKeys.length) {
                return;
            }
            const tabEdits = {
                ...(this.manuallyEditedPricingFields[tabKey] || {}),
            };
            fieldKeys.forEach(fieldKey => {
                delete tabEdits[fieldKey];
            });
            this.manuallyEditedPricingFields = {
                ...this.manuallyEditedPricingFields,
                [tabKey]: tabEdits,
            };
        },
        clearAllManualPricingFields() {
            this.manuallyEditedPricingFields = {};
        },
        capturePricingFieldBaselines() {
            persistActiveFurnizorPricingFromRoot(this.productBuilderForm);
            this.pricingFieldBaselinesByTab = buildAllPricingFieldBaselines(
                this.productBuilderForm,
                this.effectivePricingSettings,
                this.resultFields,
            );
            this.pricingFieldReferenceBaselinesByTab =
                buildAllPricingFieldReferenceBaselines(
                    this.productBuilderForm,
                    this.effectivePricingSettings,
                    this.resultFields,
                );
            // First capture: hint + session-start start equal to the open session.
            this.pricingFieldLastSavedBaselinesByTab = cloneDeep(
                this.pricingFieldBaselinesByTab,
            );
            this.pricingFieldSessionStartBaselinesByTab = cloneDeep(
                this.pricingFieldBaselinesByTab,
            );
        },
        captureSessionPricingFieldBaselines() {
            persistActiveFurnizorPricingFromRoot(this.productBuilderForm);
            this.pricingFieldBaselinesByTab = buildAllPricingFieldBaselines(
                this.productBuilderForm,
                this.effectivePricingSettings,
                this.resultFields,
            );
            if (!Object.keys(this.pricingFieldLastSavedBaselinesByTab).length) {
                this.pricingFieldLastSavedBaselinesByTab = cloneDeep(
                    this.pricingFieldBaselinesByTab,
                );
            }
            this.captureSessionStartPricingFieldBaselines();
            const savedListPrice = parseNumericInput(
                this.computedProduct?.listPriceRonIncVatFinal,
            );
            if (savedListPrice > 0) {
                this.listPriceRonIncVatSessionBaseline = savedListPrice;
            }
        },
        /**
         * Fresh open of a live (non-draft) product: every furnizor tab's open
         * baseline / Valoare veche / session-start must match the saved form so
         * nothing is orange and reset is hidden — including newly added tabs.
         *
         * Adaos actual is re-derived from list vs entry on tab focus when list
         * price is sticky (syncComputedMarkupActual). Stored API values can
         * differ by rounding, so we normalize each tab to the computed % before
         * snapshotting.
         */
        captureCleanOpenPricingBaselines() {
            persistActiveFurnizorPricingFromRoot(this.productBuilderForm);
            this.clearAllManualPricingFields();
            this.clearPricingInputDrafts();
            this.clearPricingResultDrafts();
            this.pricingFieldFocusSnapshot = null;
            this.listPriceIncVatFocusSnapshot = null;

            this.normalizeDerivedMarkupActualOnAllFurnizorTabs();

            this.captureSessionPricingFieldBaselines();
            this.pricingFieldLastSavedBaselinesByTab = cloneDeep(
                this.pricingFieldBaselinesByTab,
            );
            this.pricingFieldReferenceBaselinesByTab =
                buildAllPricingFieldReferenceBaselines(
                    this.productBuilderForm,
                    this.effectivePricingSettings,
                    this.resultFields,
                );
            const pricings = this.productBuilderForm.furnizorPricings || [];
            pricings.forEach((pricing) => {
                if (!pricing?.clientKey) {
                    return;
                }
                const current = String(pricing.supplierListPriceNew ?? "").trim();
                this.supplierListPriceSessionBaselineByTab[pricing.clientKey] =
                    current;
                this.supplierListPricePersistedOldByTab[pricing.clientKey] =
                    current;
            });
        },
        /**
         * Align each furnizor row's markupActual with what the calculator will
         * show after tab switch / syncComputedMarkupActual, so clean open has
         * no false orange highlight.
         */
        normalizeDerivedMarkupActualOnAllFurnizorTabs() {
            const form = this.productBuilderForm;
            const settings = this.effectivePricingSettings;
            const pricings = Array.isArray(form.furnizorPricings)
                ? form.furnizorPricings
                : [];
            pricings.forEach((pricing) => {
                if (!pricing) {
                    return;
                }
                const merged = mergeFormWithFurnizorPricing(form, pricing);
                const computed = calculateProductPricing(
                    builderFormToCalculatorInput(merged),
                    settings,
                );
                if (!(parseNumericInput(computed?.entryPriceRonIncVat) > 0)) {
                    return;
                }
                const nextMarkup = ratioToPercentageInput(
                    computed.computedMarkupActual,
                );
                pricing.markupActual = nextMarkup;
            });
            persistActiveFurnizorPricingFromRoot(form);
            const active = getActiveFurnizorPricing(form);
            if (active) {
                form.markupActual = active.markupActual ?? form.markupActual;
            }
            this.markupActualManual = false;
        },
        captureSessionStartPricingFieldBaselines() {
            persistActiveFurnizorPricingFromRoot(this.productBuilderForm);
            this.pricingFieldSessionStartBaselinesByTab =
                buildAllPricingFieldBaselines(
                    this.productBuilderForm,
                    this.effectivePricingSettings,
                    this.resultFields,
                );
        },
        captureLastSavedPricingFieldBaselinesFromCurrent() {
            persistActiveFurnizorPricingFromRoot(this.productBuilderForm);
            this.pricingFieldLastSavedBaselinesByTab =
                buildAllPricingFieldBaselines(
                    this.productBuilderForm,
                    this.effectivePricingSettings,
                    this.resultFields,
                );
        },
        captureLastSavedPricingFieldBaselinesFromSessionStart() {
            this.pricingFieldLastSavedBaselinesByTab = cloneDeep(
                this.pricingFieldSessionStartBaselinesByTab || {},
            );
            if (!Object.keys(this.pricingFieldLastSavedBaselinesByTab).length) {
                this.captureLastSavedPricingFieldBaselinesFromCurrent();
            }
        },
        pricingResultFieldSessionBaseline(fieldKey) {
            return this.activePricingFieldBaseline?.[fieldKey] ?? "";
        },
        pricingResultFieldHintOldValue(fieldKey) {
            // Valoare veche only from last-în-lucru (not open-session / invent 0).
            return this.activePricingFieldLastSavedBaseline?.[fieldKey] ?? "";
        },
        pricingResultFieldCurrentValue(fieldKey, format = "") {
            if (
                this.pricingResultEditingKey === fieldKey &&
                Object.prototype.hasOwnProperty.call(
                    this.pricingResultDrafts,
                    fieldKey,
                )
            ) {
                return this.pricingResultDrafts[fieldKey];
            }
            return this.formatResultValue(fieldKey, format);
        },
        pricingResultFieldChangeRatio(fieldKey, format = "") {
            if (this.usesCompareReferenceHints) {
                const baseline = this.getActiveCompareReferenceBaseline();
                if (!baseline) {
                    return null;
                }
                const oldValue = baselineValueForField(fieldKey, baseline);
                if (String(oldValue ?? "").trim() === "") {
                    return null;
                }
                const current = this.pricingResultFieldCurrentValue(
                    fieldKey,
                    format,
                );
                return calculatePricingValueChangeRatio(current, oldValue);
            }
            // % badge must match Valoare veche (last-în-lucru). Old 0 ⇒ null
            // (no meaningful relative difference — e.g. brand-new furnizor).
            const baseline = this.pricingResultFieldHintOldValue(fieldKey);
            if (String(baseline ?? "").trim() === "") {
                return null;
            }
            const current = this.pricingResultFieldCurrentValue(fieldKey, format);
            return calculatePricingValueChangeRatio(current, baseline);
        },
        pricingResultFieldChangeHint(fieldKey, format = "") {
            const ratio = this.pricingResultFieldChangeRatio(fieldKey, format);
            if (ratio === null || ratio === 0) {
                return "";
            }
            return formatPercentValue(ratio, 1);
        },
        pricingResultFieldChangeHintClass(fieldKey, format = "") {
            return pricingFieldChangeHintClass(
                this.pricingResultFieldChangeRatio(fieldKey, format),
            );
        },
        pricingResultFieldSavedHint(fieldKey) {
            const compareHint =
                this.formatCompareReferenceResultSavedHint(fieldKey);
            if (compareHint !== null) {
                return compareHint;
            }
            const savedValue = this.pricingResultFieldHintOldValue(fieldKey);
            if (!String(savedValue ?? "").trim()) {
                return "";
            }
            const resultField = this.resultFields.find(
                (field) => field.key === fieldKey,
            );
            const current = this.pricingResultFieldCurrentValue(
                fieldKey,
                resultField?.format || "",
            );
            // Same % as the orange badge (current vs last-în-lucru).
            // Do NOT compare old vs AF/reference — that produced inconsistent
            // (21,5%) / (-9,1%) on ex-VAT vs cu-TVA and false -99,99% with no edit.
            return formatPricingResultSavedHint(savedValue, current);
        },
        capturePricingFieldBaselineForTab(tab) {
            if (!tab?.clientKey) {
                return;
            }
            // Open-session only (orange highlight vs copied form values).
            // Valoare veche / last-în-lucru / reference are set by the caller
            // (zeros for brand-new furnizor).
            const snapshot = buildPricingFieldBaselineSnapshot(
                tab,
                this.productBuilderForm,
                this.effectivePricingSettings,
                this.resultFields,
            );
            this.pricingFieldBaselinesByTab = {
                ...this.pricingFieldBaselinesByTab,
                [tab.clientKey]: snapshot,
            };
            this.pricingFieldSessionStartBaselinesByTab = {
                ...this.pricingFieldSessionStartBaselinesByTab,
                [tab.clientKey]: cloneDeep(snapshot),
            };
        },
        pricingFieldFocusStorageKey(fieldKey) {
            return `${this.activeFurnizorPricingKey}:${fieldKey}`;
        },
        storePricingFieldFocusSnapshot(fieldKey, value) {
            this.pricingFieldFocusSnapshot = {
                key: this.pricingFieldFocusStorageKey(fieldKey),
                value: stableSerialize(value),
            };
        },
        captureListPriceFocusSnapshot() {
            this.listPriceIncVatFocusSnapshot =
                this.computedProduct?.listPriceRonIncVatFinal ?? null;
        },
        pricingFieldEditOptions() {
            return {
                preservedListPriceRonIncVatFinal:
                    this.listPriceIncVatFocusSnapshot,
            };
        },
        hasPricingFieldFocusChanged(fieldKey, currentValue) {
            const snapshot = this.pricingFieldFocusSnapshot;
            // No focus snapshot ⇒ not a real user edit (e.g. drafts re-armed
            // after reset). Do not treat as changed.
            if (
                !snapshot ||
                snapshot.key !== this.pricingFieldFocusStorageKey(fieldKey)
            ) {
                return false;
            }
            return snapshot.value !== stableSerialize(currentValue);
        },
        resetAllPricingFieldsToBaseline() {
            const baseline = this.activePricingFieldBaseline;
            if (!baseline || !this.hasPricingChangesFromBaseline) {
                return;
            }
            if (this.pricingSyncLock || this.shouldSkipPricingFieldEdit()) {
                return;
            }
            this.pricingSyncLock = true;
            try {
                // Drop in-progress edits so child inputs / save flush cannot
                // re-apply pre-reset sticky list price on top of restore.
                this.clearPricingInputDrafts();
                this.clearPricingResultDrafts();
                this.pricingFieldFocusSnapshot = null;
                this.listPriceIncVatFocusSnapshot = null;

                restorePricingFieldsFromBaseline(
                    this.productBuilderForm,
                    baseline,
                );

                const tabKey = this.activeFurnizorPricingKey;
                if (tabKey) {
                    this.manuallyEditedPricingFields = {
                        ...this.manuallyEditedPricingFields,
                        [tabKey]: {},
                    };
                }

                normalizePricingFormNumericDisplay(this.productBuilderForm);
                persistActiveFurnizorPricingFromRoot(this.productBuilderForm);
                this.markupActualManual = Boolean(
                    this.productBuilderForm.overrideListPriceRonIncVatFinal ||
                        String(
                            this.productBuilderForm.markupActual ?? "",
                        ).trim(),
                );
            } finally {
                this.$nextTick(() => {
                    // Child :model-value sync may re-arm drafts after restore.
                    this.clearPricingInputDrafts();
                    this.clearPricingResultDrafts();
                    this.pricingFieldFocusSnapshot = null;
                    this.listPriceIncVatFocusSnapshot = null;
                    // Let computed/markup sync settle, then refresh this tab's
                    // open baseline from the restored form so the reset button
                    // hides (avoids false diffs from markupActual sync / result formatting).
                    this.pricingSyncLock = false;
                    this.$nextTick(() => {
                        this.resyncActiveTabBaselineAfterReset();
                    });
                });
            }
        },
        /**
         * After restore, computed markup/results can differ slightly from the
         * frozen open snapshot. Re-snapshot open + session-start only so the
         * reset button hides — do NOT advance Valoare veche (lastSaved/reference).
         */
        resyncActiveTabBaselineAfterReset() {
            const tabKey = this.activeFurnizorPricingKey;
            const active = getActiveFurnizorPricing(this.productBuilderForm);
            if (!tabKey || !active) {
                return;
            }
            persistActiveFurnizorPricingFromRoot(this.productBuilderForm);
            const snapshot = buildPricingFieldBaselineSnapshot(
                active,
                this.productBuilderForm,
                this.effectivePricingSettings,
                this.resultFields,
            );
            this.pricingFieldBaselinesByTab = {
                ...this.pricingFieldBaselinesByTab,
                [tabKey]: snapshot,
            };
            this.pricingFieldSessionStartBaselinesByTab = {
                ...this.pricingFieldSessionStartBaselinesByTab,
                [tabKey]: cloneDeep(snapshot),
            };
            this.supplierListPriceSessionBaselineByTab[tabKey] = String(
                this.productBuilderForm.supplierListPriceNew ?? "",
            ).trim();
        },
        withFurnizorTabSwitch(callback) {
            this.furnizorTabSwitchLock = true;
            try {
                callback();
            } finally {
                this.$nextTick(() => {
                    this.furnizorTabSwitchLock = false;
                });
            }
        },
        shouldSkipPricingFieldEdit() {
            return this.formLoading || this.furnizorTabSwitchLock;
        },
        syncManualPricingFieldDependencies(manualFieldKey, calcFieldKey) {
            const resultFields = PRICING_RESULT_FIELD_KEYS;
            const isDiscountField =
                String(manualFieldKey).startsWith("discounts");
            const isTransportEdit =
                calcFieldKey === "transportCostRate" ||
                calcFieldKey === "transportCostBreakdown";
            const isStampDutyEdit =
                calcFieldKey === "stampDutyAmount" ||
                calcFieldKey === "stampDutyBreakdown";
            const isInputEdit =
                PRICING_INPUT_FIELD_KEYS.includes(calcFieldKey) ||
                isDiscountField ||
                calcFieldKey === "currency";

            if (
                isInputEdit &&
                calcFieldKey !== "markupActual" &&
                !isTransportEdit &&
                !isStampDutyEdit
            ) {
                this.clearManualPricingFields(resultFields);
            }
            if (calcFieldKey === "markupActual") {
                this.clearManualPricingFields(resultFields);
            }
            if (resultFields.includes(manualFieldKey)) {
                this.clearManualPricingFields(
                    resultFields.filter(fieldKey => fieldKey !== manualFieldKey),
                );
            }
        },
        runPricingFieldEdit(fieldKey, value, manualFieldKey = fieldKey, extraOptions = {}) {
            if (this.pricingSyncLock || this.shouldSkipPricingFieldEdit()) {
                return;
            }
            this.pricingSyncLock = true;
            try {
                const result = applyPricingFieldEdit(
                    this.productBuilderForm,
                    fieldKey,
                    value,
                    this.effectivePricingSettings,
                    {
                        ...this.pricingFieldEditOptions(),
                        ...extraOptions,
                    },
                );
                if (result.markupActualManual !== undefined) {
                    this.markupActualManual = result.markupActualManual;
                }
                normalizePricingFormNumericDisplay(this.productBuilderForm);
                persistActiveFurnizorPricingFromRoot(this.productBuilderForm);
                this.markPricingFieldManual(manualFieldKey);
                this.syncManualPricingFieldDependencies(
                    manualFieldKey,
                    fieldKey,
                );
            } finally {
                this.$nextTick(() => {
                    this.pricingSyncLock = false;
                });
            }
        },
        onPricingSelectFieldChange(value) {
            if (this.shouldSkipPricingFieldEdit()) {
                return;
            }
            const previousCurrency = this.productBuilderForm.currency;
            if (
                pricingFieldValuesEqual(
                    "currency",
                    value,
                    previousCurrency,
                )
            ) {
                return;
            }
            const hasListPrice =
                parseNumericInput(
                    this.productBuilderForm.supplierListPriceNew,
                ) > 0;
            if (!hasListPrice) {
                this.applyCurrencyChange(previousCurrency, value, {
                    convertPrices: false,
                });
                return;
            }
            this.pendingCurrencyChange = {
                from: previousCurrency,
                to: value,
            };
            this.currencyConvertDialogOpen = true;
        },
        currencyOptionLabel(code) {
            const normalized = String(code || "").trim().toLowerCase();
            const option = this.currencyOptions.find(
                row => String(row.value || "").trim().toLowerCase() === normalized,
            );
            if (option?.title) {
                return option.title;
            }
            if (!normalized) {
                return "—";
            }
            if (normalized === "euro" || normalized === "eur") {
                return "EUR";
            }
            return normalized.toUpperCase();
        },
        cancelCurrencyConvertDialog() {
            this.currencyConvertDialogOpen = false;
            this.pendingCurrencyChange = null;
        },
        resolveCurrencyConvertDialog(convertPrices) {
            const pending = this.pendingCurrencyChange;
            this.currencyConvertDialogOpen = false;
            this.pendingCurrencyChange = null;
            if (!pending?.to) {
                return;
            }
            this.applyCurrencyChange(pending.from, pending.to, {
                convertPrices: Boolean(convertPrices),
            });
        },
        applyCurrencyChange(fromCurrency, toCurrency, { convertPrices = true } = {}) {
            this.captureListPriceFocusSnapshot();
            this.runPricingFieldEdit("currency", toCurrency, "currency", {
                convertSupplierListPriceNew: convertPrices,
                convertSupplierListPriceOld: convertPrices,
            });
            if (convertPrices) {
                this.convertActiveTabBaselinesForCurrencyChange(
                    fromCurrency,
                    toCurrency,
                );
            }
        },
        convertActiveTabBaselinesForCurrencyChange(fromCurrency, toCurrency) {
            const tabKey = this.activeFurnizorPricingKey;
            if (!tabKey) {
                return;
            }
            const settings = this.effectivePricingSettings;
            const convertMap = map => {
                const current = map?.[tabKey];
                if (!current || typeof current !== "object") {
                    return map;
                }
                return {
                    ...map,
                    [tabKey]: convertPricingBaselineForCurrencyChange(
                        current,
                        fromCurrency,
                        toCurrency,
                        settings,
                    ),
                };
            };
            this.pricingFieldLastSavedBaselinesByTab = convertMap(
                this.pricingFieldLastSavedBaselinesByTab,
            );
            this.pricingFieldSessionStartBaselinesByTab = convertMap(
                this.pricingFieldSessionStartBaselinesByTab,
            );
            this.pricingFieldReferenceBaselinesByTab = convertMap(
                this.pricingFieldReferenceBaselinesByTab,
            );
            // Do NOT convert pricingFieldBaselinesByTab (open-session snapshot):
            // currency must stay orange vs that baseline, and Reset must restore
            // the original currency + amounts from when the editor opened.

            const convertAmountMap = map => {
                if (
                    !map
                    || !Object.prototype.hasOwnProperty.call(map, tabKey)
                ) {
                    return map;
                }
                return {
                    ...map,
                    [tabKey]: convertSupplierListPriceAmount(
                        map[tabKey],
                        fromCurrency,
                        toCurrency,
                        settings,
                    ),
                };
            };
            this.supplierListPriceSessionBaselineByTab = convertAmountMap(
                this.supplierListPriceSessionBaselineByTab,
            );
            this.supplierListPricePersistedOldByTab = convertAmountMap(
                this.supplierListPricePersistedOldByTab,
            );
        },
        onPricingInputFieldFocus(fieldKey) {
            if (fieldKey === "markupActual") {
                this.markupActualManual = true;
            }
            const current = this.pricingInputDisplayValue(fieldKey);
            this.pricingInputEditingKey = fieldKey;
            this.pricingInputDrafts = {
                ...this.pricingInputDrafts,
                [fieldKey]: current,
            };
            this.storePricingFieldFocusSnapshot(fieldKey, current);
            this.captureListPriceFocusSnapshot();
        },
        onPricingInputFieldDraft(fieldKey, value) {
            if (this.pricingSyncLock || this.shouldSkipPricingFieldEdit()) {
                return;
            }
            this.pricingInputEditingKey = fieldKey;
            this.pricingInputDrafts = {
                ...this.pricingInputDrafts,
                [fieldKey]: value,
            };
        },
        onPricingInputFieldBlur(fieldKey) {
            if (
                this.shouldSkipPricingFieldEdit() ||
                this.pricingSyncLock
            ) {
                this.clearPricingInputDrafts();
                return;
            }
            const draft = Object.prototype.hasOwnProperty.call(
                this.pricingInputDrafts,
                fieldKey,
            )
                ? this.pricingInputDrafts[fieldKey]
                : this.productBuilderForm[fieldKey];
            if (!this.hasPricingFieldFocusChanged(fieldKey, draft)) {
                this.clearPricingInputDrafts();
                return;
            }
            this.clearPricingInputDrafts();
            this.productBuilderForm[fieldKey] = draft;
            this.runPricingFieldEdit(fieldKey, draft);
        },
        onLockMarkupToReferenceChange(value) {
            if (this.shouldSkipPricingFieldEdit() || this.pricingSyncLock) {
                return;
            }
            this.captureListPriceFocusSnapshot();
            this.runPricingFieldEdit('lockMarkupToReference', Boolean(value));
        },
        onDiscountFieldFocus(index) {
            const fieldKey = `discounts.${index}`;
            const current = this.pricingInputDisplayValue(fieldKey);
            this.pricingInputEditingKey = fieldKey;
            this.pricingInputDrafts = {
                ...this.pricingInputDrafts,
                [fieldKey]: current,
            };
            this.storePricingFieldFocusSnapshot(fieldKey, current);
            this.captureListPriceFocusSnapshot();
        },
        onDiscountFieldDraft(index, value) {
            if (this.pricingSyncLock || this.shouldSkipPricingFieldEdit()) {
                return;
            }
            const fieldKey = `discounts.${index}`;
            this.pricingInputEditingKey = fieldKey;
            this.pricingInputDrafts = {
                ...this.pricingInputDrafts,
                [fieldKey]: value,
            };
        },
        onDiscountFieldBlur(index) {
            if (
                this.shouldSkipPricingFieldEdit()
                || this.pricingSyncLock
            ) {
                this.clearPricingInputDrafts();
                return;
            }
            const fieldKey = `discounts.${index}`;
            const draft = Object.prototype.hasOwnProperty.call(
                this.pricingInputDrafts,
                fieldKey,
            )
                ? this.pricingInputDrafts[fieldKey]
                : this.productBuilderForm.discounts?.[index];
            if (!this.hasPricingFieldFocusChanged(fieldKey, draft)) {
                this.clearPricingInputDrafts();
                return;
            }
            this.clearPricingInputDrafts();
            const discounts = [...(this.productBuilderForm.discounts || [])];
            discounts[index] = draft;
            this.productBuilderForm.discounts = discounts;
            this.runPricingFieldEdit("discounts", discounts, fieldKey);
        },
        onPricingResultFieldFocus(resultField) {
            if (resultField?.readonly) {
                return;
            }
            const fieldKey = resultField.key;
            const format = resultField.format || "";
            if (fieldKey === "listPriceRonIncVatFinal") {
                this.markupActualManual = true;
            }
            this.pricingResultEditingKey = fieldKey;
            const display = this.formatResultValue(fieldKey, format);
            this.pricingResultDrafts[fieldKey] = display;
            this.storePricingFieldFocusSnapshot(fieldKey, display);
            this.captureListPriceFocusSnapshot();
        },
        onPricingResultFieldDraft(resultField, value) {
            if (resultField?.readonly) {
                return;
            }
            if (this.pricingSyncLock || this.shouldSkipPricingFieldEdit()) {
                return;
            }
            this.pricingResultEditingKey = resultField.key;
            this.pricingResultDrafts[resultField.key] = value;
        },
        onPricingResultFieldBlur(resultField) {
            if (resultField?.readonly) {
                return;
            }
            const fieldKey = resultField.key;
            if (this.shouldSkipPricingFieldEdit()) {
                this.clearPricingResultDrafts();
                return;
            }
            const draft = Object.prototype.hasOwnProperty.call(
                this.pricingResultDrafts,
                fieldKey,
            )
                ? this.pricingResultDrafts[fieldKey]
                : this.formatResultValue(fieldKey, resultField.format);
            if (!this.hasPricingFieldFocusChanged(fieldKey, draft)) {
                this.clearPricingResultDrafts();
                return;
            }
            this.clearPricingResultDrafts();
            this.runPricingFieldEdit(fieldKey, draft);
        },
        syncComputedMarkupActual(product) {
            if (this.simulationMode) {
                return;
            }
            if (this.pricingSyncLock) {
                return;
            }
            // Skip while the user is typing adaos actual — draft owns the field.
            if (this.pricingInputEditingKey === "markupActual") {
                return;
            }
            // Formula mode: adaos drives listă. Do not write back the post-rounding
            // derived % — that drifts the value the user typed by ~0.0x.
            if (!this.productBuilderForm.overrideListPriceRonIncVatFinal) {
                return;
            }
            // No list price yet (e.g. create + only pret lista furnizor) — do not
            // invent adaos (−100% from listă 0 vs intrare).
            if (!(parseNumericInput(product?.listPriceRonIncVatFinal) > 0)) {
                return;
            }
            // Sticky list: (listă cu TVA − intrare cu TVA) / intrare cu TVA.
            if (!(parseNumericInput(product?.entryPriceRonIncVat) > 0)) {
                return;
            }
            const nextValue = ratioToPercentageInput(
                product?.computedMarkupActual,
            );
            const current = this.productBuilderForm.markupActual;
            // Numeric compare: avoid "146,78" vs "146.78" rewriting the field
            // and falsely lighting orange right after a clean open.
            if (
                String(current ?? "").trim() !== ""
                && parseNumericInput(nextValue) === parseNumericInput(current)
            ) {
                this.markupActualManual = false;
                return;
            }
            if (nextValue !== current) {
                this.productBuilderForm.markupActual = nextValue;
            }
            this.markupActualManual = false;
        },
        normalizeMeasurementUnit(unit) {
            const normalized = String(unit || "")
                .trim()
                .toLowerCase();
            return MEASUREMENT_UNIT_ALIASES[normalized] || unit;
        },
        addSystem() {
            this.productBuilderForm.systems.push("");
            if (!Array.isArray(this.productBuilderForm.systemSubcategories)) {
                this.productBuilderForm.systemSubcategories = [];
            }
            while (
                this.productBuilderForm.systemSubcategories.length <
                this.productBuilderForm.systems.length
            ) {
                this.productBuilderForm.systemSubcategories.push("");
            }
            this.systemPairMenusOpen.push(false);
        },
        removeSystem(index) {
            if (this.productBuilderForm.systems.length <= 1) {
                this.productBuilderForm.systems = [""];
                this.productBuilderForm.systemSubcategories = [""];
                this.systemPairMenusOpen = [false];
                return;
            }
            this.productBuilderForm.systems.splice(index, 1);
            if (Array.isArray(this.productBuilderForm.systemSubcategories)) {
                this.productBuilderForm.systemSubcategories.splice(index, 1);
            }
            this.systemPairMenusOpen.splice(index, 1);
        },
        async onSystemChange(index, value) {
            if (!Array.isArray(this.productBuilderForm.systemSubcategories)) {
                this.productBuilderForm.systemSubcategories = [];
            }
            while (
                this.productBuilderForm.systemSubcategories.length <
                this.productBuilderForm.systems.length
            ) {
                this.productBuilderForm.systemSubcategories.push("");
            }
            if (!value) {
                this.productBuilderForm.systemSubcategories[index] = "";
                return;
            }
            this.productBuilderForm.systems[index] =
                normalizeSystemValue(value);
            await this.loadSubcategoryOptionsForSystem(
                this.productBuilderForm.systems[index],
            );
            this.productBuilderForm.systemSubcategories[index] =
                getDefaultSystemSubcategory(
                    this.subcategoryOptionsForSystem(index),
                );
        },
        applyDefaultSubcategoriesForSystems() {
            this.productBuilderForm.systems.forEach((system, index) => {
                if (
                    !system ||
                    this.productBuilderForm.systemSubcategories[index]
                ) {
                    return;
                }
                this.productBuilderForm.systemSubcategories[index] =
                    getDefaultSystemSubcategory(
                        this.subcategoryOptionsForSystem(index),
                    );
            });
        },
        /**
         * Keep the current chapter/subchapter visible in autocomplete items when
         * system changes shrink the lookup list — otherwise Vuetify clears v-model.
         */
        withSelectedStructureOption(options, code, name) {
            const list = Array.isArray(options) ? options : [];
            const selectedCode = String(code || "").trim();
            if (!selectedCode) {
                return list;
            }
            if (list.some(option => String(option?.code || "") === selectedCode)) {
                return list;
            }
            const selectedName = String(name || "").trim();
            return [
                ...list,
                {
                    code: selectedCode,
                    name: selectedName,
                    title: selectedName
                        ? `${selectedCode} – ${selectedName}`
                        : selectedCode,
                },
            ];
        },
        addDiscount() {
            this.productBuilderForm.discounts.push("");
            if (!Array.isArray(this.productBuilderForm.discountNotes)) {
                this.productBuilderForm.discountNotes = [];
            }
            this.productBuilderForm.discountNotes.push("");
        },
        hasDiscountValueToClear(index) {
            const fieldKey = `discounts.${index}`;
            const display = this.pricingInputDisplayValue(fieldKey);
            return String(display ?? "").trim() !== "";
        },
        clearDiscountValue(index) {
            if (this.shouldSkipPricingFieldEdit() || this.pricingSyncLock) {
                return;
            }
            const fieldKey = `discounts.${index}`;
            if (!this.hasDiscountValueToClear(index)) {
                return;
            }
            this.clearPricingInputDrafts();
            const discounts = [...(this.productBuilderForm.discounts || [])];
            discounts[index] = "";
            this.productBuilderForm.discounts = discounts;
            this.runPricingFieldEdit("discounts", discounts, fieldKey);
        },
        removeDiscount(index) {
            if (this.productBuilderForm.discounts.length <= 1) {
                this.productBuilderForm.discounts = [];
                this.productBuilderForm.discountNotes = [];
            } else {
                this.productBuilderForm.discounts.splice(index, 1);
                if (Array.isArray(this.productBuilderForm.discountNotes)) {
                    this.productBuilderForm.discountNotes.splice(index, 1);
                }
            }
            this.applyDiscountPricingSideEffects();
        },
        addCostUplift() {
            if (!Array.isArray(this.productBuilderForm.costUplifts)) {
                this.productBuilderForm.costUplifts = [];
            }
            if (!Array.isArray(this.productBuilderForm.costUpliftNotes)) {
                this.productBuilderForm.costUpliftNotes = [];
            }
            if (!Array.isArray(this.productBuilderForm.costUpliftTypes)) {
                this.productBuilderForm.costUpliftTypes = [];
            }
            this.productBuilderForm.costUplifts.push("");
            this.productBuilderForm.costUpliftNotes.push("");
            this.productBuilderForm.costUpliftTypes.push("fixed");
        },
        hasCostUpliftValueToClear(index) {
            return (
                String(
                    this.productBuilderForm.costUplifts?.[index] ?? "",
                ).trim() !== ""
            );
        },
        clearCostUpliftValue(index) {
            if (this.shouldSkipPricingFieldEdit() || this.pricingSyncLock) {
                return;
            }
            if (!this.hasCostUpliftValueToClear(index)) {
                return;
            }
            if (!Array.isArray(this.productBuilderForm.costUplifts)) {
                this.productBuilderForm.costUplifts = [];
            }
            const next = [...this.productBuilderForm.costUplifts];
            next[index] = "";
            this.productBuilderForm.costUplifts = next;
            this.applyCostUpliftPricingSideEffects();
        },
        removeCostUplift(index) {
            if (!Array.isArray(this.productBuilderForm.costUplifts)) {
                this.productBuilderForm.costUplifts = [];
            }
            if (this.productBuilderForm.costUplifts.length <= 1) {
                this.productBuilderForm.costUplifts = [];
                this.productBuilderForm.costUpliftNotes = [];
                this.productBuilderForm.costUpliftTypes = [];
            } else {
                this.productBuilderForm.costUplifts.splice(index, 1);
                if (Array.isArray(this.productBuilderForm.costUpliftNotes)) {
                    this.productBuilderForm.costUpliftNotes.splice(index, 1);
                }
                if (Array.isArray(this.productBuilderForm.costUpliftTypes)) {
                    this.productBuilderForm.costUpliftTypes.splice(index, 1);
                }
            }
            this.applyCostUpliftPricingSideEffects();
        },
        onCostUpliftFieldBlur(index) {
            if (this.shouldSkipPricingFieldEdit() || this.pricingSyncLock) {
                return;
            }
            const raw = this.productBuilderForm.costUplifts?.[index];
            const trimmed = String(raw ?? "").trim();
            const next = [...(this.productBuilderForm.costUplifts || [])];
            if (!trimmed) {
                next[index] = "";
            } else {
                const numeric = parseNumericInput(trimmed);
                next[index] =
                    numeric > 0 ? formatNumericInputValue(numeric, 2) : "";
            }
            this.productBuilderForm.costUplifts = next;
            this.applyCostUpliftPricingSideEffects();
        },
        applyCostUpliftPricingSideEffects() {
            const form = this.productBuilderForm;
            const costUplifts = Array.isArray(form.costUplifts)
                ? [...form.costUplifts]
                : [];
            if (!Array.isArray(form.costUpliftTypes)) {
                form.costUpliftTypes = costUplifts.map(() => "fixed");
            }
            const result = applyPricingFieldEdit(
                form,
                "costUplifts",
                costUplifts,
                this.effectivePricingSettings,
                this.pricingFieldEditOptions(),
            );
            if (result.markupActualManual !== undefined) {
                this.markupActualManual = result.markupActualManual;
            }
            persistActiveFurnizorPricingFromRoot(form);
        },
        commitTransportCostBreakdown(nextBreakdown) {
            const normalized = normalizeTransportCostBreakdown(nextBreakdown);
            this.productBuilderForm.transportCostBreakdown = normalized;
            // Keep existing list price sticky across formula apply.
            this.captureListPriceFocusSnapshot();
            this.runPricingFieldEdit(
                "transportCostBreakdown",
                normalized,
                "transportCostRate",
            );
        },
        openTransportCostFormulaModal() {
            this.captureListPriceFocusSnapshot();
            const tabKey = this.activeFurnizorPricingKey || '';
            const existingBefore = String(
                this.transportCostRateBeforeFormulaByTab[tabKey] ?? '',
            ).trim();
            // Capture manual builder % only before entering/using the calculator.
            // While already in formula mode, keep the earlier snapshot so Resetează
            // → Aplică can restore the pre-calculator value (not the calculated %).
            if (!this.isTransportCostFormulaMode) {
                this.transportCostRateBeforeFormulaByTab = {
                    ...this.transportCostRateBeforeFormulaByTab,
                    [tabKey]: String(
                        this.productBuilderForm.transportCostRate ?? '',
                    ).trim(),
                };
            } else if (!existingBefore) {
                // Draft reopened with an active formula: no pre-calc snapshot —
                // use Valoare veche so Resetează does not wipe to 0% (−100%).
                this.transportCostRateBeforeFormulaByTab = {
                    ...this.transportCostRateBeforeFormulaByTab,
                    [tabKey]: String(
                        this.pricingPercentFieldHintOldValue('transportCostRate') ?? '',
                    ).trim(),
                };
            }
            this.transportCostFormulaModalOpen = true;
        },
        onTransportCostFormulaApply(breakdown) {
            const next = cloneTransportCostBreakdown(breakdown);
            const tabKey = this.activeFurnizorPricingKey || '';
            // Cleared calculator / exit formula: drop green formula styling.
            // - If % came from calculation → restore pre-calculator / Valoare veche
            //   (never keep the calculated %, never fall back to bare 0 when an
            //   old value exists — that shows −100% in the builder).
            // - If user typed % manually in builder → keep that value (do not wipe).
            // - New furnizor (Valoare veche 0): restore 0 without keeping prior-tab %.
            if (next.mode === TRANSPORT_COST_MODE_PERCENT) {
                next.mode = TRANSPORT_COST_MODE_PERCENT;
                next.rateIsManual = true;
                next.goodsValue = '';
                next.goodsQuantity = '';
                next.goodsDiscount = '';
                next.goodsValueExtra = '';
                next.goodsValueIsManual = false;
                const rateCameFromFormula = this.isTransportCostFormulaMode;
                const currentRate = String(
                    this.productBuilderForm.transportCostRate ?? '',
                ).trim();
                const beforeFormulaRate = String(
                    this.transportCostRateBeforeFormulaByTab[tabKey] ?? '',
                ).trim();
                const valorVecheRate = String(
                    this.pricingPercentFieldHintOldValue('transportCostRate') ?? '',
                ).trim();
                const lastSavedRate = String(
                    baselineValueForField(
                        'transportCostRate',
                        this.activePricingFieldLastSavedBaseline,
                    )
                    ?? '',
                ).trim();
                const referenceRate = String(
                    baselineValueForField(
                        'transportCostRate',
                        this.activePricingFieldReferenceBaseline,
                    )
                    ?? '',
                ).trim();
                const isNewFurnizorTab = Boolean(
                    tabKey
                    && (
                        this.newFurnizorKeysPendingFirstSave?.[tabKey]
                        || this.unpublishedNewFurnizorKeys?.[tabKey]
                    ),
                );
                // Leaving a calculated formula must not keep the computed %.
                // Prefer: value before calculator → Valoare veche / saved / reference → 0.
                // New furnizor: Valoare veche is 0 — do not keep a copied prior-tab %.
                const restoredRate = rateCameFromFormula
                    ? (
                        isNewFurnizorTab
                            ? (valorVecheRate || lastSavedRate || referenceRate || '0')
                            : (
                                beforeFormulaRate
                                || valorVecheRate
                                || lastSavedRate
                                || referenceRate
                                || '0'
                            )
                    )
                    : (currentRate || '0');
                // Set directly — avoid markPricingFieldManual on formula exit.
                this.productBuilderForm.transportCostBreakdown =
                    normalizeTransportCostBreakdown(next);
                this.productBuilderForm.transportCostEstimated = '';
                this.productBuilderForm.transportCostRate = restoredRate;
                this.transportCostRateBeforeFormulaByTab = {
                    ...this.transportCostRateBeforeFormulaByTab,
                    [tabKey]: restoredRate,
                };
                if (tabKey) {
                    const edited = {
                        ...(this.manuallyEditedPricingFields[tabKey] || {}),
                    };
                    delete edited.transportCostBreakdown;
                    // Only clear the rate edit flag when undoing a calculated %.
                    if (rateCameFromFormula) {
                        delete edited.transportCostRate;
                    }
                    this.manuallyEditedPricingFields = {
                        ...this.manuallyEditedPricingFields,
                        [tabKey]: edited,
                    };
                }
                persistActiveFurnizorPricingFromRoot(this.productBuilderForm);
                return;
            }
            next.mode = TRANSPORT_COST_MODE_FORMULA;
            next.rateIsManual = false;
            this.commitTransportCostBreakdown(next);
        },
        commitStampDutyBreakdown(nextBreakdown) {
            this.captureListPriceFocusSnapshot();
            const normalized = normalizeStampDutyBreakdown(nextBreakdown);
            this.productBuilderForm.stampDutyBreakdown = normalized;
            this.runPricingFieldEdit(
                "stampDutyBreakdown",
                normalized,
                "stampDutyAmount",
            );
        },
        openStampDutyFormulaModal() {
            this.captureListPriceFocusSnapshot();
            const tabKey = this.activeFurnizorPricingKey || '';
            if (!this.isStampDutyFormulaMode) {
                this.stampDutyAmountBeforeFormulaByTab = {
                    ...this.stampDutyAmountBeforeFormulaByTab,
                    [tabKey]: String(
                        this.productBuilderForm.stampDutyAmount ?? '',
                    ).trim() || '0',
                };
            } else if (
                !String(this.stampDutyAmountBeforeFormulaByTab[tabKey] ?? '').trim()
            ) {
                const saved = String(
                    baselineValueForField(
                        'stampDutyAmount',
                        this.activePricingFieldLastSavedBaseline,
                    ) ?? '',
                ).trim();
                this.stampDutyAmountBeforeFormulaByTab = {
                    ...this.stampDutyAmountBeforeFormulaByTab,
                    [tabKey]: saved || '0',
                };
            }
            this.stampDutyFormulaModalOpen = true;
        },
        onStampDutyFormulaApply(breakdown) {
            const next = cloneStampDutyBreakdown(breakdown);
            const tabKey = this.activeFurnizorPricingKey || '';
            if (next.mode === STAMP_DUTY_MODE_MANUAL) {
                next.mode = STAMP_DUTY_MODE_MANUAL;
                next.amountIsManual = true;
                const amountCameFromFormula = this.isStampDutyFormulaMode;
                const currentAmount = String(
                    this.productBuilderForm.stampDutyAmount ?? '',
                ).trim();
                const beforeFormulaAmount = String(
                    this.stampDutyAmountBeforeFormulaByTab[tabKey] ?? '',
                ).trim();
                const restoredAmount = amountCameFromFormula
                    ? (beforeFormulaAmount || '0')
                    : (currentAmount || '0');
                this.productBuilderForm.stampDutyBreakdown =
                    normalizeStampDutyBreakdown(next);
                this.productBuilderForm.stampDutyAmount = restoredAmount;
                this.stampDutyAmountBeforeFormulaByTab = {
                    ...this.stampDutyAmountBeforeFormulaByTab,
                    [tabKey]: restoredAmount,
                };
                if (tabKey) {
                    const edited = {
                        ...(this.manuallyEditedPricingFields[tabKey] || {}),
                    };
                    delete edited.stampDutyBreakdown;
                    if (amountCameFromFormula) {
                        delete edited.stampDutyAmount;
                    }
                    this.manuallyEditedPricingFields = {
                        ...this.manuallyEditedPricingFields,
                        [tabKey]: edited,
                    };
                }
                persistActiveFurnizorPricingFromRoot(this.productBuilderForm);
                return;
            }
            next.mode = STAMP_DUTY_MODE_FORMULA;
            next.amountIsManual = false;
            this.commitStampDutyBreakdown(next);
        },
        furnizorTabLabel(pricing) {
            if (pricing?.usesManufacturerAsFurnizor) {
                const manufacturerName = String(
                    this.productBuilderForm.manufacturerName || "",
                ).trim();
                return manufacturerName || "Producător";
            }
            const name = String(pricing?.furnizorName || "").trim();
            const code = String(pricing?.furnizorCode || "").trim();
            return name || code || "Furnizor";
        },
        /**
         * Manufacturers are mixed into the furnizor autocomplete (e.g. Poliplast
         * code 02). Selecting one without a Furnizor catalog id used to save a
         * blank tab after apply — ensure the catalog row exists first.
         */
        async onActiveFurnizorSelectCode(code) {
            this.activeFurnizorSelectCode = code;
            const active = this.activeFurnizorPricing;
            if (!active) {
                return;
            }
            const normalized = String(code || "").trim();
            if (!normalized) {
                return;
            }
            if (active.furnizorId != null) {
                return;
            }
            const name =
                String(active.furnizorName || "").trim() || normalized;
            const ensurePromise = (async () => {
                try {
                    const ensured = await createFurnizor({
                        code: normalized,
                        name,
                    });
                    this.furnizorOptions = getFurnizori();
                    const current = this.activeFurnizorPricing;
                    if (
                        !current ||
                        String(current.furnizorCode || "")
                            .trim()
                            .toLowerCase() !== normalized.toLowerCase()
                    ) {
                        return;
                    }
                    if (ensured?.id != null) {
                        current.furnizorId = ensured.id;
                        current.furnizorCode = ensured.code || normalized;
                        current.furnizorName = ensured.name || name;
                        current.usesManufacturerAsFurnizor = false;
                    }
                } catch (error) {
                    console.warn(
                        "Could not ensure furnizor catalog row for",
                        normalized,
                        error,
                    );
                }
            })();
            this.ensuringFurnizorPromise = ensurePromise;
            await ensurePromise;
            if (this.ensuringFurnizorPromise === ensurePromise) {
                this.ensuringFurnizorPromise = null;
            }
        },
        async waitForPendingFurnizorEnsure() {
            if (this.ensuringFurnizorPromise) {
                await this.ensuringFurnizorPromise;
            }
        },        onFurnizorTabChange(nextKey) {
            this.withFurnizorTabSwitch(() => {
                if (this.readOnly) {
                    // View-only: load tab into form without writing root back
                    // into the previous tab (avoids corrupting unchanged furnizori).
                    const next = (
                        this.productBuilderForm.furnizorPricings || []
                    ).find((row) => row.clientKey === nextKey);
                    if (next) {
                        applyFurnizorPricingToRoot(
                            this.productBuilderForm,
                            next,
                        );
                        this.syncMarkupActualManualFromForm();
                    }
                    return;
                }
                switchActiveFurnizorPricing(this.productBuilderForm, nextKey);
                this.syncMarkupActualManualFromForm();
            });
        },
        /**
         * Valoare veche baselines for a brand-new product or furnizor tab:
         * explicit 0s (not prices copied from another tab/product).
         */
        buildNewFurnizorZeroValoareVecheBaseline(tab) {
            const discounts = Array.isArray(tab?.discounts)
                ? tab.discounts.map(() => "0")
                : [];
            /** @type {Record<string, unknown>} */
            const snapshot = {
                supplierListPriceNew: "0",
                currency: tab?.currency ?? "ron",
                discounts,
                transportCostRate: "0",
                transportCostEstimated: "",
                transportCostBreakdown: createDefaultTransportCostBreakdown({
                    mode: TRANSPORT_COST_MODE_PERCENT,
                    rateIsManual: true,
                }),
                stampDutyAmount: "0",
                stampDutyCurrency: "ron",
                stampDutyBreakdown: createDefaultStampDutyBreakdown({
                    mode: STAMP_DUTY_MODE_MANUAL,
                    amountIsManual: true,
                }),
                markupReference: "0",
                markupActual: "0",
                overrideListPriceRonIncVatFinal: false,
                overrideListPriceRonIncVatFinalValue: "",
                useManualFinalPrice: false,
                manualListPriceRonIncVatFinal: "",
                priceAdjustmentRatio: "",
                listPriceRonIncVatReference: "0",
            };
            (this.resultFields || []).forEach((field) => {
                if (field?.key) {
                    snapshot[field.key] = "0";
                }
            });
            return snapshot;
        },
        /**
         * New product create: Valoare veche = 0 for every furnizor tab
         * (same as Adaugă furnizor). Open + session-start stay at 0 so the
         * first draft save does not replace hints with empty form baselines.
         */
        applyZeroValoareVecheBaselinesForNewProduct() {
            const pricings = this.productBuilderForm?.furnizorPricings || [];
            if (!pricings.length) {
                return;
            }
            const openBaselines = { ...this.pricingFieldBaselinesByTab };
            const lastSaved = { ...this.pricingFieldLastSavedBaselinesByTab };
            const reference = { ...this.pricingFieldReferenceBaselinesByTab };
            const sessionStart = {
                ...this.pricingFieldSessionStartBaselinesByTab,
            };
            const persistedOld = { ...this.supplierListPricePersistedOldByTab };
            pricings.forEach((tab) => {
                if (!tab?.clientKey) {
                    return;
                }
                const zeroHint = this.buildNewFurnizorZeroValoareVecheBaseline(tab);
                openBaselines[tab.clientKey] = cloneDeep(zeroHint);
                lastSaved[tab.clientKey] = cloneDeep(zeroHint);
                reference[tab.clientKey] = cloneDeep(zeroHint);
                sessionStart[tab.clientKey] = cloneDeep(zeroHint);
                persistedOld[tab.clientKey] = "0";
                this.supplierListPriceSessionBaselineByTab[tab.clientKey] = "0";
            });
            this.pricingFieldBaselinesByTab = openBaselines;
            this.pricingFieldLastSavedBaselinesByTab = lastSaved;
            this.pricingFieldReferenceBaselinesByTab = reference;
            this.pricingFieldSessionStartBaselinesByTab = sessionStart;
            this.supplierListPricePersistedOldByTab = persistedOld;
        },
        /**
         * First draft-save after adding a furnizor: Valoare veche for the next
         * reopen must be the values just saved — not the prices copied from the
         * previous tab at "Adaugă furnizor" (those live in session-start).
         */
        promoteNewFurnizorBaselinesToSavedValuesOnFirstDraftSave() {
            const pending = this.newFurnizorKeysPendingFirstSave || {};
            const pendingKeys = Object.keys(pending).filter((key) => pending[key]);
            if (!pendingKeys.length) {
                return;
            }
            persistActiveFurnizorPricingFromRoot(this.productBuilderForm);
            const pricings = this.productBuilderForm.furnizorPricings || [];
            const remaining = { ...pending };

            pendingKeys.forEach((tabKey) => {
                const tab = pricings.find((row) => row?.clientKey === tabKey);
                if (!tab) {
                    delete remaining[tabKey];
                    return;
                }
                const snapshot = buildPricingFieldBaselineSnapshot(
                    tab,
                    this.productBuilderForm,
                    this.effectivePricingSettings,
                    this.resultFields,
                );
                this.pricingFieldBaselinesByTab = {
                    ...this.pricingFieldBaselinesByTab,
                    [tabKey]: snapshot,
                };
                this.pricingFieldSessionStartBaselinesByTab = {
                    ...this.pricingFieldSessionStartBaselinesByTab,
                    [tabKey]: cloneDeep(snapshot),
                };
                this.pricingFieldLastSavedBaselinesByTab = {
                    ...this.pricingFieldLastSavedBaselinesByTab,
                    [tabKey]: cloneDeep(snapshot),
                };
                this.pricingFieldReferenceBaselinesByTab = {
                    ...this.pricingFieldReferenceBaselinesByTab,
                    [tabKey]: buildPricingFieldReferenceSnapshot(
                        tab,
                        this.productBuilderForm,
                        this.effectivePricingSettings,
                        this.resultFields,
                    ),
                };
                const savedPrice = String(tab.supplierListPriceNew ?? "").trim();
                this.supplierListPriceSessionBaselineByTab[tabKey] = savedPrice;
                this.supplierListPricePersistedOldByTab[tabKey] = savedPrice;
                delete remaining[tabKey];
            });
            this.newFurnizorKeysPendingFirstSave = remaining;
        },
        addFurnizorTab() {
            this.withFurnizorTabSwitch(() => {
                addFurnizorPricingTab(this.productBuilderForm);
                const newTab = this.activeFurnizorPricing;
                if (newTab) {
                    // Open-session baselines = copied prices (orange highlight),
                    // including inherited transport formula from the previous tab.
                    this.capturePricingFieldBaselineForTab(newTab);
                    // Valoare veche = 0 until this furnizor has a prior save.
                    const zeroHint = this.buildNewFurnizorZeroValoareVecheBaseline(
                        newTab,
                    );
                    this.pricingFieldLastSavedBaselinesByTab = {
                        ...this.pricingFieldLastSavedBaselinesByTab,
                        [newTab.clientKey]: cloneDeep(zeroHint),
                    };
                    this.pricingFieldReferenceBaselinesByTab = {
                        ...this.pricingFieldReferenceBaselinesByTab,
                        [newTab.clientKey]: cloneDeep(zeroHint),
                    };
                    this.pricingFieldSessionStartBaselinesByTab = {
                        ...this.pricingFieldSessionStartBaselinesByTab,
                        [newTab.clientKey]: cloneDeep(zeroHint),
                    };
                    const newPrice = String(newTab.supplierListPriceNew || "").trim();
                    this.supplierListPriceSessionBaselineByTab[newTab.clientKey] =
                        newPrice;
                    this.supplierListPricePersistedOldByTab[newTab.clientKey] = "0";
                    // Fresh Resetează snapshot for this tab (do not reuse previous tab %).
                    this.transportCostRateBeforeFormulaByTab = {
                        ...this.transportCostRateBeforeFormulaByTab,
                        [newTab.clientKey]: '',
                    };
                    this.stampDutyAmountBeforeFormulaByTab = {
                        ...this.stampDutyAmountBeforeFormulaByTab,
                        [newTab.clientKey]: '0',
                    };
                    this.newFurnizorKeysPendingFirstSave = {
                        ...this.newFurnizorKeysPendingFirstSave,
                        [newTab.clientKey]: true,
                    };
                    this.unpublishedNewFurnizorKeys = {
                        ...this.unpublishedNewFurnizorKeys,
                        [newTab.clientKey]: true,
                    };
                }
                this.syncMarkupActualManualFromForm();
            });
        },
        removeActiveFurnizorTab() {
            const active = this.activeFurnizorPricing;
            if (!active) {
                return;
            }
            const removedKey = active.clientKey;
            this.withFurnizorTabSwitch(() => {
                removeFurnizorPricingTab(
                    this.productBuilderForm,
                    removedKey,
                );
                if (removedKey) {
                    const nextBaselines = { ...this.pricingFieldBaselinesByTab };
                    delete nextBaselines[removedKey];
                    this.pricingFieldBaselinesByTab = nextBaselines;
                    const nextReferenceBaselines = {
                        ...this.pricingFieldReferenceBaselinesByTab,
                    };
                    delete nextReferenceBaselines[removedKey];
                    this.pricingFieldReferenceBaselinesByTab =
                        nextReferenceBaselines;
                    const nextLastSavedBaselines = {
                        ...this.pricingFieldLastSavedBaselinesByTab,
                    };
                    delete nextLastSavedBaselines[removedKey];
                    this.pricingFieldLastSavedBaselinesByTab =
                        nextLastSavedBaselines;
                    const nextSessionStartBaselines = {
                        ...this.pricingFieldSessionStartBaselinesByTab,
                    };
                    delete nextSessionStartBaselines[removedKey];
                    this.pricingFieldSessionStartBaselinesByTab =
                        nextSessionStartBaselines;
                    if (this.transportCostRateBeforeFormulaByTab?.[removedKey] !== undefined) {
                        const nextBeforeFormula = {
                            ...this.transportCostRateBeforeFormulaByTab,
                        };
                        delete nextBeforeFormula[removedKey];
                        this.transportCostRateBeforeFormulaByTab = nextBeforeFormula;
                    }
                    if (this.stampDutyAmountBeforeFormulaByTab?.[removedKey] !== undefined) {
                        const nextStampBefore = {
                            ...this.stampDutyAmountBeforeFormulaByTab,
                        };
                        delete nextStampBefore[removedKey];
                        this.stampDutyAmountBeforeFormulaByTab = nextStampBefore;
                    }
                    if (this.newFurnizorKeysPendingFirstSave?.[removedKey]) {
                        const nextPending = {
                            ...this.newFurnizorKeysPendingFirstSave,
                        };
                        delete nextPending[removedKey];
                        this.newFurnizorKeysPendingFirstSave = nextPending;
                    }
                    if (this.unpublishedNewFurnizorKeys?.[removedKey]) {
                        const nextUnpublished = {
                            ...this.unpublishedNewFurnizorKeys,
                        };
                        delete nextUnpublished[removedKey];
                        this.unpublishedNewFurnizorKeys = nextUnpublished;
                    }
                }
                this.syncMarkupActualManualFromForm();
            });
        },
        markActiveFurnizorDefault() {
            const active = this.activeFurnizorPricing;
            if (!active) {
                return;
            }
            setDefaultFurnizorPricing(
                this.productBuilderForm,
                active.clientKey,
            );
        },
        openCreateManufacturerDialog() {
            this.createManufacturerIsEdit = false;
            this.editingManufacturerId = null;
            this.newManufacturerCode = suggestNextCatalogCode(
                this.manufacturerOptions,
            );
            this.newManufacturerName = "";
            this.createManufacturerDialogOpen = true;
            this.$nextTick(() => {
                this.$refs.createManufacturerForm?.resetValidation?.();
            });
        },
        openEditManufacturerDialog() {
            const selected = this.manufacturerOptions.find(
                row => row.id === this.selectedManufacturerId,
            );
            if (!selected?.id) {
                showAlertModal(
                    this.$store,
                    "Selectați un producător pentru a-l edita.",
                    "warning",
                    6000,
                );
                return;
            }
            this.createManufacturerIsEdit = true;
            this.editingManufacturerId = selected.id;
            this.newManufacturerCode = selected.code || "";
            this.newManufacturerName = selected.name || "";
            this.createManufacturerDialogOpen = true;
            this.$nextTick(() => {
                this.$refs.createManufacturerForm?.resetValidation?.();
            });
        },
        closeCreateManufacturerDialog() {
            if (this.creatingManufacturer) {
                return;
            }
            this.createManufacturerDialogOpen = false;
            this.createManufacturerIsEdit = false;
            this.editingManufacturerId = null;
            this.newManufacturerCode = "";
            this.newManufacturerName = "";
        },
        async submitCreateManufacturer() {
            if (!this.canSubmitManufacturerCreate) {
                return;
            }
            const code = String(this.newManufacturerCode || "").trim();
            const name = String(this.newManufacturerName || "").trim();
            this.creatingManufacturer = true;
            try {
                if (this.createManufacturerIsEdit) {
                    const updated = await updateManufacturer({
                        id: this.editingManufacturerId,
                        code,
                        name,
                    });
                    this.manufacturerOptions = getManufacturerCodes();
                    this.manufacturerSyncLock = "code";
                    this.productBuilderForm.manufacturerCode =
                        updated.code || "";
                    this.productBuilderForm.manufacturerName =
                        updated.name || "";
                    this.$nextTick(() => {
                        this.manufacturerSyncLock = null;
                    });
                    this.createManufacturerDialogOpen = false;
                    this.createManufacturerIsEdit = false;
                    this.editingManufacturerId = null;
                    this.newManufacturerCode = "";
                    this.newManufacturerName = "";
                    showAlertModal(
                        this.$store,
                        `Producătorul ${updated.name} a fost actualizat.`,
                        "success",
                        5000,
                    );
                } else {
                    const created = await createManufacturer({ code, name });
                    this.manufacturerOptions = getManufacturerCodes();
                    this.manufacturerSyncLock = "code";
                    this.productBuilderForm.manufacturerCode =
                        created.code || "";
                    this.productBuilderForm.manufacturerName =
                        created.name || "";
                    this.$nextTick(() => {
                        this.manufacturerSyncLock = null;
                    });
                    this.createManufacturerDialogOpen = false;
                    this.newManufacturerCode = "";
                    this.newManufacturerName = "";
                    showAlertModal(
                        this.$store,
                        `Producătorul ${created.name} a fost adăugat.`,
                        "success",
                        5000,
                    );
                }
            } catch (error) {
                const detail =
                    error?.response?.data?.detail ||
                    error?.message ||
                    (this.createManufacturerIsEdit
                        ? "Nu s-a putut actualiza producătorul."
                        : "Nu s-a putut crea producătorul.");
                showAlertModal(this.$store, detail, "danger", 8000);
            } finally {
                this.creatingManufacturer = false;
            }
        },
        openCreateSupplierDialog() {
            this.activeUsesManufacturerAsFurnizor = false;
            this.createSupplierIsEdit = false;
            this.editingSupplierId = null;
            this.newSupplierCode = suggestNextCatalogCode(
                this.allFurnizorCatalogCodes,
            );
            this.newSupplierName = "";
            this.createSupplierDialogOpen = true;
            this.$nextTick(() => {
                this.$refs.createSupplierForm?.resetValidation?.();
            });
        },
        openEditSupplierDialog() {
            const selected = this.furnizorOptions.find(
                row => row.id === this.activeFurnizorId,
            );
            if (!selected?.id) {
                showAlertModal(
                    this.$store,
                    "Selectați un furnizor pentru a-l edita.",
                    "warning",
                    6000,
                );
                return;
            }
            this.createSupplierIsEdit = true;
            this.editingSupplierId = selected.id;
            this.newSupplierCode = selected.code || "";
            this.newSupplierName = selected.name || "";
            this.createSupplierDialogOpen = true;
            this.$nextTick(() => {
                this.$refs.createSupplierForm?.resetValidation?.();
            });
        },
        syncFurnizorPricingsAfterCatalogChange(furnizor) {
            if (!furnizor?.id) {
                return;
            }
            (this.productBuilderForm.furnizorPricings || []).forEach(
                pricing => {
                    if (pricing?.furnizorId === furnizor.id) {
                        pricing.furnizorCode = furnizor.code || "";
                        pricing.furnizorName = furnizor.name || "";
                    }
                },
            );
        },
        closeCreateSupplierDialog() {
            if (this.creatingSupplier) {
                return;
            }
            this.createSupplierDialogOpen = false;
            this.createSupplierIsEdit = false;
            this.editingSupplierId = null;
            this.newSupplierCode = "";
            this.newSupplierName = "";
        },
        async submitCreateSupplier() {
            if (!this.canSubmitSupplierCreate) {
                return;
            }
            const code = String(this.newSupplierCode || "").trim();
            const name = String(this.newSupplierName || "").trim();
            this.creatingSupplier = true;
            try {
                if (this.createSupplierIsEdit) {
                    const updated = await updateFurnizor({
                        id: this.editingSupplierId,
                        code,
                        name,
                    });
                    this.furnizorOptions = getFurnizori();
                    this.syncFurnizorPricingsAfterCatalogChange(updated);
                    this.activeFurnizorId = updated.id;
                    this.createSupplierDialogOpen = false;
                    this.createSupplierIsEdit = false;
                    this.editingSupplierId = null;
                    this.newSupplierCode = "";
                    this.newSupplierName = "";
                    showAlertModal(
                        this.$store,
                        `Furnizorul ${
                            updated.title || updated.name
                        } a fost actualizat.`,
                        "success",
                        5000,
                    );
                } else {
                    const created = await createFurnizor({ code, name });
                    this.furnizorOptions = getFurnizori();
                    this.activeFurnizorId = created.id;
                    this.createSupplierDialogOpen = false;
                    this.newSupplierCode = "";
                    this.newSupplierName = "";
                    showAlertModal(
                        this.$store,
                        `Furnizorul ${
                            created.title || created.name
                        } a fost adăugat.`,
                        "success",
                        5000,
                    );
                }
            } catch (error) {
                const detail =
                    error?.response?.data?.detail ||
                    error?.message ||
                    (this.createSupplierIsEdit
                        ? "Nu s-a putut actualiza furnizorul."
                        : "Nu s-a putut crea furnizorul.");
                showAlertModal(this.$store, detail, "danger", 8000);
            } finally {
                this.creatingSupplier = false;
            }
        },
        async initializeForm() {
            this.structureAutoFillLock = true;
            this.localEditItem = null;
            this.sessionSourceProductCode = "";
            this.sessionCopyBaselineCode = "";
            this.sessionCopyOriginCode = "";
            this.existingProductUpdateConfirmOpen = false;
            this.confirmedSaveAsExistingProductUpdate = false;
            this.supplierListPriceSessionBaselineByTab = {};
            this.supplierListPricePersistedOldByTab = {};
            this.newFurnizorKeysPendingFirstSave = {};
            this.unpublishedNewFurnizorKeys = {};
            this.transportCostRateBeforeFormulaByTab = {};
            this.stampDutyAmountBeforeFormulaByTab = {};
            this.listPriceRonIncVatSessionBaseline = 0;
            this.listPriceRonIncVatPersistedReference = "";
            this.listPriceComparisonBaselineFrozen = false;
            this.pricingFieldBaselinesByTab = {};
            this.pricingFieldReferenceBaselinesByTab = {};
            this.pricingFieldLastSavedBaselinesByTab = {};
            this.pricingFieldSessionStartBaselinesByTab = {};
            this.pricingFieldFocusSnapshot = null;
            this.listPriceIncVatFocusSnapshot = null;
            this.propagateSellingPriceDialogOpen = false;
            this.pendingPropagateSellingPrice = null;
            this.propagateFurnizorFieldsDialogOpen = false;
            this.pendingFurnizorPropagate = null;
            this.clearAllManualPricingFields();
            this.$refs.productCodeSuggest?.reset?.();
            this.productCodeAutofill = "";

            const needsDetailFetch = Boolean(
                !this.isDraftEditMode &&
                    this.editItem?.id &&
                    !this.editItem.pricing_detail &&
                    !this.editItem.has_pricing_detail,
            );
            if (needsDetailFetch) {
                this.formLoading = true;
            }

            try {
                if (needsDetailFetch) {
                    try {
                        const fresh = await this.fetchItemDetail(
                            this.editItem.id,
                        );
                        if (fresh) {
                            this.localEditItem = {
                                ...this.editItem,
                                ...fresh,
                                categories: this.editItem.categories?.length
                                    ? [...this.editItem.categories]
                                    : getProductMainCategories(fresh),
                            };
                        }
                    } catch (error) {
                        console.error(
                            "Failed to load product detail:",
                            error,
                        );
                    }
                }

                await ensureProductLookupsLoaded();
                this.manufacturerOptions = getManufacturerCodes();
                this.furnizorOptions = getFurnizori();
                if (this.simulationMode && this.simulationForm) {
                    // Official form and simulation form MUST share the same clientKeys
                    // (built together in the compare dialog) so baselines match tabs.
                    const officialForm = this.simulationOfficialForm
                        ? cloneDeep(this.simulationOfficialForm)
                        : null;
                    if (officialForm) {
                        this.productBuilderForm = officialForm;
                        // Keep raw official values as simulation baselines — do not
                        // re-derive markupActual (rounding drift vs stored API %).
                        this.captureSessionPricingFieldBaselines();
                        this.pricingFieldLastSavedBaselinesByTab = cloneDeep(
                            this.pricingFieldBaselinesByTab,
                        );
                        this.pricingFieldSessionStartBaselinesByTab = cloneDeep(
                            this.pricingFieldBaselinesByTab,
                        );
                        this.pricingFieldReferenceBaselinesByTab =
                            buildAllPricingFieldReferenceBaselines(
                                this.productBuilderForm,
                                this.effectivePricingSettings,
                                this.resultFields,
                            );
                    }

                    const officialBaselines = cloneDeep(
                        this.pricingFieldBaselinesByTab || {},
                    );
                    const officialLastSaved = cloneDeep(
                        this.pricingFieldLastSavedBaselinesByTab || {},
                    );
                    const officialSessionStart = cloneDeep(
                        this.pricingFieldSessionStartBaselinesByTab || {},
                    );
                    const officialReference = cloneDeep(
                        this.pricingFieldReferenceBaselinesByTab || {},
                    );

                    this.productBuilderForm = cloneDeep(this.simulationForm);
                    syncDiscountsListToLegacy(this.productBuilderForm);
                    persistActiveFurnizorPricingFromRoot(this.productBuilderForm);
                    this.syncMarkupActualManualFromForm();
                    this.preparePriceComparisonBaselines();
                    // Re-apply official baselines after load so changed fields stay orange.
                    this.pricingFieldBaselinesByTab = officialBaselines;
                    this.pricingFieldLastSavedBaselinesByTab = officialLastSaved;
                    this.pricingFieldSessionStartBaselinesByTab =
                        officialSessionStart;
                    this.pricingFieldReferenceBaselinesByTab = officialReference;
                    this._simulationOfficialBaselines = officialBaselines;
                    this._simulationOfficialLastSaved = officialLastSaved;
                    this._simulationOfficialSessionStart = officialSessionStart;
                    await this.loadSubcategoryOptionsForSystems(
                        this.productBuilderForm.systems,
                    );
                    this.applyDefaultSubcategoriesForSystems();
                    this.syncSystemPairMenusOpen();
                    this.captureSessionSourceProductCode(
                        this.simulationForm.overrideProductCodeValue
                            || this.editItem?.product_code
                            || "",
                    );
                    this.$nextTick(() => {
                        this.structureAutoFillLock = false;
                        this.activateInitialFurnizorTab();
                        // One more restore after tab switch / watchers settle.
                        this.pricingFieldBaselinesByTab = cloneDeep(officialBaselines);
                        this.pricingFieldLastSavedBaselinesByTab = cloneDeep(
                            officialLastSaved,
                        );
                        this.pricingFieldSessionStartBaselinesByTab = cloneDeep(
                            officialSessionStart,
                        );
                    });
                    return;
                }
                if (this.isDraftEditMode) {
                    const draftBuilderState =
                        this.draftEntry.builderState || {};
                    const { productBuilderForm } = draftBuilderState;
                    this.productBuilderForm = cloneDeep(productBuilderForm);
                    this.pricingFieldBaselinesByTab = cloneDeep(
                        draftBuilderState.pricingFieldBaselinesByTab || {},
                    );
                    this.pricingFieldReferenceBaselinesByTab = cloneDeep(
                        draftBuilderState.pricingFieldReferenceBaselinesByTab ||
                            {},
                    );
                    this.pricingFieldLastSavedBaselinesByTab = cloneDeep(
                        draftBuilderState.pricingFieldLastSavedBaselinesByTab ||
                            draftBuilderState.pricingFieldBaselinesByTab ||
                            {},
                    );
                    this.pricingFieldSessionStartBaselinesByTab = cloneDeep(
                        draftBuilderState.pricingFieldSessionStartBaselinesByTab
                            || {},
                    );
                    this.manuallyEditedPricingFields = cloneDeep(
                        draftBuilderState.manuallyEditedPricingFields || {},
                    );
                    this.newFurnizorKeysPendingFirstSave = {
                        ...(draftBuilderState.newFurnizorKeysPendingFirstSave || {}),
                    };
                    this.unpublishedNewFurnizorKeys = {
                        ...(draftBuilderState.unpublishedNewFurnizorKeys || {}),
                    };
                    if (
                        typeof draftBuilderState.markupActualManual ===
                        "boolean"
                    ) {
                        this.markupActualManual =
                            draftBuilderState.markupActualManual;
                    } else {
                        this.syncMarkupActualManualFromForm();
                    }
                    syncDiscountsListToLegacy(this.productBuilderForm);
                    persistActiveFurnizorPricingFromRoot(
                        this.productBuilderForm,
                    );
                    this.productBuilderForm.serialNumber = Boolean(
                        this.productBuilderForm.serialNumber,
                    );
                    this.restorePriceComparisonSessionFromSavedForm();
                    // Drop resurrected catalog supplier-old from older drafts so
                    // untouched "Preț listă furnizor" does not show a false %.
                    this.normalizeSupplierPricesForDraftBuilderUi();
                    if (
                        !Array.isArray(this.productBuilderForm.furnizorPricings) ||
                        !this.productBuilderForm.furnizorPricings.length
                    ) {
                        persistActiveFurnizorPricingFromRoot(
                            this.productBuilderForm,
                        );
                    }
                    if (
                        !Array.isArray(
                            this.productBuilderForm.componentProductCodes,
                        )
                    ) {
                        this.productBuilderForm.componentProductCodes = [];
                    }
                    if (
                        !Array.isArray(
                            this.productBuilderForm.componentProductsKeepInList,
                        )
                    ) {
                        this.productBuilderForm.componentProductsKeepInList = [
                            ...this.productBuilderForm.componentProductCodes,
                        ];
                    }
                    if (this.productBuilderForm.isCompositeProduct) {
                        this.lockSistemaManufacturer();
                    }
                    await this.loadSubcategoryOptionsForSystems(
                        this.productBuilderForm.systems,
                    );
                    this.applyDefaultSubcategoriesForSystems();
                    this.syncSystemPairMenusOpen();
                    this.captureSessionSourceProductCode();
                    this.clearStaleProductCodeOverride();
                    this.$nextTick(() => {
                        this.structureAutoFillLock = false;
                    });
                    return;
                }
                const editItem = this.effectiveEditItem;
                if (this.isPersistedEditMode) {
                    const { productBuilderForm } = pricingDetailToBuilderForm(
                        editItem.pricing_detail,
                        editItem,
                    );
                    this.productBuilderForm = productBuilderForm;
                    this.preparePriceComparisonBaselines();
                    if (!this.listPriceComparisonBaselineFrozen) {
                        const fallbackReference =
                            findReferencePriceForProduct(
                                this.existingProducts,
                                editItem.product_code,
                            ) || String(editItem.price || "");
                        if (fallbackReference) {
                            this.productBuilderForm.listPriceRonIncVatReference =
                                fallbackReference;
                            this.freezeListPriceComparisonBaseline();
                        }
                    }
                    if (!this.productBuilderForm.priceListIds?.length) {
                        await this.loadEditPriceLists();
                    }
                    await this.loadSubcategoryOptionsForSystems(
                        this.productBuilderForm.systems,
                    );
                    this.applyDefaultSubcategoriesForSystems();
                    this.syncSystemPairMenusOpen();
                    this.syncMarkupActualManualFromForm();
                    this.productBuilderForm.overrideProductCode = true;
                    this.productBuilderForm.overrideProductCodeValue = String(
                        editItem.product_code || "",
                    ).trim();
                    this.captureSessionSourceProductCode(editItem.product_code);
                    this.$nextTick(() => {
                        this.structureAutoFillLock = false;
                    });
                    return;
                }
                if (this.isLegacyUpgradeMode) {
                    const { productBuilderForm } = legacyProductToBuilderForm(
                        editItem,
                        editItem.categories || [],
                    );
                    this.productBuilderForm = productBuilderForm;
                    this.preparePriceComparisonBaselines();
                    await this.loadEditPriceLists();
                    await this.loadSubcategoryOptionsForSystems(
                        this.productBuilderForm.systems,
                    );
                    this.applyDefaultSubcategoriesForSystems();
                    this.syncSystemPairMenusOpen();
                    this.syncMarkupActualManualFromForm();
                    this.captureSessionSourceProductCode(editItem.product_code);
                    this.$nextTick(() => {
                        this.structureAutoFillLock = false;
                    });
                    return;
                }
                this.resetForm();
                this.$nextTick(() => {
                    this.structureAutoFillLock = false;
                });
            } finally {
                normalizePricingFormNumericDisplay(this.productBuilderForm);
                persistActiveFurnizorPricingFromRoot(this.productBuilderForm);
                this.syncMarkupActualManualFromForm();
                // Draft reopen: keep restored open baselines so orange edits remain.
                // Live product open: always treat saved values as the clean baseline
                // for every furnizor tab (incl. newly added ones after publish).
                if (this.isDraftEditMode) {
                    if (!Object.keys(this.pricingFieldBaselinesByTab).length) {
                        this.captureSessionPricingFieldBaselines();
                    } else if (
                        !Object.keys(this.pricingFieldSessionStartBaselinesByTab)
                            .length
                    ) {
                        this.captureSessionStartPricingFieldBaselines();
                    }
                    if (
                        !Object.keys(this.pricingFieldLastSavedBaselinesByTab)
                            .length
                    ) {
                        this.pricingFieldLastSavedBaselinesByTab = cloneDeep(
                            this.pricingFieldSessionStartBaselinesByTab ||
                                this.pricingFieldBaselinesByTab,
                        );
                    }
                    if (
                        !Object.keys(this.pricingFieldReferenceBaselinesByTab)
                            .length
                    ) {
                        this.pricingFieldReferenceBaselinesByTab =
                            buildAllPricingFieldReferenceBaselines(
                                this.productBuilderForm,
                                this.effectivePricingSettings,
                                this.resultFields,
                            );
                    }
                } else {
                    this.captureCleanOpenPricingBaselines();
                    // Brand-new product (not edit/copy of existing): Valoare veche = 0.
                    if (!this.isExistingProductEdit && !this.simulationMode) {
                        this.applyZeroValoareVecheBaselinesForNewProduct();
                    }
                }
                this.activateCompareFurnizorTab();
                this.activateInitialFurnizorTab();
                this.loadCompareReferenceBaselines();
                if (this.statusLockedToDeVerificat) {
                    this.productBuilderForm.status = 'de_verificat';
                }
                this.formLoading = false;
            }
        },
        async fetchItemDetail(itemId) {
            const params = {};
            if (this.selectedPriceList != null && this.selectedPriceList !== "") {
                params.price_list_id = this.selectedPriceList;
            }
            const response = await apiClient.get(`/api/items/${itemId}/`, {
                params,
            });
            return response.data;
        },
        onCompositeProductToggle(enabled) {
            if (enabled) {
                this.compoundComponentsTouched = false;
                this.lockSistemaManufacturer();
                if (
                    !Array.isArray(
                        this.productBuilderForm.componentProductCodes,
                    )
                ) {
                    this.productBuilderForm.componentProductCodes = [];
                }
                if (
                    !Array.isArray(
                        this.productBuilderForm.componentProductsKeepInList,
                    )
                ) {
                    this.productBuilderForm.componentProductsKeepInList = [];
                }
                this.onCompoundComponentsChange(
                    this.productBuilderForm.componentProductCodes,
                );
                this.openCompoundPickerDialog();
                return;
            }
            this.compoundProductSearch = "";
            this.compoundPickerDialogOpen = false;
            this.compoundDraftCodes = [];
            this.compoundDraftKeepInList = [];
            this.compoundComponentsTouched = false;
            this.productBuilderForm.componentProductCodes = [];
            this.productBuilderForm.componentProductsKeepInList = [];
            this.productBuilderForm.overrideListPriceRonIncVatFinal = false;
            this.productBuilderForm.overrideListPriceRonIncVatFinalValue = "";
        },
        openCompoundPickerDialog() {
            const selectedCodes = (
                this.productBuilderForm.componentProductCodes || []
            )
                .map(code => String(code).trim())
                .filter(Boolean);
            const keepInList = (
                this.productBuilderForm.componentProductsKeepInList || []
            )
                .map(code => String(code).trim())
                .filter(Boolean);
            this.compoundPickerSyncLock = true;
            this.compoundDraftKeepInList = keepInList.length
                ? keepInList.filter(code => selectedCodes.includes(code))
                : [...selectedCodes];
            this.compoundDraftCodes = [...selectedCodes];
            this.compoundProductSearch = "";
            this.compoundPickerPage = 1;
            this.compoundPickerItemsPerPage = 10;
            this.compoundPickerDialogOpen = true;
            this.$nextTick(() => {
                this.compoundPickerSyncLock = false;
            });
        },
        closeCompoundPickerDialog() {
            this.compoundPickerDialogOpen = false;
            this.compoundProductSearch = "";
            this.compoundPickerSyncLock = true;
            this.compoundDraftCodes = [];
            this.compoundDraftKeepInList = [];
            this.$nextTick(() => {
                this.compoundPickerSyncLock = false;
            });
        },
        confirmCompoundPickerDialog() {
            if (this.compoundDraftCodes.length < 2) {
                return;
            }
            this.onCompoundComponentsChange(
                [...this.compoundDraftCodes],
                [...this.compoundDraftKeepInList],
            );
            this.compoundComponentsTouched = false;
            this.compoundPickerDialogOpen = false;
            this.compoundProductSearch = "";
            this.compoundPickerSyncLock = true;
            this.compoundDraftCodes = [];
            this.compoundDraftKeepInList = [];
            this.$nextTick(() => {
                this.compoundPickerSyncLock = false;
            });
        },
        isCompoundDraftKeptInList(productCode) {
            return this.compoundDraftKeepInListSet.has(
                String(productCode || "").trim(),
            );
        },
        setCompoundDraftKeepInList(productCode, checked) {
            const code = String(productCode || "").trim();
            if (!code || !this.compoundDraftSelectedSet.has(code)) {
                return;
            }
            const keepSet = new Set(this.compoundDraftKeepInListSet);
            if (checked) {
                keepSet.add(code);
            } else {
                keepSet.delete(code);
            }
            this.compoundDraftKeepInList = [...keepSet];
        },
        isComponentKeptInList(productCode) {
            const code = String(productCode || "").trim();
            if (!code) {
                return false;
            }
            const selectedCodes = (
                this.productBuilderForm.componentProductCodes || []
            )
                .map(entry => String(entry).trim())
                .filter(Boolean);
            if (!selectedCodes.includes(code)) {
                return false;
            }
            const keepInList =
                this.productBuilderForm.componentProductsKeepInList;
            if (!Array.isArray(keepInList)) {
                return true;
            }
            return keepInList.map(entry => String(entry).trim()).includes(code);
        },
        compoundPickerRowProps({ item }) {
            if (!this.compoundDraftSelectedSet.has(item.value)) {
                return {};
            }
            return {
                class: "compound-picker-row--selected",
            };
        },
        onCompoundComponentsChange(codes, keepInListCodes = null) {
            if (!this.productBuilderForm.isCompositeProduct) {
                return;
            }
            this.lockSistemaManufacturer();
            const selectedCodes = (Array.isArray(codes) ? codes : [])
                .map(code => String(code).trim())
                .filter(Boolean);
            this.productBuilderForm.componentProductCodes = selectedCodes;
            const resolvedKeepInList = Array.isArray(keepInListCodes)
                ? keepInListCodes
                      .map(code => String(code).trim())
                      .filter(code => selectedCodes.includes(code))
                : selectedCodes;
            this.productBuilderForm.componentProductsKeepInList =
                resolvedKeepInList;
            const selectedProducts = (this.existingProducts || []).filter(
                product =>
                    selectedCodes.includes(
                        String(product.product_code || "").trim(),
                    ),
            );
            if (selectedProducts.length < 2) {
                return;
            }
            this.applyCompoundFromProducts(selectedProducts);
        },
        lockSistemaManufacturer() {
            this.manufacturerSyncLock = "compound";
            this.productBuilderForm.manufacturerCode =
                SISTEMA_MANUFACTURER_CODE;
            this.productBuilderForm.manufacturerName =
                SISTEMA_MANUFACTURER_NAME;
            this.$nextTick(() => {
                this.manufacturerSyncLock = null;
            });
        },
        applyCompoundFromProducts(products) {
            const seed = buildCompoundProductSeed(products);
            if (!seed) {
                return;
            }

            const price = parseNumericInput(seed.price);
            const priceText = price > 0 ? formatNumericInputValue(price, 2) : "";

            if (!String(this.productBuilderForm.productName || "").trim()) {
                this.productBuilderForm.productName = seed.productName || "";
            }
            if (!this.productBuilderForm.measurementUnit) {
                this.productBuilderForm.measurementUnit =
                    seed.measurementUnit || "set";
            }

            this.productBuilderForm.currency = "ron";
            this.productBuilderForm.supplierListPriceNew = priceText;
            this.productBuilderForm.markupReference =
                this.productBuilderForm.markupReference || "0";
            this.productBuilderForm.listPriceRonIncVatReference = priceText;
            const active = getActiveFurnizorPricing(this.productBuilderForm);
            if (active) {
                active.usesManufacturerAsFurnizor = true;
                active.furnizorId = null;
                active.furnizorCode = "";
                active.furnizorName = "";
            }
            persistActiveFurnizorPricingFromRoot(this.productBuilderForm);
        },
        async loadSubcategoryOptionsForSystem(system) {
            const normalizedSystem = normalizeSystemValue(system);
            if (
                !normalizedSystem ||
                this.subcategoryOptionsBySystem[normalizedSystem]
            ) {
                return;
            }
            const options = await fetchSubcategoriesForSystem(normalizedSystem);
            this.subcategoryOptionsBySystem = {
                ...this.subcategoryOptionsBySystem,
                [normalizedSystem]: options,
            };
        },
        async loadSubcategoryOptionsForSystems(systems = []) {
            const uniqueSystems = [
                ...new Set(
                    systems
                        .map(system => normalizeSystemValue(system))
                        .filter(Boolean),
                ),
            ];
            await Promise.all(
                uniqueSystems.map(system =>
                    this.loadSubcategoryOptionsForSystem(system),
                ),
            );
        },
        async loadEditPriceLists() {
            if (!this.effectiveEditItem?.id) {
                return;
            }
            try {
                const response = await apiClient.get(
                    `/api/items/${this.effectiveEditItem.id}/price-lists/`,
                );
                const allLists = response.data.price_lists || [];
                // Default to the list the user is viewing — not every list the product
                // belongs to. Saving all lists overwrote each list's price and logged
                // spurious list_price changes in Istoric on older lists.
                if (this.selectedPriceList != null && this.selectedPriceList !== "") {
                    const selected = allLists.filter(
                        (priceList) =>
                            (priceList?.id ?? priceList) === this.selectedPriceList,
                    );
                    this.productBuilderForm.priceListIds = selected.length
                        ? selected
                        : allLists;
                } else {
                    this.productBuilderForm.priceListIds = allLists;
                }
            } catch (error) {
                console.error("Error loading edit price lists:", error);
            }
        },
        resetForm() {
            this.productBuilderForm = createDefaultProductBuilderForm();
            this.supplierListPriceSessionBaselineByTab = {};
            this.supplierListPricePersistedOldByTab = {};
            this.newFurnizorKeysPendingFirstSave = {};
            this.unpublishedNewFurnizorKeys = {};
            this.transportCostRateBeforeFormulaByTab = {};
            this.stampDutyAmountBeforeFormulaByTab = {};
            this.listPriceRonIncVatSessionBaseline = 0;
            this.listPriceRonIncVatPersistedReference = "";
            this.listPriceComparisonBaselineFrozen = false;
            this.clearPricingResultDrafts();
            this.clearPricingInputDrafts();
            this.pricingFieldBaselinesByTab = {};
            this.pricingFieldReferenceBaselinesByTab = {};
            this.pricingFieldLastSavedBaselinesByTab = {};
            this.pricingFieldSessionStartBaselinesByTab = {};
            this.clearCompareReferenceState();
            this.pricingFieldFocusSnapshot = null;
            this.listPriceIncVatFocusSnapshot = null;
            this.propagateSellingPriceDialogOpen = false;
            this.pendingPropagateSellingPrice = null;
            this.propagateFurnizorFieldsDialogOpen = false;
            this.pendingFurnizorPropagate = null;
            this.clearAllManualPricingFields();
            this.systemPairMenusOpen = [false];
            this.manufacturerSyncLock = null;
            this.markupActualManual = false;
            this.compoundProductSearch = "";
            this.compoundPickerDialogOpen = false;
            this.compoundDraftCodes = [];
            this.compoundDraftKeepInList = [];
            this.compoundComponentsTouched = false;
            const selectedList = this.priceLists.find(
                priceList => priceList.id === this.selectedPriceList,
            );
            if (selectedList) {
                this.productBuilderForm.priceListIds = [
                    JSON.parse(JSON.stringify(selectedList)),
                ];
            }
            if (this.statusLockedToDeVerificat) {
                this.productBuilderForm.status = 'de_verificat';
            }
        },
        onProductCodeSuggestSelect(code) {
            this.productCodeAutofill = code;
            this.applyProductCodeAutofill({ showInvalidWarning: false });
        },
        onProductCodeAutofillBlur() {
            this.applyProductCodeAutofill({ showInvalidWarning: true });
        },
        findExistingProductByCode(productCode) {
            const code = String(productCode || "").trim();
            if (!code) {
                return null;
            }
            return (
                (this.existingProducts || []).find(
                    product =>
                        String(product.product_code || "").trim() === code,
                ) || null
            );
        },
        applyProductDetailsFromExisting(product) {
            if (!product) {
                return;
            }
            const pricingDetail = product.pricing_detail || {};
            this.productBuilderForm.productName =
                product.product_name || product.name || "";
            this.productBuilderForm.manufacturerProductName = String(
                pricingDetail.manufacturerProductName ||
                    product.manufacturer_product_name ||
                    "",
            );
            this.productBuilderForm.manufacturerProductCode = String(
                pricingDetail.manufacturerProductCode ||
                    product.manufacturer_product_code ||
                    "",
            );
            const measurementUnit = this.normalizeMeasurementUnit(
                product.measurement_unit,
            );
            if (measurementUnit) {
                this.productBuilderForm.measurementUnit = measurementUnit;
            }
            this.productBuilderForm.nomenclatureCode = String(
                pricingDetail.nomenclatureCode || "",
            );
            this.productBuilderForm.visibleInOfferApp =
                product.visible_in_offer_app ??
                pricingDetail.visibleInOfferApp ??
                true;
        },
        applyProductCodeAutofill({ showInvalidWarning = true } = {}) {
            const code = resolveProductCodeSuggestValue(
                this.productCodeAutofill,
            );
            if (!code) {
                return;
            }
            if (code !== this.productCodeAutofill) {
                this.productCodeAutofill = code;
            }
            const parsed = parseProductCodeStructure(code);
            if (!parsed) {
                if (showInvalidWarning) {
                    showAlertModal(
                        this.$store,
                        "Cod produs invalid. Folosiți formatul „capitol+subcapitol.nr progresiv.cod producător” (ex: 101.02.51).",
                        "warning",
                        8000,
                    );
                }
                return;
            }
            this.structureAutoFillLock = true;
            this.productBuilderForm.chapterCode = parsed.chapterCode;
            this.productBuilderForm.subchapterCode = parsed.subchapterCode;
            this.productBuilderForm.progressiveNumber =
                parsed.progressiveNumber;

            const manufacturer = findManufacturerByCode(
                this.manufacturerOptions,
                parsed.manufacturerCode,
            );
            if (manufacturer) {
                this.productBuilderForm.manufacturerCode = manufacturer.code;
                this.productBuilderForm.manufacturerName = manufacturer.name;
            } else if (parsed.manufacturerCode) {
                this.productBuilderForm.manufacturerCode =
                    parsed.manufacturerCode;
            }

            if (this.selectedSystems.length) {
                const chapterName = getChapterNameForSystems(
                    this.selectedSystems,
                    parsed.chapterCode,
                );
                if (chapterName) {
                    this.productBuilderForm.chapterName = chapterName;
                }
                const subchapterName = getSubchapterNameForSystems(
                    this.selectedSystems,
                    parsed.chapterCode,
                    parsed.subchapterCode,
                );
                if (subchapterName) {
                    this.productBuilderForm.subchapterName = subchapterName;
                }
            }

            const existingProduct = this.findExistingProductByCode(code);
            if (existingProduct) {
                this.applyProductDetailsFromExisting(existingProduct);
            }

            this.$nextTick(() => {
                this.structureAutoFillLock = false;
            });
        },
        onChapterCodeChange(chapterCode) {
            if (this.structureAutoFillLock) {
                return;
            }
            const chapterName = getChapterNameForSystems(
                this.selectedSystems,
                chapterCode,
            );
            if (chapterName) {
                this.productBuilderForm.chapterName = chapterName;
            }
            if (this.productBuilderForm.subchapterCode) {
                const subchapterName = getSubchapterNameForSystems(
                    this.selectedSystems,
                    chapterCode,
                    this.productBuilderForm.subchapterCode,
                );
                this.productBuilderForm.subchapterName =
                    subchapterName || this.productBuilderForm.subchapterName;
            }
            this.maybeSuggestProgressiveNumber(true);
        },
        onSubchapterCodeChange(subchapterCode) {
            if (this.structureAutoFillLock) {
                return;
            }
            const normalizedCode = padSubchapterCode(subchapterCode);
            if (normalizedCode && normalizedCode !== subchapterCode) {
                this.productBuilderForm.subchapterCode = normalizedCode;
                return;
            }
            const subchapterName = getSubchapterNameForSystems(
                this.selectedSystems,
                this.productBuilderForm.chapterCode,
                subchapterCode,
            );
            if (subchapterName) {
                this.productBuilderForm.subchapterName = subchapterName;
            }
            this.maybeSuggestProgressiveNumber(true);
        },
        openStructureCreateDialog(mode) {
            if (mode === "subchapter") {
                if (
                    !this.primarySystem ||
                    !this.productBuilderForm.chapterCode
                ) {
                    return;
                }
                this.structureCreateMode = "subchapter";
                this.structureCreateIsEdit = false;
                this.structureCreateOriginalCode = "";
                this.structureCreateForm = {
                    systems: this.primarySystem ? [this.primarySystem] : [],
                    code: "",
                    name: "",
                };
            } else {
                const selectedSystems = systemsArrayFromForm(
                    this.productBuilderForm,
                );
                this.structureCreateMode = "chapter";
                this.structureCreateIsEdit = false;
                this.structureCreateOriginalCode = "";
                this.structureCreateForm = {
                    systems: selectedSystems.length
                        ? [...selectedSystems]
                        : this.primarySystem
                        ? [this.primarySystem]
                        : [],
                    code: "",
                    name: "",
                };
            }
            this.structureCreateDialogOpen = true;
        },
        openStructureEditDialog(mode) {
            if (mode === "subchapter") {
                const chapterCode = String(
                    this.productBuilderForm.chapterCode || "",
                ).trim();
                const subchapterCode = padSubchapterCode(
                    this.productBuilderForm.subchapterCode,
                );
                if (!chapterCode || !subchapterCode) {
                    return;
                }
                const existing = findSubchapterByCode(
                    chapterCode,
                    subchapterCode,
                );
                if (!existing) {
                    showAlertModal(
                        this.$store,
                        "Subcapitolul selectat nu a fost găsit în lookup-uri.",
                        "danger",
                        6000,
                    );
                    return;
                }
                this.structureCreateMode = "subchapter";
                this.structureCreateIsEdit = true;
                this.structureCreateOriginalCode = existing.code;
                this.structureCreateForm = {
                    systems: [],
                    code: existing.code,
                    name:
                        existing.name ||
                        this.productBuilderForm.subchapterName ||
                        "",
                };
            } else {
                const chapterCode = String(
                    this.productBuilderForm.chapterCode || "",
                ).trim();
                if (!chapterCode) {
                    return;
                }
                const existing = findChapterByCode(chapterCode);
                if (!existing) {
                    showAlertModal(
                        this.$store,
                        "Capitolul selectat nu a fost găsit în lookup-uri.",
                        "danger",
                        6000,
                    );
                    return;
                }
                this.structureCreateMode = "chapter";
                this.structureCreateIsEdit = true;
                this.structureCreateOriginalCode = existing.code;
                this.structureCreateForm = {
                    systems: [...(existing.systems || [])],
                    code: existing.code,
                    name:
                        existing.name ||
                        this.productBuilderForm.chapterName ||
                        "",
                };
            }
            this.structureCreateDialogOpen = true;
        },
        closeStructureCreateDialog() {
            if (this.structureCreateSaving) {
                return;
            }
            this.structureCreateDialogOpen = false;
            this.structureCreateIsEdit = false;
            this.structureCreateOriginalCode = "";
            this.structureCreateForm = { systems: [], code: "", name: "" };
        },
        onStructureCreateDialogToggle(isOpen) {
            if (!isOpen) {
                this.closeStructureCreateDialog();
            }
        },
        structureCreateErrorMessage(error, fallback) {
            const detail = error?.response?.data?.detail;
            if (typeof detail === "string" && detail.trim()) {
                return detail;
            }
            if (Array.isArray(detail) && detail.length) {
                return detail.join(" ");
            }
            return fallback;
        },
        async submitStructureCreateDialog() {
            if (!this.canSubmitStructureCreate) {
                return;
            }
            const code = String(this.structureCreateForm.code || "").trim();
            const name = String(this.structureCreateForm.name || "").trim();
            if (!code || !name) {
                return;
            }

            this.structureCreateSaving = true;
            try {
                if (this.structureCreateMode === "subchapter") {
                    const system = this.primarySystem;
                    const chapterCode = String(
                        this.productBuilderForm.chapterCode || "",
                    ).trim();
                    if (!chapterCode) {
                        return;
                    }
                    let saved;
                    if (this.structureCreateIsEdit) {
                        saved = await updateProductSubchapter({
                            chapterCode,
                            originalCode: this.structureCreateOriginalCode,
                            code,
                            name,
                        });
                    } else {
                        if (!system) {
                            return;
                        }
                        saved = await createProductSubchapter({
                            system,
                            chapterCode,
                            code,
                            name,
                        });
                    }
                    this.structureOptionsVersion += 1;
                    const savedCode = padSubchapterCode(saved?.code || code);
                    this.productBuilderForm.subchapterCode = savedCode;
                    this.onSubchapterCodeChange(savedCode);
                    showAlertModal(
                        this.$store,
                        this.structureCreateIsEdit
                            ? "Subcapitolul a fost actualizat."
                            : "Subcapitolul a fost adăugat.",
                        "success",
                        4000,
                    );
                } else {
                    const systems = (this.structureCreateForm.systems || [])
                        .map(
                            value =>
                                normalizeSystemValue(value) ||
                                String(value || "").trim(),
                        )
                        .filter(Boolean);
                    if (!systems.length) {
                        return;
                    }
                    let saved;
                    if (this.structureCreateIsEdit) {
                        saved = await updateProductChapter({
                            originalCode: this.structureCreateOriginalCode,
                            systems,
                            code,
                            name,
                        });
                    } else {
                        saved = await createProductChapter({
                            systems,
                            code,
                            name,
                        });
                    }
                    this.structureOptionsVersion += 1;
                    const savedCode = String(saved?.code || code).trim();
                    const previousChapterCode = String(
                        this.productBuilderForm.chapterCode || "",
                    ).trim();
                    this.productBuilderForm.chapterCode = savedCode;
                    this.productBuilderForm.chapterName = saved?.name || name;
                    if (
                        !this.structureCreateIsEdit ||
                        previousChapterCode !== savedCode
                    ) {
                        this.productBuilderForm.subchapterCode = "";
                        this.productBuilderForm.subchapterName = "";
                    } else {
                        const subchapterName = getSubchapterNameForSystems(
                            this.selectedSystems,
                            savedCode,
                            this.productBuilderForm.subchapterCode,
                        );
                        if (subchapterName) {
                            this.productBuilderForm.subchapterName =
                                subchapterName;
                        }
                    }
                    this.maybeSuggestProgressiveNumber(true);
                    showAlertModal(
                        this.$store,
                        this.structureCreateIsEdit
                            ? "Capitolul a fost actualizat."
                            : "Capitolul a fost adăugat.",
                        "success",
                        4000,
                    );
                }
                this.structureCreateDialogOpen = false;
                this.structureCreateIsEdit = false;
                this.structureCreateOriginalCode = "";
                this.structureCreateForm = { systems: [], code: "", name: "" };
            } catch (error) {
                showAlertModal(
                    this.$store,
                    this.structureCreateErrorMessage(
                        error,
                        this.structureCreateMode === "subchapter"
                            ? this.structureCreateIsEdit
                                ? "Nu s-a putut actualiza subcapitolul."
                                : "Nu s-a putut adăuga subcapitolul."
                            : this.structureCreateIsEdit
                            ? "Nu s-a putut actualiza capitolul."
                            : "Nu s-a putut adăuga capitolul.",
                    ),
                    "danger",
                    8000,
                    error,
                );
            } finally {
                this.structureCreateSaving = false;
            }
        },
        maybeSuggestProgressiveNumber(force = false) {
            if (this.structureAutoFillLock) {
                return;
            }
            if (!force && this.productBuilderForm.progressiveNumber) {
                return;
            }
            const suggested = suggestNextProgressiveNumber(
                this.existingProductCodes,
                this.productBuilderForm.chapterCode,
                this.productBuilderForm.subchapterCode,
            );
            if (suggested) {
                this.productBuilderForm.progressiveNumber = suggested;
            }
        },
        captureSessionSourceProductCode(explicitCode = "") {
            const normalizedExplicit = String(explicitCode || "").trim();
            if (normalizedExplicit) {
                this.sessionCopyBaselineCode = normalizedExplicit;
                this.sessionCopyOriginCode = "";
                this.sessionSourceProductCode = normalizedExplicit;
                return;
            }
            if (this.isDraftEditMode) {
                const draftBuilderState = this.draftEntry?.builderState || {};
                const draftProductCode = String(
                    this.draftEntry?.payload?.product_code
                        || this.draftEntry?.summary?.product_code
                        || "",
                ).trim();
                this.sessionCopyBaselineCode = String(
                    draftBuilderState.sessionCopyBaselineCode
                        || draftProductCode
                        || "",
                ).trim();
                const originCode = String(
                    this.draftEntry?.sourceProductCode
                        || draftBuilderState.sessionCopyOriginCode
                        || "",
                ).trim();
                this.sessionCopyOriginCode =
                    originCode
                    && originCode !== this.sessionCopyBaselineCode
                        ? originCode
                        : "";
                this.sessionSourceProductCode = String(
                    draftBuilderState.sessionSourceProductCode
                        || this.sessionCopyOriginCode
                        || this.sessionCopyBaselineCode
                        || "",
                ).trim();
                return;
            }
            if (this.effectiveEditItem?.product_code) {
                const code = String(this.effectiveEditItem.product_code).trim();
                this.sessionCopyBaselineCode = code;
                this.sessionCopyOriginCode = "";
                this.sessionSourceProductCode = code;
                return;
            }
            this.sessionCopyBaselineCode = "";
            this.sessionCopyOriginCode = "";
            this.sessionSourceProductCode = "";
        },
        syncCopyParentOnStructureChange() {
            if (this.structureAutoFillLock) {
                return;
            }
            const baseline = String(this.sessionCopyBaselineCode || "").trim();
            if (!baseline) {
                return;
            }
            if (this.productBuilderForm.overrideProductCode) {
                return;
            }
            this.sessionSourceProductCode = baseline;
        },
        isInPlaceProductCodeUpdateForCode(productCode) {
            const code = String(productCode ?? "").trim();
            if (!code) {
                return false;
            }
            if (this.isDraftEditMode && !this.isStructureChangedProductCopy) {
                return (
                    String(this.draftEntry?.payload?.product_code || "").trim()
                    === code
                );
            }
            if (
                this.isExistingProductEdit
                && !this.isDraftEditMode
                && !this.isStructureChangedProductCopy
            ) {
                return (
                    String(this.effectiveEditItem?.product_code || "").trim()
                    === code
                );
            }
            return false;
        },
        alertDuplicateLiveProductCode() {
            showAlertModal(
                this.$store,
                "Codul produsului există deja în listă.",
                "warning",
                8000,
            );
        },
        clearStaleProductCodeOverride() {
            if (!this.productBuilderForm.overrideProductCode) {
                return;
            }
            const overrideCode = String(
                this.productBuilderForm.overrideProductCodeValue || "",
            ).trim();
            if (!overrideCode) {
                return;
            }
            const builtCode = calculateProductPricing(
                builderFormToCalculatorInput({
                    ...this.productBuilderForm,
                    overrideProductCode: false,
                    overrideProductCodeValue: "",
                }),
                this.effectivePricingSettings,
            ).productCode;
            if (builtCode && builtCode !== overrideCode) {
                this.productBuilderForm.overrideProductCode = false;
                this.productBuilderForm.overrideProductCodeValue = "";
                this.pruneExtraFurnizorPricingsForProductCopy();
            }
        },
        clearLegacyProductCodeOverride() {
            if (this.structureAutoFillLock) {
                return;
            }
            if (
                !this.isLegacyUpgradeMode
                && !this.isPersistedEditMode
                && !this.isDraftEditMode
            ) {
                return;
            }
            if (this.productBuilderForm.overrideProductCode) {
                this.productBuilderForm.overrideProductCode = false;
                this.productBuilderForm.overrideProductCodeValue = "";
                this.pruneExtraFurnizorPricingsForProductCopy();
            }
        },
        /**
         * When copying via structure/code change, keep only the default
         * (or active) supplier — do not carry over extra furnizor tabs.
         */
        pruneExtraFurnizorPricingsForProductCopy() {
            const form = this.productBuilderForm;
            const pricings = Array.isArray(form.furnizorPricings)
                ? form.furnizorPricings
                : [];
            if (pricings.length <= 1) {
                return;
            }
            persistActiveFurnizorPricingFromRoot(form);
            const activeKey = String(form.activeFurnizorPricingKey || "").trim();
            const keep =
                getDefaultFurnizorPricing(form)
                || pricings.find((row) => row.clientKey === activeKey)
                || pricings[0];
            if (!keep) {
                return;
            }
            const keepKey = keep.clientKey;
            const removedKeys = pricings
                .map((row) => row.clientKey)
                .filter((key) => key && key !== keepKey);
            keep.isDefault = true;
            form.furnizorPricings = [keep];
            form.activeFurnizorPricingKey = keepKey;
            applyFurnizorPricingToRoot(form, keep);
            this.syncMarkupActualManualFromForm();
            if (!removedKeys.length) {
                return;
            }
            const stripTabKeys = (map) => {
                if (!map || typeof map !== "object") {
                    return map;
                }
                const next = { ...map };
                removedKeys.forEach((key) => {
                    delete next[key];
                });
                return next;
            };
            this.pricingFieldBaselinesByTab = stripTabKeys(
                this.pricingFieldBaselinesByTab,
            );
            this.pricingFieldReferenceBaselinesByTab = stripTabKeys(
                this.pricingFieldReferenceBaselinesByTab,
            );
            this.pricingFieldLastSavedBaselinesByTab = stripTabKeys(
                this.pricingFieldLastSavedBaselinesByTab,
            );
            this.pricingFieldSessionStartBaselinesByTab = stripTabKeys(
                this.pricingFieldSessionStartBaselinesByTab,
            );
            this.supplierListPriceSessionBaselineByTab = stripTabKeys(
                this.supplierListPriceSessionBaselineByTab,
            );
            this.supplierListPricePersistedOldByTab = stripTabKeys(
                this.supplierListPricePersistedOldByTab,
            );
        },
        onManufacturerNameChange(manufacturerName) {
            if (this.manufacturerSyncLock) {
                return;
            }
            const normalized = String(manufacturerName ?? "").trim();
            if (!normalized) {
                return;
            }
            const manufacturer = (this.manufacturerOptions || []).find(
                row =>
                    String(row.name).trim().toLowerCase()
                    === normalized.toLowerCase(),
            );
            if (!manufacturer) {
                return;
            }
            this.manufacturerSyncLock = "name";
            this.productBuilderForm.manufacturerCode = manufacturer.code;
            this.$nextTick(() => {
                this.manufacturerSyncLock = null;
            });
        },
        onManufacturerCodeChange(manufacturerCode) {
            if (this.manufacturerSyncLock) {
                return;
            }
            const manufacturer = findManufacturerByCode(
                this.manufacturerOptions,
                manufacturerCode,
            );
            if (!manufacturer) {
                return;
            }
            this.manufacturerSyncLock = "code";
            this.productBuilderForm.manufacturerName = manufacturer.name;
            this.$nextTick(() => {
                this.manufacturerSyncLock = null;
            });
        },
        onDialogToggle(value) {
            this.$emit("update:modelValue", value);
        },
        close() {
            this.$emit("update:modelValue", false);
        },
        discountsChangedFromBaseline() {
            const discounts = this.productBuilderForm.discounts || [];
            const baseline = this.activePricingFieldBaseline;
            const baselineDiscounts = Array.isArray(baseline?.discounts)
                ? baseline.discounts
                : [];
            if (discounts.length !== baselineDiscounts.length) {
                return true;
            }
            return discounts.some(
                (value, index) =>
                    !pricingFieldValuesEqual(
                        `discounts.${index}`,
                        value,
                        baselineDiscounts[index],
                    ),
            );
        },
        hasManualListPriceEdit() {
            return (
                this.isPricingFieldManual("listPriceRonIncVatFinal") ||
                this.isPricingFieldManual("listPriceRonExVat") ||
                this.isPricingFieldManual("listPriceEuroExVat")
            );
        },
        applyDiscountPricingSideEffects() {
            const form = this.productBuilderForm;
            const discounts = Array.isArray(form.discounts)
                ? [...form.discounts]
                : [];
            syncDiscountsListToLegacy(form);
            const result = applyPricingFieldEdit(
                form,
                "discounts",
                discounts,
                this.effectivePricingSettings,
                this.pricingFieldEditOptions(),
            );
            if (result.markupActualManual !== undefined) {
                this.markupActualManual = result.markupActualManual;
            }
            persistActiveFurnizorPricingFromRoot(form);
        },
        flushPendingResultFieldEdit() {
            const fieldKey = this.pricingResultEditingKey;
            if (!fieldKey) {
                return false;
            }
            if (
                !this.pricingFieldFocusSnapshot
                || this.pricingFieldFocusSnapshot.key
                    !== this.pricingFieldFocusStorageKey(fieldKey)
            ) {
                this.clearPricingResultDrafts();
                return false;
            }
            const draft = Object.prototype.hasOwnProperty.call(
                this.pricingResultDrafts,
                fieldKey,
            )
                ? this.pricingResultDrafts[fieldKey]
                : this.formatResultValue(fieldKey);
            const changed = this.hasPricingFieldFocusChanged(fieldKey, draft);
            this.clearPricingResultDrafts();
            if (!changed) {
                return false;
            }
            const result = applyPricingFieldEdit(
                this.productBuilderForm,
                fieldKey,
                draft,
                this.effectivePricingSettings,
                this.pricingFieldEditOptions(),
            );
            if (result.markupActualManual !== undefined) {
                this.markupActualManual = result.markupActualManual;
            }
            this.markPricingFieldManual(fieldKey);
            this.syncManualPricingFieldDependencies(fieldKey, fieldKey);
            persistActiveFurnizorPricingFromRoot(this.productBuilderForm);
            return true;
        },
        flushPendingPricingInputEdit() {
            const fieldKey = this.pricingInputEditingKey;
            if (!fieldKey) {
                return false;
            }
            if (
                !this.pricingFieldFocusSnapshot
                || this.pricingFieldFocusSnapshot.key
                    !== this.pricingFieldFocusStorageKey(fieldKey)
            ) {
                this.clearPricingInputDrafts();
                return false;
            }
            const draft = Object.prototype.hasOwnProperty.call(
                this.pricingInputDrafts,
                fieldKey,
            )
                ? this.pricingInputDrafts[fieldKey]
                : this.pricingInputDisplayValue(fieldKey);
            const changed = this.hasPricingFieldFocusChanged(fieldKey, draft);
            this.clearPricingInputDrafts();
            if (!changed) {
                return false;
            }
            if (String(fieldKey).startsWith("discounts.")) {
                const index = Number(String(fieldKey).split(".")[1]);
                const discounts = [...(this.productBuilderForm.discounts || [])];
                discounts[index] = draft;
                this.productBuilderForm.discounts = discounts;
                this.runPricingFieldEdit("discounts", discounts, fieldKey);
            } else {
                this.productBuilderForm[fieldKey] = draft;
                this.runPricingFieldEdit(fieldKey, draft);
            }
            return true;
        },
        finalizePricingFormForSave() {
            const flushedInput = this.flushPendingPricingInputEdit();
            const flushedResult = this.flushPendingResultFieldEdit();
            const form = this.productBuilderForm;
            syncDiscountsListToLegacy(form);
            if (
                this.discountsChangedFromBaseline() &&
                !this.hasManualListPriceEdit() &&
                !flushedResult &&
                !flushedInput
            ) {
                this.applyDiscountPricingSideEffects();
            }
            if (!this.pricingSyncLock) {
                this.syncComputedMarkupActual(this.computedProduct);
            }
            normalizePricingFormNumericDisplay(form);
            persistActiveFurnizorPricingFromRoot(form);
        },
        buildSavePayload({ forDraftSave = false } = {}) {
            this.finalizePricingFormForSave();
            const priceListIds = Array.isArray(
                this.productBuilderForm.priceListIds,
            )
                ? this.productBuilderForm.priceListIds
                : [];
            // Use the raw form arrays so system/subcategory indexes stay aligned.
            // Filtering empty systems first would shift subcategories and drop categories.
            const formSystems = Array.isArray(this.productBuilderForm.systems)
                ? this.productBuilderForm.systems
                : systemsArrayFromForm(this.productBuilderForm);
            const formSubcategories = Array.isArray(
                this.productBuilderForm.systemSubcategories,
            )
                ? this.productBuilderForm.systemSubcategories
                : [];
            const categoryAssignments = buildCategoryAssignmentsFromSystems(
                formSystems,
                formSubcategories,
            );
            const categoriesFromSystems = categoryAssignments.length
                ? categoryAssignments.map(
                      assignment => assignment.main_category,
                  )
                : syncCategoriesFromSystems(
                      systemsArrayFromForm(this.productBuilderForm),
                      [],
                  );

            let oldProductCode = null;
            if (this.isExistingProductEdit && !this.isDraftEditMode) {
                const useStructureCopyLogic =
                    forDraftSave || this.isLegacyUpgradeMode;
                if (useStructureCopyLogic) {
                    // Keep the original product when structure is unchanged; otherwise create a copy.
                    if (this.productBuilderForm.overrideProductCode) {
                        oldProductCode = this.effectiveEditItem.product_code;
                    }
                } else {
                    oldProductCode = this.effectiveEditItem.product_code;
                }
            } else if (
                this.isDraftEditMode &&
                this.draftEntry.sourceProductCode
            ) {
                if (forDraftSave) {
                    if (this.productBuilderForm.overrideProductCode) {
                        oldProductCode = this.draftEntry.sourceProductCode;
                    }
                } else {
                    oldProductCode = this.draftEntry.sourceProductCode;
                }
            }

            if (
                forDraftSave &&
                this.confirmedSaveAsExistingProductUpdate
            ) {
                const code = String(this.computedProduct?.productCode || "").trim();
                if (code && this.existingProductCodes.includes(code)) {
                    oldProductCode = code;
                }
            }

            const formForSave = {
                ...this.productBuilderForm,
                measurementUnit: this.normalizeMeasurementUnit(
                    this.productBuilderForm.measurementUnit,
                ),
            };
            syncSystemsListToLegacy(formForSave);
            this.applyPriceBaselinesForSave(formForSave);
            // Keep live form in sync so draft builderState matches saved baselines.
            this.productBuilderForm.supplierListPriceOld =
                formForSave.supplierListPriceOld;
            this.productBuilderForm.supplierListPriceNew =
                formForSave.supplierListPriceNew;
            this.productBuilderForm.listPriceRonIncVatReference =
                formForSave.listPriceRonIncVatReference;
            persistActiveFurnizorPricingFromRoot(this.productBuilderForm);

            const payload = builderFormToSavePayload({
                form: formForSave,
                settings: this.effectivePricingSettings,
                computedProduct: this.computedProduct,
                defaultComputedProduct: this.defaultComputedProduct,
                categories: categoriesFromSystems,
                categoryAssignments,
                priceListIds,
                oldProductCode,
            });

            if (
                !payload.old_product_code &&
                this.existingProductCodes.includes(payload.product_code) &&
                this.isInPlaceProductCodeUpdateForCode(payload.product_code)
            ) {
                payload.old_product_code = payload.product_code;
            }

            if (this.productBuilderForm.isCompositeProduct) {
                const selectedCodes = (
                    this.productBuilderForm.componentProductCodes || []
                )
                    .map(code => String(code).trim())
                    .filter(Boolean);
                const keepInList = new Set(
                    (this.productBuilderForm.componentProductsKeepInList || [])
                        .map(code => String(code).trim())
                        .filter(Boolean),
                );
                payload.compositeComponentRemovalCodes = selectedCodes.filter(
                    code => !keepInList.has(code),
                );
            }

            return payload;
        },
        sessionBaselineListPriceRonIncVatFinal() {
            return (
                this.activePricingFieldSessionStartBaseline
                    ?.listPriceRonIncVatFinal ?? ""
            );
        },
        shouldAskPropagateSellingPriceOnDraftSave() {
            return shouldOfferPropagateSellingPriceToOtherFurnizors(
                this.productBuilderForm,
                this.computedProduct?.listPriceRonIncVatFinal,
                this.sessionBaselineListPriceRonIncVatFinal(),
                this.effectivePricingSettings,
            );
        },
        markListPriceManualForFurnizorTabs(clientKeys = []) {
            if (!clientKeys.length) {
                return;
            }
            const next = { ...this.manuallyEditedPricingFields };
            const listPriceFields = [
                "listPriceRonIncVatFinal",
                "listPriceRonExVat",
                "listPriceEuroExVat",
            ];
            clientKeys.forEach(tabKey => {
                if (!tabKey) {
                    return;
                }
                const tabEdits = { ...(next[tabKey] || {}) };
                listPriceFields.forEach(fieldKey => {
                    tabEdits[fieldKey] = true;
                });
                next[tabKey] = tabEdits;
            });
            this.manuallyEditedPricingFields = next;
        },
        applyActiveSellingPriceToOtherFurnizors() {
            const listPrice = this.computedProduct?.listPriceRonIncVatFinal;
            const updatedKeys = applySellingPriceToOtherFurnizorPricings(
                this.productBuilderForm,
                listPrice,
                this.effectivePricingSettings,
            );
            this.markListPriceManualForFurnizorTabs(updatedKeys);
            this.markupActualManual = true;
        },
        cancelPropagateSellingPriceDialog() {
            this.propagateSellingPriceDialogOpen = false;
            this.pendingPropagateSellingPrice = null;
            this.confirmedSaveAsExistingProductUpdate = false;
            this.propagateFurnizorFieldsDialogOpen = false;
            this.pendingFurnizorPropagate = null;
        },
        resolvePropagateSellingPriceDialog(applyToOthers) {
            this.propagateSellingPriceDialogOpen = false;
            this.pendingPropagateSellingPrice = null;
            if (applyToOthers) {
                this.applyActiveSellingPriceToOtherFurnizors();
            }
            // Wait for the selling-price overlay to close before opening the
            // next confirmation (peer-product propagate), otherwise it can
            // steal focus / cover this dialog.
            this.$nextTick(() => {
                this.continueDraftSaveAfterFurnizorPropagateCheck();
            });
        },
        shouldAskPropagateFurnizorFieldsOnDraftSave() {
            if (this.simulationMode || this.readOnly) {
                return false;
            }
            if (!this.activePropagateFurnizorIdentity) {
                return false;
            }
            // Peers are loaded async from the full price list; only gate on
            // changed propagate-able fields here.
            return this.propagateFurnizorDefaultFieldKeys.length > 0;
        },
        resetPropagateFurnizorPeersState() {
            this.propagateFurnizorPeerSourceItems = [];
            this.propagateFurnizorPeerItemsByCode = {};
            this.propagateFurnizorPeersTotalCount = 0;
            this.propagateFurnizorPeersPage = 1;
            this.propagateFurnizorPeersItemsPerPage = 25;
            this.propagateFurnizorPeersSearch = "";
            this.propagateFurnizorPeersFilters = emptyPropagateFilterCriteria();
            this.propagateFurnizorPeersClientAll = null;
            this.propagateFurnizorPeersLoading = false;
        },
        /**
         * Load one page of candidate products for the active furnizor.
         * @param {{
         *   page?: number,
         *   itemsPerPage?: number,
         *   search?: string,
         *   criteria?: Object,
         * }} [opts]
         * @returns {Promise<void>}
         */
        async loadPropagateFurnizorPeersPage(opts = {}) {
            const label = String(this.activePropagateFurnizorLabel || "").trim();
            const page = Math.max(1, Number(opts.page) || this.propagateFurnizorPeersPage || 1);
            const itemsPerPage = Math.min(
                100,
                Math.max(1, Number(opts.itemsPerPage) || this.propagateFurnizorPeersItemsPerPage || 25),
            );
            const criteria = opts.criteria !== undefined
                ? { ...emptyPropagateFilterCriteria(), ...(opts.criteria || {}) }
                : { ...this.propagateFurnizorPeersFilters };
            if (opts.search !== undefined) {
                criteria.search = String(opts.search || "").trim();
            }
            const search = String(criteria.search || "").trim();
            const useClientPagination = propagateCriteriaNeedsFullFetch(criteria);

            this.propagateFurnizorPeersPage = page;
            this.propagateFurnizorPeersItemsPerPage = itemsPerPage;
            this.propagateFurnizorPeersSearch = search;
            this.propagateFurnizorPeersFilters = criteria;
            this.propagateFurnizorPeersLoading = true;

            try {
                let items = [];
                let count = 0;
                const identity = this.activePropagateFurnizorIdentity;
                const exclude = String(this.computedProduct?.productCode || "").trim();

                if (useClientPagination) {
                    let allItems = [];
                    if (typeof this.fetchFurnizorPeerProductsAll === "function" && label) {
                        try {
                            const fetched = await this.fetchFurnizorPeerProductsAll({
                                furnizorLabel: label,
                                search,
                                criteria,
                            });
                            allItems = Array.isArray(fetched) ? fetched : [];
                        } catch (error) {
                            console.error(
                                "Failed to load furnizor peers for filtered propagate:",
                                error,
                            );
                            allItems = [];
                        }
                    } else {
                        allItems = Array.isArray(this.existingProducts)
                            ? this.existingProducts
                            : [];
                    }
                    const allPeers = filterPropagatePeersWithCriteria(
                        collectPropagatePeers({
                            items: allItems,
                            drafts: this.productDrafts,
                            identity,
                            excludeProductCode: exclude,
                        }),
                        criteria,
                        itemMatchesBulkCriteria,
                    );
                    this.propagateFurnizorPeersClientAll = allPeers;
                    count = allPeers.length;
                    const start = (page - 1) * itemsPerPage;
                    const pagePeers = allPeers.slice(start, start + itemsPerPage);
                    items = pagePeers.map((peer) => peer.item).filter(Boolean);
                    const cache = { ...this.propagateFurnizorPeerItemsByCode };
                    for (const peer of allPeers) {
                        const code = String(peer?.product_code || "").trim();
                        if (code && peer.item) {
                            cache[code] = peer.item;
                        }
                    }
                    this.propagateFurnizorPeerItemsByCode = cache;
                } else {
                    this.propagateFurnizorPeersClientAll = null;
                    if (typeof this.fetchFurnizorPeerProductsPage === "function" && label) {
                        try {
                            const fetched = await this.fetchFurnizorPeerProductsPage({
                                furnizorLabel: label,
                                search,
                                criteria,
                                page,
                                itemsPerPage,
                            });
                            items = Array.isArray(fetched?.items) ? fetched.items : [];
                            count = Number(fetched?.count) || 0;
                            if (fetched?.page) {
                                this.propagateFurnizorPeersPage = Number(fetched.page) || page;
                            }
                        } catch (error) {
                            console.error(
                                "Failed to load furnizor peer products for propagate:",
                                error,
                            );
                            items = [];
                            count = 0;
                        }
                    } else {
                        // Fallback: paginate the already-loaded products page client-side.
                        const allPeers = filterPropagatePeersWithCriteria(
                            collectPropagatePeers({
                                items: this.existingProducts || [],
                                drafts: this.productDrafts,
                                identity,
                                excludeProductCode: exclude,
                            }),
                            criteria,
                            itemMatchesBulkCriteria,
                        );
                        count = allPeers.length;
                        const start = (page - 1) * itemsPerPage;
                        items = allPeers
                            .slice(start, start + itemsPerPage)
                            .map((peer) => peer.item)
                            .filter(Boolean);
                    }
                    const cache = { ...this.propagateFurnizorPeerItemsByCode };
                    for (const item of items) {
                        const code = String(item?.product_code || "").trim();
                        if (code) {
                            cache[code] = item;
                        }
                    }
                    this.propagateFurnizorPeerItemsByCode = cache;
                }

                this.propagateFurnizorPeerSourceItems = items;
                // Prefer selectable peer count (exclude source). Server totals
                // include the current product; client-filtered lists already exclude it.
                if (useClientPagination) {
                    this.propagateFurnizorPeersTotalCount = count;
                } else {
                    const pagePeerCount = collectPropagatePeers({
                        items,
                        drafts: this.productDrafts,
                        identity,
                        excludeProductCode: exclude,
                    }).length;
                    this.propagateFurnizorPeersTotalCount = count > 0
                        ? Math.max(count - 1, pagePeerCount)
                        : pagePeerCount;
                }
            } finally {
                this.propagateFurnizorPeersLoading = false;
            }
        },
        onPropagateFurnizorPeersPageChange(page) {
            const clientAll = this.propagateFurnizorPeersClientAll;
            if (Array.isArray(clientAll)) {
                const itemsPerPage = this.propagateFurnizorPeersItemsPerPage || 25;
                const nextPage = Math.max(1, Number(page) || 1);
                this.propagateFurnizorPeersPage = nextPage;
                const start = (nextPage - 1) * itemsPerPage;
                const pagePeers = clientAll.slice(start, start + itemsPerPage);
                this.propagateFurnizorPeerSourceItems = pagePeers
                    .map((peer) => peer.item)
                    .filter(Boolean);
                return;
            }
            void this.loadPropagateFurnizorPeersPage({ page });
        },
        onPropagateFurnizorPeersItemsPerPageChange(size) {
            void this.loadPropagateFurnizorPeersPage({
                page: 1,
                itemsPerPage: size,
            });
        },
        onPropagateFurnizorPeersSearch(search) {
            // Prefer @filters when both fire; keep for backward compatibility.
            if (this._propagateFiltersJustEmitted) {
                return;
            }
            void this.loadPropagateFurnizorPeersPage({
                page: 1,
                search,
            });
        },
        onPropagateFurnizorPeersFilters(criteria) {
            this._propagateFiltersJustEmitted = true;
            setTimeout(() => {
                this._propagateFiltersJustEmitted = false;
            }, 50);
            void this.loadPropagateFurnizorPeersPage({
                page: 1,
                criteria: criteria || emptyPropagateFilterCriteria(),
            });
        },
        /**
         * Fetch every matching peer for the active furnizor (respects dialog filters),
         * cache items for apply, return selectable product codes.
         * @param {{ search?: string, criteria?: Object }} [args]
         * @returns {Promise<string[]>}
         */
        async resolveAllPropagateFurnizorPeerCodes(args = {}) {
            const identity = this.activePropagateFurnizorIdentity;
            const exclude = String(this.computedProduct?.productCode || "").trim();
            const label = String(this.activePropagateFurnizorLabel || "").trim();
            const criteria = args.criteria !== undefined
                ? { ...emptyPropagateFilterCriteria(), ...(args.criteria || {}) }
                : { ...this.propagateFurnizorPeersFilters };
            if (args.search !== undefined) {
                criteria.search = String(args.search || "").trim();
            }
            const search = String(criteria.search || "").trim();

            if (!identity || !exclude) {
                return [];
            }

            if (Array.isArray(this.propagateFurnizorPeersClientAll)) {
                return this.propagateFurnizorPeersClientAll
                    .map((peer) => peer.product_code)
                    .filter(Boolean);
            }

            let items = [];
            if (typeof this.fetchFurnizorPeerProductsAll === "function" && label) {
                try {
                    const fetched = await this.fetchFurnizorPeerProductsAll({
                        furnizorLabel: label,
                        search,
                        criteria,
                    });
                    items = Array.isArray(fetched) ? fetched : [];
                } catch (error) {
                    console.error(
                        "Failed to load all furnizor peers for select-all:",
                        error,
                    );
                    // Fall back to codes already visible / cached from visited pages.
                    return this.propagateFurnizorPeers
                        .map((peer) => peer.product_code)
                        .filter(Boolean);
                }
            } else {
                items = Array.isArray(this.existingProducts)
                    ? this.existingProducts
                    : [];
            }

            const cache = { ...this.propagateFurnizorPeerItemsByCode };
            for (const item of items) {
                const code = String(item?.product_code || "").trim();
                if (code) {
                    cache[code] = item;
                }
            }
            this.propagateFurnizorPeerItemsByCode = cache;

            const peers = filterPropagatePeersWithCriteria(
                collectPropagatePeers({
                    items,
                    drafts: this.productDrafts,
                    identity,
                    excludeProductCode: exclude,
                }),
                criteria,
                itemMatchesBulkCriteria,
            );
            return peers.map((peer) => peer.product_code).filter(Boolean);
        },
        async continueDraftSaveAfterFurnizorPropagateCheck() {
            this.pendingFurnizorPropagate = null;
            // Never open on top of the multi-furnizor selling-price prompt.
            if (this.propagateSellingPriceDialogOpen) {
                return;
            }
            if (!this.shouldAskPropagateFurnizorFieldsOnDraftSave()) {
                this.resetPropagateFurnizorPeersState();
                this.completeDraftSave();
                return;
            }
            this.resetPropagateFurnizorPeersState();
            this.propagateFurnizorPeersLoading = true;
            this.propagateFurnizorFieldsDialogOpen = true;
            await this.loadPropagateFurnizorPeersPage({ page: 1 });
            if (!this.propagateFurnizorFieldsDialogOpen) {
                // User cancelled while peers were loading.
                return;
            }
            if (
                this.propagateFurnizorPeersTotalCount > 0
                || this.propagateFurnizorPeers.length > 0
            ) {
                return;
            }
            this.propagateFurnizorFieldsDialogOpen = false;
            this.resetPropagateFurnizorPeersState();
            this.completeDraftSave();
        },
        onPropagateFurnizorFieldsSkip() {
            this.propagateFurnizorFieldsDialogOpen = false;
            this.pendingFurnizorPropagate = null;
            this.resetPropagateFurnizorPeersState();
            this.completeDraftSave();
        },
        onPropagateFurnizorFieldsCancel() {
            this.propagateFurnizorFieldsDialogOpen = false;
            this.pendingFurnizorPropagate = null;
            this.resetPropagateFurnizorPeersState();
            this.confirmedSaveAsExistingProductUpdate = false;
        },
        onPropagateFurnizorFieldsApply({ fieldKeys, targetProductCodes }) {
            const identity = this.activePropagateFurnizorIdentity;
            const keys = Array.isArray(fieldKeys) ? fieldKeys : [];
            const codes = Array.isArray(targetProductCodes)
                ? targetProductCodes
                : [];
            if (!identity || !keys.length || !codes.length) {
                this.propagateFurnizorFieldsDialogOpen = false;
                this.pendingFurnizorPropagate = null;
                this.resetPropagateFurnizorPeersState();
                this.completeDraftSave();
                return;
            }
            const cache = this.propagateFurnizorPeerItemsByCode || {};
            let peerSourceItems = codes
                .map((code) => cache[String(code || "").trim()])
                .filter(Boolean);
            // If select-all / multi-page left gaps, prefer the full cache values.
            if (peerSourceItems.length < codes.length) {
                const byCode = { ...cache };
                for (const item of Object.values(byCode)) {
                    const code = String(item?.product_code || "").trim();
                    if (code && codes.includes(code) && !peerSourceItems.some(
                        (row) => String(row?.product_code || "").trim() === code,
                    )) {
                        peerSourceItems.push(item);
                    }
                }
            }
            this.pendingFurnizorPropagate = {
                identity: cloneDeep(identity),
                fieldKeys: keys,
                targetProductCodes: codes,
                values: extractPropagateValuesFromForm(
                    this.productBuilderForm,
                    keys,
                ),
                peerSourceItems: cloneDeep(peerSourceItems),
            };
            this.propagateFurnizorFieldsDialogOpen = false;
            this.resetPropagateFurnizorPeersState();
            this.completeDraftSave();
        },
        cancelExistingProductUpdateDialog() {
            this.existingProductUpdateConfirmOpen = false;
            this.confirmedSaveAsExistingProductUpdate = false;
        },
        confirmExistingProductUpdateDialog() {
            this.existingProductUpdateConfirmOpen = false;
            this.confirmedSaveAsExistingProductUpdate = true;
            void this.continueDraftSaveAfterConfirmations();
        },
        submitSimulationApply() {
            persistActiveFurnizorPricingFromRoot(this.productBuilderForm);
            this.$emit("simulation-apply", {
                form: cloneDeep(this.productBuilderForm),
                productCode: String(
                    this.productBuilderForm.overrideProductCodeValue
                        || this.computedProduct?.productCode
                        || this.editItem?.product_code
                        || "",
                ).trim(),
            });
            this.close();
        },
        async continueDraftSaveAfterConfirmations() {
            // Keep confirmation order:
            // 1) multi-furnizor selling price on THIS product
            // 2) copy fields to OTHER products of the same furnizor
            this.propagateFurnizorFieldsDialogOpen = false;
            this.pendingFurnizorPropagate = null;
            if (this.shouldAskPropagateSellingPriceOnDraftSave()) {
                this.pendingPropagateSellingPrice = parseNumericInput(
                    this.computedProduct?.listPriceRonIncVatFinal,
                );
                this.propagateSellingPriceDialogOpen = true;
                return;
            }
            this.continueDraftSaveAfterFurnizorPropagateCheck();
        },
        completeDraftSave() {
            const saveAsExistingUpdate = this.confirmedSaveAsExistingProductUpdate;
            const payload = this.buildSavePayload({ forDraftSave: true });
            // Payload may keep historical supplier-old for publish, but the draft
            // builder UI must not resurrect catalog-old on an untouched field.
            this.normalizeSupplierPricesForDraftBuilderUi();
            // Valoare veche for the *next* reopen = values from *this* session's open
            // (not the edited values just saved):
            // - open at 0, save 10 ⇒ next Valoare veche is still 0
            // - reopen at 10, save 20 ⇒ next Valoare veche is 10
            // Open baselines stay frozen for orange highlight vs original product.
            this.captureLastSavedPricingFieldBaselinesFromSessionStart();
            // Exception: brand-new furnizor tabs — first save must seed Valoare
            // veche from the values just saved (not prices copied at add time).
            this.promoteNewFurnizorBaselinesToSavedValuesOnFirstDraftSave();
            const sourceProductCode = saveAsExistingUpdate
                ? (payload.product_code || null)
                : this.isStructureChangedProductCopy
                    ? (this.sessionSourceProductCode
                        || payload.old_product_code
                        || null)
                    : (this.sessionCopyOriginCode
                        || payload.old_product_code
                        || null);
            const replaceDraftId = this.isStructureChangedProductCopy
                && !saveAsExistingUpdate
                ? null
                : (this.draftEntry?.id || null);
            const propagate = this.pendingFurnizorPropagate
                ? cloneDeep(this.pendingFurnizorPropagate)
                : null;
            this.pendingFurnizorPropagate = null;
            this.$emit("draft-save", {
                payload,
                builderState: {
                    productBuilderForm: cloneDeep(this.productBuilderForm),
                    pricingSettings: cloneDeep(this.effectivePricingSettings),
                    markupActualManual: this.markupActualManual,
                    pricingFieldBaselinesByTab: cloneDeep(
                        this.pricingFieldBaselinesByTab,
                    ),
                    pricingFieldReferenceBaselinesByTab: cloneDeep(
                        this.pricingFieldReferenceBaselinesByTab,
                    ),
                    pricingFieldLastSavedBaselinesByTab: cloneDeep(
                        this.pricingFieldLastSavedBaselinesByTab,
                    ),
                    pricingFieldSessionStartBaselinesByTab: cloneDeep(
                        this.pricingFieldSessionStartBaselinesByTab,
                    ),
                    manuallyEditedPricingFields: cloneDeep(
                        this.manuallyEditedPricingFields,
                    ),
                    newFurnizorKeysPendingFirstSave: {
                        ...this.newFurnizorKeysPendingFirstSave,
                    },
                    unpublishedNewFurnizorKeys: {
                        ...this.unpublishedNewFurnizorKeys,
                    },
                    sessionSourceProductCode: this.sessionSourceProductCode,
                    sessionCopyBaselineCode: String(
                        payload.product_code || "",
                    ).trim(),
                    sessionCopyOriginCode:
                        this.isStructureChangedProductCopy
                            ? this.sessionSourceProductCode
                            : this.sessionCopyOriginCode,
                },
                sourceProductCode,
                replaceDraftId,
                propagate,
            });
            this.confirmedSaveAsExistingProductUpdate = false;
        },
        async submitDraft() {
            // Simulation mode must never touch drafts / official list.
            if (this.simulationMode) {
                return;
            }
            // A draft is work-in-progress: we only require enough to identify it
            // (a product code) and that it does not collide with another draft.
            // Full field validation is enforced when publishing the in-work list.
            const productCode = this.computedProduct.productCode;
            if (!productCode) {
                showAlertModal(
                    this.$store,
                    "Completați structura produsului (capitol, subcapitol, producător) pentru a genera codul înainte de a salva în lista în lucru.",
                    "warning",
                    8000,
                );
                return;
            }
            if (this.blockedDraftProductCodes.includes(productCode)) {
                showAlertModal(
                    this.$store,
                    "Codul produsului există deja în lista în lucru.",
                    "warning",
                    8000,
                );
                return;
            }

            await this.waitForPendingFurnizorEnsure();
            this.finalizePricingFormForSave();
            if (
                this.isDuplicateLiveProductCodeForCreate
                && !this.confirmedSaveAsExistingProductUpdate
            ) {
                this.existingProductUpdateConfirmOpen = true;
                return;
            }
            await this.continueDraftSaveAfterConfirmations();
        },
        async submitDirectSave() {
            // Simulation mode must never write to the official price list.
            if (this.simulationMode) {
                return;
            }
            const productCode = this.computedProduct.productCode;
            if (!productCode) {
                showAlertModal(
                    this.$store,
                    "Completați structura produsului (capitol, subcapitol, producător) pentru a genera codul înainte de a salva în listă.",
                    "warning",
                    8000,
                );
                return;
            }
            if (!String(this.productBuilderForm.productName || "").trim()) {
                showAlertModal(
                    this.$store,
                    "Completați denumirea comercială înainte de a salva în listă.",
                    "warning",
                    8000,
                );
                return;
            }
            if (this.blockedDraftProductCodes.includes(productCode)) {
                showAlertModal(
                    this.$store,
                    "Codul produsului există deja în lista în lucru.",
                    "warning",
                    8000,
                );
                return;
            }
            if (this.isDuplicateLiveProductCodeForCreate) {
                this.alertDuplicateLiveProductCode();
                return;
            }

            await this.waitForPendingFurnizorEnsure();
            this.$emit("save-direct", {
                payload: this.buildSavePayload(),
                replaceDraftId: this.draftEntry?.id || null,
            });
        },    },
};
</script>

<style scoped>
:deep(.compound-picker-row--selected) {
    background-color: rgba(var(--v-theme-secondary), 0.08);
}

.supplier-list-price-change {
    font-size: calc(1em - 2px);
    line-height: inherit;
}

.transport-cost-formula-btn--active {
    box-shadow: 0 0 0 2px rgba(var(--v-theme-success), 0.45);
    color: rgb(var(--v-theme-success)) !important;
}

.transport-cost-formula-btn--active :deep(.v-icon) {
    color: rgb(var(--v-theme-success)) !important;
}

.pricing-field--formula :deep(.v-field) {
    background-color: rgba(var(--v-theme-success), 0.06);
}

.pricing-field--formula :deep(.v-field__outline) {
    --v-field-border-opacity: 1;
    color: rgb(var(--v-theme-success));
}

.pricing-field--manual :deep(.v-field__input) {
    color: rgb(251, 140, 0);
    font-weight: 600;
}

.pricing-field--manual :deep(.v-field__append-inner .v-icon),
.pricing-field--manual :deep(.v-text-field__suffix) {
    color: rgb(251, 140, 0);
}

.pricing-field--manual :deep(.v-field__outline) {
    --v-field-border-opacity: 1;
    color: rgb(251, 140, 0);
}

.pricing-field--manual :deep(.v-field) {
    background-color: rgba(251, 140, 0, 0.08);
}

.pricing-field--manual :deep(.supplier-list-price-change.text-error) {
    color: rgb(var(--v-theme-error)) !important;
}

.pricing-field--manual :deep(.supplier-list-price-change.text-success) {
    color: rgb(var(--v-theme-success)) !important;
}

.pricing-result-info {
    background-color: rgba(var(--v-theme-on-surface), 0.03);
    border-color: rgba(var(--v-theme-on-surface), 0.12) !important;
}

.pricing-result-info--manual {
    background-color: rgba(251, 140, 0, 0.08);
    border-color: rgb(251, 140, 0) !important;
}

.pricing-result-info--manual .text-body-1 {
    color: rgb(251, 140, 0);
}

.builder-form-fieldset {
    border: 0;
    margin: 0;
    padding: 0;
    min-width: 0;
}

.builder-form {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.builder-section {
    --builder-accent: var(--v-theme-primary);
    --builder-accent-strong: var(--v-theme-primary-darken-1);
    padding: 0 16px 12px;
    border-radius: 10px;
    border: 1.5px solid rgb(var(--builder-accent));
    background: rgba(var(--v-theme-surface), 0.78);
    overflow: hidden;
}

.builder-section--structure {
    --builder-accent: var(--v-theme-primary);
    --builder-accent-strong: var(--v-theme-primary-darken-1);
}

.builder-section--manufacturer {
    --builder-accent: var(--v-theme-secondary);
    --builder-accent-strong: var(--v-theme-secondary-darken-1);
}

.builder-section--product {
    --builder-accent: var(--v-theme-info);
    --builder-accent-strong: var(--v-theme-info);
}

.builder-section--supplier {
    --builder-accent: 0, 137, 123;
    --builder-accent-strong: 0, 105, 92;
}

.builder-section--pricing {
    --builder-accent: var(--v-theme-success);
    --builder-accent-strong: var(--v-theme-success);
}

.builder-section--results {
    --builder-accent: var(--v-theme-secondary-darken-1);
    --builder-accent-strong: var(--v-theme-secondary-darken-1);
}

.builder-section-heading {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    min-height: 48px;
    margin: 0 -16px 14px;
    padding: 8px 16px;
    background: rgba(var(--builder-accent), 0.12);
    border-bottom: 1px solid rgba(var(--builder-accent), 0.35);
}

.builder-section-heading :deep(.v-btn) {
    height: 32px;
    min-height: 32px;
}

.builder-section-heading :deep(.v-btn--icon) {
    width: 32px;
}

.builder-section-heading :deep(.v-selection-control) {
    min-height: 32px;
    height: 32px;
}

.builder-section-heading :deep(.v-label) {
    line-height: 1.2;
}

.lock-markup-to-reference {
    gap: 2px;
    padding: 0 4px 0 8px;
    border-radius: 999px;
    background: rgba(var(--v-theme-surface), 0.55);
}

.lock-markup-to-reference__label {
    font-size: 0.8125rem;
    font-weight: 500;
    opacity: 0.92;
    user-select: none;
}

.lock-markup-to-reference__info {
    cursor: help;
    opacity: 0.75;
    margin-inline-end: 2px;
}

.lock-markup-to-reference__info:hover,
.lock-markup-to-reference__info:focus-visible {
    opacity: 1;
    outline: none;
}

.builder-section-title {
    position: relative;
    display: inline-flex;
    align-items: center;
    padding-left: 12px;
    font-size: 0.95rem;
    font-weight: 600;
    letter-spacing: 0.01em;
    line-height: 1.2;
    color: rgb(var(--builder-accent-strong));
}

.builder-section-title::before {
    content: "";
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 3px;
    height: 1em;
    border-radius: 999px;
    background: rgb(var(--builder-accent));
}

.builder-panel-nav {
    padding: 6px 8px;
    border-radius: 999px;
    background: rgba(var(--v-theme-on-surface), 0.04);
}

.builder-panel-chip {
    cursor: pointer;
    font-weight: 500;
}

.builder-panel-chip:hover {
    background: rgba(var(--v-theme-primary), 0.12);
}

.builder-form-fieldset--readonly {
    pointer-events: none;
    opacity: 0.92;
}

.builder-form-fieldset--readonly .compare-furnizor-tabs-nav {
    pointer-events: auto;
    opacity: 1;
}

.builder-form-fieldset--readonly .pricing-field--manual,
.compare-field--changed {
    --compare-highlight: 255, 193, 7;
}

.builder-form-fieldset--readonly .pricing-field--manual :deep(.v-field__input),
.compare-field--changed :deep(.v-field__input) {
    color: rgb(183, 110, 0);
    font-weight: 600;
}

.builder-form-fieldset--readonly .pricing-field--manual :deep(.v-field__outline),
.compare-field--changed :deep(.v-field__outline) {
    --v-field-border-opacity: 1;
    color: rgb(255, 179, 0);
}

.builder-form-fieldset--readonly .pricing-field--manual :deep(.v-field),
.compare-field--changed :deep(.v-field) {
    background-color: rgba(255, 193, 7, 0.14);
}

.compare-field--changed :deep(.v-selection-control) {
    background-color: rgba(255, 193, 7, 0.14);
    border-radius: 4px;
    padding-inline: 4px;
}

.compare-furnizor-tab--changed {
    background-color: rgba(255, 193, 7, 0.18) !important;
    border-radius: 4px;
}
</style>
